import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CropSquareIcon from '@mui/icons-material/CropSquare';

import { DEFAULT_PIPE_COMBOS } from '../pipeCatalog';
import fittingsData from '../fittingsData';
import {
  isFittingCompatibleWithMainPipe,
  isFittingCompatibleWithSquareProfile,
} from './separatorFittingCompatibility';
import {
  facingStickFields,
  getFittingConnectionDiameter,
  getHoleDiameterForPipe,
  flangePipeInsertion,
  nakShaibaPipeMeasure,
  isAutoConstructorReducerAllowed,
  resolveNippelMuftaFitting,
  resolveSideConnectionOutletDimension,
  endFittingAutoLength,
  getEndMuftaCutLength,
} from './collectorUtils';
import {
  determineThickness,
  getMinThickness,
  getActualCoating,
  resolveCatalogThickness,
} from './pipeThicknessUtils';
import { computeHydraulicSeparatorTotalHeight } from './hydraulicSeparatorTotalHeight';

/**
 * Извлекаем уникальные диаметры из каталога труб
 */
const getAvailableDiameters = () => {
  const diameters = new Set();
  Object.keys(DEFAULT_PIPE_COMBOS).forEach(key => {
    const [diameter] = key.split('|');
    diameters.add(parseFloat(diameter));
  });
  
  const excludedDiameters = [54, 306];
  
  return Array.from(diameters)
    .filter(d => d > 38 && !excludedDiameters.includes(d))
    .sort((a, b) => a - b)
    .map(d => ({
      value: d,
      label: `⌀${d}`,
    }));
};

const availableDiameters = getAvailableDiameters();

/**
 * Доступные квадратные профили для гидроразделителя
 */
const availableSquareProfiles = [
  { value: '40x40', label: '40×40' },
  { value: '50x50', label: '50×50' },
  { value: '60x60', label: '60×60' },
  { value: '80x80', label: '80×80' },
  { value: '100x100', label: '100×100' },
  { value: '150x150', label: '150×150' },
];

/**
 * Категории фитингов для подключений
 */
const connectionFittingCategories = [
  { id: 'Ниппели', label: 'Ниппели' },
  { id: 'Муфты', label: 'Муфты' },
  { id: 'Фланцы', label: 'Фланцы' },
  { id: 'Накидные гайки', label: 'Накидные гайки' },
  { id: 'Шайбы с наружной резьбой', label: 'Шайбы с нар. резьбой' },
];

/**
 * Получаем фитинги по категории
 */
/**
 * Получаем фитинги по категории
 * @param {string} category - Категория фитинга
 * @param {number} mainPipeDiameter - Диаметр основной трубы
 * @param {string} shape - Форма трубы ('round' или 'square')
 * @param {string} mainPipeProfile - Профиль трубы (для квадратных)
 * @param {boolean} isEndConnection - Торцевое подключение (верхнее/нижнее)
 */
const getFittingsByCategory = (category, mainPipeDiameter = null, shape = 'round', mainPipeProfile = null, isEndConnection = false) => {
  return Object.entries(fittingsData)
    .filter(([key, data]) => {
      return data.category === category;
    })
    .map(([key, data]) => {
      let isCompatible = true;
      if (shape === 'square' && mainPipeProfile) {
        // Для квадратных профилей
        if (isEndConnection) {
          isCompatible = isEndFittingCompatibleWithSquareProfile(key, mainPipeProfile);
        } else {
          isCompatible = isFittingCompatibleWithSquareProfile(key, mainPipeProfile);
        }
      } else if (shape === 'round' && mainPipeDiameter) {
        // Для круглых труб
        if (isEndConnection) {
          isCompatible = isEndFittingCompatibleWithMainPipe(key, mainPipeDiameter);
        } else {
          isCompatible = isFittingCompatibleWithMainPipe(key, mainPipeDiameter);
        }
      }
      
      return {
        id: key,
        label: data.displayName,
        diameter: data.diameter,
        length: data.length,
        isCompatible,
      };
    });
};

/**
 * Проверка совместимости торцевого фитинга (верхнее/нижнее подключение) с диаметром основной трубы
 *
 * Правила:
 * 1. Ниппели могут быть равны или меньше диаметра трубы (<=)
 * 2. Фланцы могут быть равны или меньше диаметра трубы (<=)
 * 3. Муфты:
 *    - 42.4мм: не больше 1"
 *    - 48.3мм: не больше 1 1/4"
 *    - 60.3мм и больше: без ограничений
 */
const isEndFittingCompatibleWithMainPipe = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // Ниппели: могут быть равны или меньше диаметра трубы
  if (category === 'Ниппели') {
    const nippelDiameter = fittingData.diameter;
    // Разрешаем если диаметр ниппеля <= диаметра трубы (равные тоже можно!)
    return nippelDiameter <= mainPipeDiameter;
  }

  // Фланцы: могут быть равны или меньше диаметра трубы
  if (category === 'Фланцы') {
    // Извлекаем Ду из ключа фланца
    const dnMatch = fittingId.match(/Flanec_(\d+)/);
    if (dnMatch) {
      const dn = parseInt(dnMatch[1]);
      const flangePipeDiameter = DN_TO_PIPE_DIAMETER[dn];
      if (flangePipeDiameter) {
        // Разрешаем если диаметр трубы для фланца <= диаметра основной трубы
        return flangePipeDiameter <= mainPipeDiameter;
      }
    }
    return true; // Если не можем определить - разрешаем
  }

  // Муфты: специальные ограничения
  if (category === 'Муфты') {
    if (mainPipeDiameter === 42.4) {
      // Для 42.4: не больше 1"
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    } else if (mainPipeDiameter === 48.3) {
      // Для 48.3: не больше 1 1/4"
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }
    // Для остальных диаметров: без ограничений
  }

  return true;
};

/**
 * Маппинг Ду фланца → диаметр трубы
 */
const DN_TO_PIPE_DIAMETER = {
  25: 33.7,
  32: 42.4,
  40: 48.3,
  50: 60.3,
  65: 76.1,
  80: 88.9,
  100: 114.3,
  125: 139.7,
  150: 168.3,
  200: 219.1,
  250: 273,
  300: 323.9,
  350: 355.6,
};

/**
 * Маппинг размера накидной гайки → диаметр трубы
 */
const NAK_GAIKA_TO_PIPE_DIAMETER = {
  '1': 21.3,
  '1_1.4': 32,
  '1_1.2': 32,
  '2': 42.4,
};

/**
 * Маппинг размеров шайб с наружной резьбой на диаметр трубы
 */
const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
  '1': 26.9,
  '1_1.4': 32,
  '1_1.2': 32,
  '2': 42.4,
};

/**
 * Извлечь Ду из ключа фланца (например, "Flanec_40_galv" → 40)
 */
const extractDnFromFlangeKey = (flangeKey) => {
  if (!flangeKey) return null;
  const match = flangeKey.match(/Flanec_(\d+)_/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Извлечь размер из ключа накидной гайки (например, "Nak_Gaika_1" → "1")
 */
const extractSizeFromNakGaikaKey = (nakGaikaKey) => {
  if (!nakGaikaKey) return null;
  const match = nakGaikaKey.match(/Nak_Gaika_(.+)/);
  return match ? match[1] : null;
};

/**
 * Получить диаметр трубы по Ду фланца
 */
const getPipeDiameterForDn = (dn) => {
  return DN_TO_PIPE_DIAMETER[dn] || null;
};

/**
 * Получить диаметр трубы по размеру накидной гайки
 */
const getPipeDiameterForNakGaika = (size) => {
  return NAK_GAIKA_TO_PIPE_DIAMETER[size] || null;
};

/**
 * Извлечь размер из ключа шайбы с наружной резьбой (например, "Shaib_Nar_Rezba_1" -> "1")
 */
const extractSizeFromShaibaNarRezba = (key) => {
  if (!key) return null;
  const match = key.match(/Shaib_Nar_Rezba_(.+)/);
  return match ? match[1] : null;
};

/**
 * Получить диаметр трубы по размеру шайбы с наружной резьбой
 */
const getPipeDiameterForShaibaNarRezba = (size) => {
  return SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[size] || null;
};

/**
 * Получить ключ шайбы/бортшайбы для фланца или накидной гайки
 */
const getShaibaKeyForFitting = (fittingKey) => {
  if (!fittingKey) return null;
  
  if (fittingKey.startsWith('Flanec_')) {
    const dn = extractDnFromFlangeKey(fittingKey);
    return dn ? `Bortshaiba_${dn}` : null;
  } else if (fittingKey.startsWith('Nak_Gaika_')) {
    const size = extractSizeFromNakGaikaKey(fittingKey);
    return size ? `Shaib_Gaika_${size}` : null;
  }
  
  return null;
};

/**
 * Найти подходящий переход концентрический
 * @param {number} fromDiameter - больший диаметр (основная труба)
 * @param {number} toDiameter - меньший диаметр (под фланец)
 * @returns {object|null} - {key, info} или null
 */
const findConcentricReducer = (fromDiameter, toDiameter) => {
  // Ищем переход с точным соответствием диаметров
  const reducerKey = `Perehod_Konc_${fromDiameter}х${toDiameter}`;
  if (!isAutoConstructorReducerAllowed(reducerKey)) return null;
  const reducerInfo = fittingsData[reducerKey];
  if (reducerInfo) {
    return { key: reducerKey, info: reducerInfo };
  }

  // Пробуем с русской "х"
  const reducerKeyRu = `Perehod_Konc_${fromDiameter}x${toDiameter}`;
  const reducerInfoRu = fittingsData[reducerKeyRu];
  if (reducerInfoRu) {
    return { key: reducerKeyRu, info: reducerInfoRu };
  }

  return null;
};

/**
 * Найти эллиптическую заглушку по диаметру
 * @param {number} diameter - диаметр трубы (гребёнки)
 * @returns {object|null} - {key, info} или null
 */
const findEllipticalCap = (diameter) => { // eslint-disable-line no-unused-vars
  const capKey = `Zaglushka_EL_${diameter}`;
  const capInfo = fittingsData[capKey];
  if (capInfo) {
    return { key: capKey, info: capInfo };
  }
  return null;
};

/**
 * Проверяет, нужен ли переход/заглушка для торцевого фитинга
 * Возвращает объект с информацией о необходимости перехода и его типе
 */
const getCapRequirementForFitting = (fittingId, mainPipeDiameter) => {
  if (!fittingId) return { needsCap: false };
  
  const isFlange = fittingId.startsWith('Flanec_');
  const isNippel = fittingId.startsWith('Nippel_');
  const isMufta = fittingId.startsWith('Mufta_');
  const isNakGaika = fittingId.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingId.startsWith('Shaib_Nar_Rezba_');
  
  // Для ниппелей: проверяем нужен ли переход
  if (isNippel) {
    const nippelDiameter = fittingsData[fittingId]?.diameter;
    
    // Специальные случаи с переходами
    // Труба 48.3, ниппель 1 1/4 (48.3) → переход 48.3х42.4
    if (mainPipeDiameter === 48.3 && fittingId === 'Nippel_1_1.4') {
      const reducer = findConcentricReducer(48.3, 42.4);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 60.3, ниппель 1 1/2 (48.3) → переход 60.3х48.3
    if (mainPipeDiameter === 60.3 && fittingId === 'Nippel_1_1.2') {
      const reducer = findConcentricReducer(60.3, 48.3);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 76.1, ниппель 2 (60.3) → переход 76.1х60.3
    if (mainPipeDiameter === 76.1 && fittingId === 'Nippel_2') {
      const reducer = findConcentricReducer(76.1, 60.3);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 88.9, ниппель 2 1/2 (76.1) → переход 88.9х76.1
    if (mainPipeDiameter === 88.9 && fittingId === 'Nippel_2_1/2') {
      const reducer = findConcentricReducer(88.9, 76.1);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 114.3, ниппель 3 (88.9): переход 114.3х88.9 исключён, дальше — эллиптическая заглушка
    if (mainPipeDiameter === 114.3 && fittingId === 'Nippel_3') {
      const reducer = findConcentricReducer(114.3, 88.9);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Общий случай: проверяем совпадение диаметров
    if (nippelDiameter && nippelDiameter !== mainPipeDiameter) {
      // Диаметры НЕ совпадают - нужна эллиптическая заглушка
      return { needsCap: true, capType: 'elliptical' };
    }
    // Диаметры совпадают - впритык (без заглушек)
    return { needsCap: false, fitsFlush: true };
  }
  
  // Для муфт: специальная логика (как в распределительном коллекторе)
  if (isMufta) {
    // Специальные случаи с переходами (проверяем ПЕРВЫМИ!)
    // Труба 48.3, муфта 1 (42.4) → переход 48.3х42.4
    if (mainPipeDiameter === 48.3 && fittingId === 'Mufta_1') {
      const reducer = findConcentricReducer(48.3, 42.4);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 60.3, муфта 1 1/4 (48.3) → переход 60.3х48.3
    if (mainPipeDiameter === 60.3 && fittingId === 'Mufta_1_1.4') {
      const reducer = findConcentricReducer(60.3, 48.3);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // При диаметре гребенки 60.3 и муфте 1 1/2" - переход 60.3х48.3
    if (mainPipeDiameter === 60.3 && fittingId === 'Mufta_1_1.2') {
      const reducer = findConcentricReducer(60.3, 48.3);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 76.1, муфта 1 1/2 (48.3) → переход 76.1х60.3
    if (mainPipeDiameter === 76.1 && fittingId === 'Mufta_1_1.2') {
      const reducer = findConcentricReducer(76.1, 60.3);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Труба 76.1, муфта 2 (60.3) → переход 76.1х60.3
    if (mainPipeDiameter === 76.1 && fittingId === 'Mufta_2') {
      const reducer = findConcentricReducer(76.1, 60.3);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer };
      }
    }
    
    // Случаи впритык (проверяем ПОСЛЕ переходов!)
    // При диаметре гребенки 42.4 и муфте 1" - впритык (без заглушек)
    if (mainPipeDiameter === 42.4 && fittingId === 'Mufta_1') {
      return { needsCap: false, fitsFlush: true };
    }
    // При диаметре гребенки 48.3 и муфте 1 1/4" - впритык (без заглушек)
    if (mainPipeDiameter === 48.3 && fittingId === 'Mufta_1_1.4') {
      return { needsCap: false, fitsFlush: true };
    }
    // При диаметре гребенки 60.3 и муфте 2" - впритык (без заглушек)
    if (mainPipeDiameter === 60.3 && fittingId === 'Mufta_2') {
      return { needsCap: false, fitsFlush: true };
    }
    
    // По умолчанию для муфт - эллиптическая заглушка
    return { needsCap: true, capType: 'elliptical' };
  }
  
  // Для фланцев: проверяем нужен ли переход
  if (isFlange) {
    const dn = extractDnFromFlangeKey(fittingId);
    const flangePipeDiameter = getPipeDiameterForDn(dn);
    
    if (flangePipeDiameter && flangePipeDiameter !== mainPipeDiameter && mainPipeDiameter > flangePipeDiameter) {
      // Гребёнка больше диаметра фланца — нужен переход или заглушка
      const reducer = findConcentricReducer(mainPipeDiameter, flangePipeDiameter);
      if (reducer) {
        return { needsCap: true, capType: 'reducer', reducer: reducer, pipeDiameter: flangePipeDiameter };
      }
      // Нет перехода — эллиптическая заглушка
      return { needsCap: true, capType: 'elliptical', pipeDiameter: flangePipeDiameter };
    }
    // Диаметры совпадают — впритык
    return { needsCap: false, fitsFlush: true, pipeDiameter: flangePipeDiameter };
  }
  
  // Для накидных гаек
  if (isNakGaika) {
    const size = extractSizeFromNakGaikaKey(fittingId);
    const pipeDiameter = getPipeDiameterForNakGaika(size);
    
    if (pipeDiameter && pipeDiameter !== mainPipeDiameter) {
      // Диаметры НЕ совпадают - эллиптическая заглушка
      return { needsCap: true, capType: 'elliptical', pipeDiameter: pipeDiameter };
    }
    // Диаметры совпадают — впритык
    return { needsCap: false, fitsFlush: true, pipeDiameter: pipeDiameter };
  }
  
  // Для шайб с наружной резьбой
  if (isShaibaNarRezba) {
    const size = extractSizeFromShaibaNarRezba(fittingId);
    const pipeDiameter = getPipeDiameterForShaibaNarRezba(size);
    
    if (pipeDiameter && pipeDiameter !== mainPipeDiameter) {
      // Диаметры НЕ совпадают - эллиптическая заглушка
      return { needsCap: true, capType: 'elliptical', pipeDiameter: pipeDiameter };
    }
    // Диаметры совпадают — впритык
    return { needsCap: false, fitsFlush: true, pipeDiameter: pipeDiameter };
  }
  
  return { needsCap: false };
};

/**
 * Проверяем совместимость торцевого фитинга с квадратным профилем
 * 
 * @param {string} fittingId - ID фитинга
 * @param {string} profile - Профиль трубы
 * @returns {boolean} - true если фитинг совместим с профилем
 */
const isEndFittingCompatibleWithSquareProfile = (fittingId, profile) => {
  // Используем ту же логику, что и для контурных фитингов
  return isFittingCompatibleWithSquareProfile(fittingId, profile);
};

/**
 * Получает вылет фитинга с учетом дефолтных значений
 * Для фланцев, накидных гаек и шайб с наружной резьбой дефолт 100мм
 * Для остальных - длина из каталога
 */
/**
 * Вычисляет реальную позицию подключения с учетом направления отсчета
 */
const getConnectionPosition = (offsetFromTop, offsetDirection, actualMainPipeHeight, topBortshaibaLength, topStraightCapLength, bottomBortshaibaLength, bottomStraightCapLength) => {
  if ((offsetDirection || 'top') === 'bottom') {
    // Отсчет снизу - пересчитываем в отсчет от верха
    // Общая высота = actualMainPipeHeight + верхние элементы + нижние элементы
    // Позиция от верха = общая высота - отступ от низа
    const totalHeight = actualMainPipeHeight + topBortshaibaLength + topStraightCapLength + bottomBortshaibaLength + bottomStraightCapLength;
    const result = totalHeight - offsetFromTop;
    return result;
  }
  // Отсчет сверху - как есть
  return offsetFromTop;
};

/**
 * Компонент редактора гидравлического разделителя
 */
function HydraulicSeparatorEditor({ onGenerate, onBack, onBackToDrawing, initialParams, calculationMode, onBackToCharacteristics }) {
  // Начальные параметры по умолчанию
  const getDefaultParams = () => ({
    // Основные параметры
    shape: 'round',            // Форма: 'round' или 'square'
    mainPipeDiameter: 101.6,    // Для круглой формы
    mainPipeProfile: '80x80',  // Для квадратной формы
    separatorHeight: 399,

    // Заглушки
    hasTopCap: true,          // Есть ли заглушка/переход сверху (по умолчанию включена)
    hasBottomCap: true,       // Есть ли заглушка/переход снизу (по умолчанию включена)
    topCapElementType: 'cap',  // 'cap' (заглушка) или 'reducer' (переход)
    bottomCapElementType: 'cap',
    topCapType: 'straight', // 'elliptical' | 'straight' (для заглушек) - плоская
    bottomCapType: 'straight', // плоская
    topReducer: 'Perehod_Konc_60.3х48.3',  // ID перехода сверху
    bottomReducer: 'Perehod_Konc_60.3х48.3', // ID перехода снизу

    // Торцевое подключение сверху
    hasTopConnection: true,  // Включаем торцевое подключение сверху по умолчанию
    topCapFittingCategory: 'Муфты', // Категория Муфты
    topCapFitting: 'Mufta_1.2', // Муфта 1/2
    topCapFittingLength: null,

    // Торцевое подключение снизу
    hasBottomConnection: true, // Включаем торцевое подключение снизу по умолчанию
    bottomCapFittingCategory: 'Муфты', // Категория Муфты
    bottomCapFitting: 'Mufta_1.2', // Муфта 1/2
    bottomCapFittingLength: null,
    
    // Общий режим размещения для левых и правых подключений
    connectionsPlacementMode: 'spacing', // 'offsets' (отступы) или 'spacing' (межосевое)
    
    // Левые подключения
    leftConnectionsFirstOffset: 82, // Отступ первого подключения (для режима spacing)
    leftConnectionsFirstOffsetMode: 'fromEdge', // 'fromEdge' или 'fromRightConnection'
    leftConnectionsFirstOffsetReferenceIndex: 0, // индекс правого подключения, от которого отступаем
    leftConnectionsFirstOffsetSaved: null, // сохранённое значение до включения режима привязки
    leftConnectionsFirstOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
    leftConnectionsLastOffset: 117, // Отступ последнего подключения (для режима spacing)
    leftConnectionsSpacing: 200, // Межосевое расстояние (для режима spacing)
    leftConnections: [
      { id: 'left-1', fittingId: 'Nippel_1_1.2', fittingLength: null, offsetFromTop: 82, offsetDirection: 'top', spacingToNext: null },
      { id: 'left-2', fittingId: 'Nippel_1_1.2', fittingLength: null, offsetFromTop: 117, offsetDirection: 'bottom', spacingToNext: null },
    ],
    
    // Правые подключения
    rightConnectionsFirstOffset: 117, // Отступ первого подключения (для режима spacing)
    rightConnectionsFirstOffsetMode: 'fromEdge', // 'fromEdge' или 'fromLeftConnection'
    rightConnectionsFirstOffsetReferenceIndex: 0, // индекс левого подключения, от которого отступаем
    rightConnectionsFirstOffsetSaved: null, // сохранённое значение до включения режима привязки
    rightConnectionsFirstOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
    rightConnectionsLastOffset: 82, // Отступ последнего подключения (для режима spacing)
    rightConnectionsSpacing: 200, // Межосевое расстояние (для режима spacing)
    rightConnections: [
      { id: 'right-1', fittingId: 'Nippel_1_1.2', fittingLength: null, offsetFromTop: 117, offsetDirection: 'top', spacingToNext: null },
      { id: 'right-2', fittingId: 'Nippel_1_1.2', fittingLength: null, offsetFromTop: 82, offsetDirection: 'bottom', spacingToNext: null },
    ],

    // Дополнительные выходы (центральные фитинги с размещением по отступам)
    additionalOutlets: [],
  });

  const [params, setParams] = useState(() => {
    if (initialParams) {
      return { ...getDefaultParams(), ...initialParams };
    }
    return getDefaultParams();
  });

  const [incompatibilityDialog, setIncompatibilityDialog] = useState({
    open: false,
    message: '',
  });

  // Состояние для drag & drop
  const [draggedLeftIndex, setDraggedLeftIndex] = useState(null);
  const [draggedRightIndex, setDraggedRightIndex] = useState(null);
  
  // Состояние для хранения предыдущих типов заглушек при переключении на квадратную форму
  const [savedCapTypes, setSavedCapTypes] = useState(null);

  // Обновление параметров при изменении initialParams (при пересчете из калькулятора)
  useEffect(() => {
    if (initialParams) {
      // Проверяем, изменились ли реально параметры (сравниваем ключевые поля)
      const hasChanged = 
        initialParams.mainPipeDiameter !== params.mainPipeDiameter ||
        initialParams.mainPipeWallThickness !== params.mainPipeWallThickness ||
        initialParams.separatorHeight !== params.separatorHeight ||
        JSON.stringify(initialParams.leftConnections) !== JSON.stringify(params.leftConnections) ||
        JSON.stringify(initialParams.rightConnections) !== JSON.stringify(params.rightConnections);
      
      if (hasChanged) {
        setParams({ ...getDefaultParams(), ...initialParams });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialParams]);

  // Обновление параметров
  const updateParam = (keyOrObj, value) => {
    if (typeof keyOrObj === 'object') {
      setParams(prev => ({ ...prev, ...keyOrObj }));
    } else {
      setParams(prev => ({ ...prev, [keyOrObj]: value }));
    }
  };

  // Исправляем неправильные дефолты при загрузке из localStorage
  React.useEffect(() => {
    const updates = {};
    let needsUpdate = false;
    
    // Проверяем topCapFittingLength
    if (params.topCapFitting) {
      const isNakGaika = params.topCapFitting.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = params.topCapFitting.startsWith('Shaib_Nar_Rezba_');
      
      if (isNakGaika || isShaibaNarRezba) {
        const wrongDefault = params.shape === 'square' ? 40 : 58;
        
        // Если значение равно неправильному дефолту, сбрасываем на null
        if (params.topCapFittingLength === wrongDefault) {
          updates.topCapFittingLength = null;
          needsUpdate = true;
        }
      }
    }
    
    // Проверяем bottomCapFittingLength
    if (params.bottomCapFitting) {
      const isNakGaika = params.bottomCapFitting.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = params.bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
      
      if (isNakGaika || isShaibaNarRezba) {
        const wrongDefault = params.shape === 'square' ? 40 : 58;
        
        // Если значение равно неправильному дефолту, сбрасываем на null
        if (params.bottomCapFittingLength === wrongDefault) {
          updates.bottomCapFittingLength = null;
          needsUpdate = true;
        }
      }
    }
    
    if (needsUpdate) {
      setParams(prev => ({ ...prev, ...updates }));
    }
  }, [params.shape, params.topCapFitting, params.topCapFittingLength, params.bottomCapFitting, params.bottomCapFittingLength]);

  // Обновление левого подключения
  const updateLeftConnection = (index, key, value) => {
    setParams(prev => {
      const updated = [...prev.leftConnections];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, leftConnections: updated };
    });
  };

  // Обновление правого подключения
  const updateRightConnection = (index, key, value) => {
    setParams(prev => {
      const updated = [...prev.rightConnections];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, rightConnections: updated };
    });
  };

  // Добавление левого подключения
  const addLeftConnection = () => {
    setParams(prev => ({
      ...prev,
      leftConnections: [
        ...prev.leftConnections,
        {
          id: `left-${Date.now()}`,
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          offsetFromTop: Math.round(prev.separatorHeight / 2),
          offsetDirection: 'top', // 'top' или 'bottom'
        },
      ],
    }));
  };

  // Удаление левого подключения
  const deleteLeftConnection = (index) => {
    if (params.leftConnections.length <= 1) return;
    setParams(prev => ({
      ...prev,
      leftConnections: prev.leftConnections.filter((_, i) => i !== index),
    }));
  };

  // Добавление правого подключения
  const addRightConnection = () => {
    setParams(prev => ({
      ...prev,
      rightConnections: [
        ...prev.rightConnections,
        {
          id: `right-${Date.now()}`,
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          offsetFromTop: Math.round(prev.separatorHeight / 2),
          offsetDirection: 'top', // 'top' или 'bottom'
        },
      ],
    }));
  };

  // Удаление правого подключения
  const deleteRightConnection = (index) => {
    if (params.rightConnections.length <= 1) return;
    setParams(prev => ({
      ...prev,
      rightConnections: prev.rightConnections.filter((_, i) => i !== index),
    }));
  };

  // Перемещение левого подключения
  const moveLeftConnection = (fromIndex, toIndex) => {
    setParams(prev => {
      const newConnections = [...prev.leftConnections];
      const [movedItem] = newConnections.splice(fromIndex, 1);
      newConnections.splice(toIndex, 0, movedItem);
      // Обновляем ID для сохранения последовательности
      return {
        ...prev,
        leftConnections: newConnections.map((c, i) => ({ ...c, id: `left-${i + 1}` }))
      };
    });
  };

  // Перемещение правого подключения
  const moveRightConnection = (fromIndex, toIndex) => {
    setParams(prev => {
      const newConnections = [...prev.rightConnections];
      const [movedItem] = newConnections.splice(fromIndex, 1);
      newConnections.splice(toIndex, 0, movedItem);
      // Обновляем ID для сохранения последовательности
      return {
        ...prev,
        rightConnections: newConnections.map((c, i) => ({ ...c, id: `right-${i + 1}` }))
      };
    });
  };

  // Функции для дополнительных выходов
  const addAdditionalOutlet = () => {
    setParams(prev => ({
      ...prev,
      additionalOutlets: [
        ...prev.additionalOutlets,
        {
          id: `outlet-${Date.now()}`,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_1',
          positionType: 'center', // 'center' (по середине) или 'distance' (на расстоянии)
          side: 'both', // 'both' (с 2-х сторон), 'front' (спереди), 'back' (сзади)
          distanceFromEdge: 50, // Расстояние от края (для positionType='distance')
          distanceDirection: 'left', // 'left' или 'right' (для positionType='distance')
        },
      ],
    }));
  };

  const updateAdditionalOutlet = (index, key, value) => {
    setParams(prev => {
      const updated = [...prev.additionalOutlets];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, additionalOutlets: updated };
    });
  };

  const removeAdditionalOutlet = (index) => {
    setParams(prev => ({
      ...prev,
      additionalOutlets: prev.additionalOutlets.filter((_, i) => i !== index),
    }));
  };

  // Получение фитингов для торцевых заглушек
  // eslint-disable-next-line no-unused-vars
  const capFittings = useMemo(() => {
    const allFittings = [];
    connectionFittingCategories.forEach(cat => {
      allFittings.push(...getFittingsByCategory(cat.id, params.mainPipeDiameter));
    });
    return allFittings.filter(f => f.isCompatible);
  }, [params.mainPipeDiameter]);

  // Вспомогательная функция для расчета длины фитинга (только для UI)
  const getFittingLengthForUI = useCallback((fittingId, fittingLength) => {
    // Если задана длина вручную, используем её
    if (fittingLength !== null && fittingLength !== undefined) {
      return fittingLength;
    }
    
    const isNippel = fittingId?.startsWith('Nippel_');
    const isNakGaika = fittingId?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = fittingId?.startsWith('Shaib_Nar_Rezba_');
    
    // Для квадратных профилей:
    if (params.shape === 'square') {
      // Накидные гайки и шайбы с резьбой: дефолт 58мм
      if (isNakGaika || isShaibaNarRezba) {
        return 58;
      }
    }
    
    // Ниппели - длина из каталога
    if (isNippel) {
      const fittingData = fittingsData[fittingId];
      return fittingData?.length || 0;
    }
    
    // Муфты на торце режутся пополам
    if (fittingId?.startsWith('Mufta_') || fittingId?.startsWith('Nippel_Mufta_')) {
      const fittingData = fittingsData[fittingId];
      return getEndMuftaCutLength(fittingId) ?? fittingData?.length ?? 0;
    }
    
    // Фланцы - дефолтная 100 мм
    if (fittingId?.startsWith('Flanec_')) {
      return 100;
    }
    
    // Накидные гайки - дефолтная 58мм для квадратных, 40мм для круглых
    if (isNakGaika) {
      return params.shape === 'square' ? 58 : 40;
    }
    
    // Шайбы с резьбой - дефолтная 58мм для квадратных, 40мм для круглых
    if (isShaibaNarRezba) {
      return params.shape === 'square' ? 58 : 40;
    }
    
    return 0;
  }, [params.shape]);

  // Вычисляем эффективный отступ первого левого подключения
  const effectiveLeftFirstOffset = useMemo(() => {
    if (params.connectionsPlacementMode !== 'spacing') {
      return params.leftConnectionsFirstOffset;
    }
    
    if (params.leftConnectionsFirstOffsetMode === 'fromRightConnection') {
      // Режим привязки к правому подключению
      if (params.rightConnections.length === 0) {
        return params.leftConnectionsFirstOffset;
      }
      
      const refIndex = params.leftConnectionsFirstOffsetReferenceIndex;
      if (refIndex >= params.rightConnections.length) {
        return params.leftConnectionsFirstOffset;
      }
      
      // Вычисляем позицию выбранного правого подключения
      let rightRefOffset = params.rightConnectionsFirstOffset;
      for (let i = 0; i < refIndex; i++) {
        const spacing = Number(params.rightConnections[i].spacingToNext) || params.rightConnectionsSpacing;
        rightRefOffset += spacing;
      }
      
      return rightRefOffset + params.leftConnectionsFirstOffset;
    }
    
    return params.leftConnectionsFirstOffset;
  }, [
    params.connectionsPlacementMode,
    params.leftConnectionsFirstOffsetMode,
    params.leftConnectionsFirstOffset,
    params.leftConnectionsFirstOffsetReferenceIndex,
    params.rightConnectionsFirstOffset,
    params.rightConnectionsSpacing,
    params.rightConnections
  ]);

  // Вычисляем эффективный отступ первого правого подключения
  const effectiveRightFirstOffset = useMemo(() => {
    if (params.connectionsPlacementMode !== 'spacing') {
      return params.rightConnectionsFirstOffset;
    }
    
    if (params.rightConnectionsFirstOffsetMode === 'fromLeftConnection') {
      // Режим привязки к левому подключению
      if (params.leftConnections.length === 0) {
        return params.rightConnectionsFirstOffset;
      }
      
      const refIndex = params.rightConnectionsFirstOffsetReferenceIndex;
      if (refIndex >= params.leftConnections.length) {
        return params.rightConnectionsFirstOffset;
      }
      
      // Вычисляем позицию выбранного левого подключения
      let leftRefOffset = effectiveLeftFirstOffset;
      for (let i = 0; i < refIndex; i++) {
        const spacing = Number(params.leftConnections[i].spacingToNext) || params.leftConnectionsSpacing;
        leftRefOffset += spacing;
      }
      
      return leftRefOffset + params.rightConnectionsFirstOffset;
    }
    
    return params.rightConnectionsFirstOffset;
  }, [
    params.connectionsPlacementMode,
    params.rightConnectionsFirstOffsetMode,
    params.rightConnectionsFirstOffset,
    params.rightConnectionsFirstOffsetReferenceIndex,
    params.leftConnectionsSpacing,
    params.leftConnections,
    effectiveLeftFirstOffset
  ]);

  // Автоматическое отключение привязки при удалении всех подключений
  useEffect(() => {
    // Если включена привязка левых к правым, но правых подключений больше нет
    if (params.leftConnectionsFirstOffsetMode === 'fromRightConnection' && params.rightConnections.length === 0) {
      setParams(prev => ({
        ...prev,
        leftConnectionsFirstOffsetMode: 'fromEdge',
        leftConnectionsFirstOffset: prev.leftConnectionsFirstOffsetSaved !== null 
          ? prev.leftConnectionsFirstOffsetSaved 
          : prev.leftConnectionsFirstOffset,
        leftConnectionsFirstOffsetSaved: null
      }));
    }
    
    // Если включена привязка правых к левым, но левых подключений больше нет
    if (params.rightConnectionsFirstOffsetMode === 'fromLeftConnection' && params.leftConnections.length === 0) {
      setParams(prev => ({
        ...prev,
        rightConnectionsFirstOffsetMode: 'fromEdge',
        rightConnectionsFirstOffset: prev.rightConnectionsFirstOffsetSaved !== null 
          ? prev.rightConnectionsFirstOffsetSaved 
          : prev.rightConnectionsFirstOffset,
        rightConnectionsFirstOffsetSaved: null
      }));
    }
  }, [params.leftConnections.length, params.rightConnections.length, params.leftConnectionsFirstOffsetMode, params.rightConnectionsFirstOffsetMode]);

  // Вычисляем позицию последнего подключения для левых и правых (для режима spacing)
  const shouldUseLeftOffset = useMemo(() => {
    if (params.connectionsPlacementMode !== 'spacing') {
      return true; // Если в режиме отступов, используем отдельные отступы
    }
    
    // Позиция конца левых подключений (без учета последнего отступа)
    let leftEnd = effectiveLeftFirstOffset;
    if (params.leftConnections.length > 1) {
      for (let i = 0; i < params.leftConnections.length - 1; i++) {
        const spacing = Number(params.leftConnections[i].spacingToNext) || params.leftConnectionsSpacing;
        leftEnd += spacing;
      }
    }
    
    // Позиция конца правых подключений (без учета последнего отступа)
    let rightEnd = effectiveRightFirstOffset;
    if (params.rightConnections.length > 1) {
      for (let i = 0; i < params.rightConnections.length - 1; i++) {
        const spacing = Number(params.rightConnections[i].spacingToNext) || params.rightConnectionsSpacing;
        rightEnd += spacing;
      }
    }
    
    // Определяем, какое подключение дальше
    // Если одно из них пустое, используем отступ непустого
    if (params.leftConnections.length === 0 && params.rightConnections.length > 0) {
      return false;
    }
    if (params.rightConnections.length === 0 && params.leftConnections.length > 0) {
      return true;
    }
    
    // Если оба есть, используем отступ более дальнего
    return leftEnd >= rightEnd;
  }, [
    params.connectionsPlacementMode,
    effectiveLeftFirstOffset,
    params.leftConnectionsSpacing,
    effectiveRightFirstOffset,
    params.rightConnectionsSpacing,
    params.leftConnections,
    params.rightConnections
  ]);

  // Вычисляем автоматическую высоту в режиме spacing
  const calculatedSeparatorHeight = useMemo(() => {
    if (params.connectionsPlacementMode !== 'spacing') {
      return params.separatorHeight; // В режиме отступов используем заданную высоту
    }
    
    // Вычисляем максимальную позицию левых подключений
    let leftMaxPosition = effectiveLeftFirstOffset;
    if (params.leftConnections.length > 1) {
      for (let i = 0; i < params.leftConnections.length - 1; i++) {
        const spacing = Number(params.leftConnections[i].spacingToNext) || params.leftConnectionsSpacing;
        leftMaxPosition += spacing;
      }
    }
    leftMaxPosition += params.leftConnectionsLastOffset;
    
    // Вычисляем максимальную позицию правых подключений
    let rightMaxPosition = effectiveRightFirstOffset;
    if (params.rightConnections.length > 1) {
      for (let i = 0; i < params.rightConnections.length - 1; i++) {
        const spacing = Number(params.rightConnections[i].spacingToNext) || params.rightConnectionsSpacing;
        rightMaxPosition += spacing;
      }
    }
    rightMaxPosition += params.rightConnectionsLastOffset;
    
    // Используем максимальное значение
    const maxPosition = Math.max(leftMaxPosition, rightMaxPosition);
    
    return Math.round(maxPosition);
  }, [
    params.connectionsPlacementMode,
    params.separatorHeight,
    effectiveLeftFirstOffset,
    params.leftConnectionsLastOffset,
    params.leftConnectionsSpacing,
    effectiveRightFirstOffset,
    params.rightConnectionsLastOffset,
    params.rightConnectionsSpacing,
    params.leftConnections,
    params.rightConnections
  ]);

  // Расчет общей высоты гидравлического разделителя (для отображения в UI)
  const totalSeparatorHeight = useMemo(
    () =>
      computeHydraulicSeparatorTotalHeight(
        {
          bodyHeight: calculatedSeparatorHeight,
          mainPipeDiameter: params.mainPipeDiameter,
          hasTopConnection: params.hasTopConnection,
          topCapFitting: params.topCapFitting,
          topCapFittingLength: params.topCapFittingLength,
          hasBottomConnection: params.hasBottomConnection,
          bottomCapFitting: params.bottomCapFitting,
          bottomCapFittingLength: params.bottomCapFittingLength,
          hasTopCap: params.hasTopCap,
          topCapType: params.topCapType,
          hasBottomCap: params.hasBottomCap,
          bottomCapType: params.bottomCapType,
        },
        (fittingId, fittingLength) => getFittingLengthForUI(fittingId, fittingLength)
      ),
    [
      calculatedSeparatorHeight,
      params.hasTopConnection,
      params.topCapFitting,
      params.topCapFittingLength,
      params.hasBottomConnection,
      params.bottomCapFitting,
      params.bottomCapFittingLength,
      params.hasTopCap,
      params.topCapType,
      params.mainPipeDiameter,
      params.hasBottomCap,
      params.bottomCapType,
      getFittingLengthForUI,
    ]
  );

  // Схема-превью гидравлического разделителя
  const schematicPreview = useMemo(() => {
    const svgWidth = 280;
    const svgHeight = 320;
    const padding = 30;
    const scale = 0.35;
    
    // Центр по горизонтали
    const centerX = svgWidth / 2;
    
    // Размеры для схемы: ширина фиксированная, высота динамическая
    const mainPipeWidth = 25; // Фиксированная ширина
    const mainPipeHeight = Math.min(200, calculatedSeparatorHeight * scale); // Динамическая высота
    
    // Позиция основной трубы
    const mainY = padding + 40;
    const mainEndY = mainY + mainPipeHeight;
    
    // Позиции подключений с учетом направления отсчета или межосевого
    const leftConnectionsPositions = params.connectionsPlacementMode === 'spacing'
      ? params.leftConnections.map((conn, index) => {
          // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
          let currentOffset = effectiveLeftFirstOffset;
          for (let i = 0; i < index; i++) {
            const spacing = Number(params.leftConnections[i].spacingToNext) || params.leftConnectionsSpacing;
            currentOffset += spacing;
          }
          const relativePos = currentOffset / calculatedSeparatorHeight;
          return mainY + mainPipeHeight * relativePos;
        })
      : params.leftConnections.map(conn => {
          // Режим отступов: используем offsetFromTop и offsetDirection
          const actualOffset = getConnectionPosition(
            conn.offsetFromTop,
            conn.offsetDirection,
            calculatedSeparatorHeight,
            0, // в превью не учитываем бортшайбы
            0, // в превью не учитываем прямые заглушки
            0,
            0
          );
          const relativePos = actualOffset / calculatedSeparatorHeight;
          return mainY + mainPipeHeight * relativePos;
        });
    
    const rightConnectionsPositions = params.connectionsPlacementMode === 'spacing'
      ? params.rightConnections.map((conn, index) => {
          // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
          let currentOffset = effectiveRightFirstOffset;
          for (let i = 0; i < index; i++) {
            const spacing = Number(params.rightConnections[i].spacingToNext) || params.rightConnectionsSpacing;
            currentOffset += spacing;
          }
          const relativePos = currentOffset / calculatedSeparatorHeight;
          return mainY + mainPipeHeight * relativePos;
        })
      : params.rightConnections.map(conn => {
          // Режим отступов: используем offsetFromTop и offsetDirection
          const actualOffset = getConnectionPosition(
            conn.offsetFromTop,
            conn.offsetDirection,
            calculatedSeparatorHeight,
            0, // в превью не учитываем бортшайбы
            0, // в превью не учитываем прямые заглушки
            0,
            0
          );
          const relativePos = actualOffset / calculatedSeparatorHeight;
          return mainY + mainPipeHeight * relativePos;
        });
    
    return (
      <svg 
        viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
        style={{ width: '100%', height: '100%', maxHeight: 320 }}
      >
        {/* Верхняя заглушка */}
        <rect
          x={centerX - mainPipeWidth / 2}
          y={mainY - 10}
          width={mainPipeWidth}
          height={10}
          fill="#bdbdbd"
          rx={params.topCapType === 'elliptical' ? 4 : 0}
        />
        
        {/* Фитинг на верхней заглушке */}
        {params.topCapFitting && (
          <line
            x1={centerX}
            y1={mainY - 10}
            x2={centerX}
            y2={mainY - 30}
            stroke="#424242"
            strokeWidth={3}
            strokeLinecap="round"
          />
        )}
        
        {/* Нижняя заглушка */}
        <rect
          x={centerX - mainPipeWidth / 2}
          y={mainEndY}
          width={mainPipeWidth}
          height={10}
          fill="#bdbdbd"
          rx={params.bottomCapType === 'elliptical' ? 4 : 0}
        />
        
        {/* Фитинг на нижней заглушке */}
        {params.bottomCapFitting && (
          <line
            x1={centerX}
            y1={mainEndY + 10}
            x2={centerX}
            y2={mainEndY + 30}
            stroke="#424242"
            strokeWidth={3}
            strokeLinecap="round"
          />
        )}
        
        {/* Основная труба (вертикальная) — верхняя половина подача, нижняя обратка */}
        <rect
          x={centerX - mainPipeWidth / 2}
          y={mainY}
          width={mainPipeWidth}
          height={mainPipeHeight / 2}
          fill="#d32f2f"
          rx={2}
        />
        <rect
          x={centerX - mainPipeWidth / 2}
          y={mainY + mainPipeHeight / 2}
          width={mainPipeWidth}
          height={mainPipeHeight / 2}
          fill="#212121"
          rx={2}
        />
        
        {/* Левые подключения */}
        {leftConnectionsPositions.map((y, i) => {
          const isSupply = y < mainY + mainPipeHeight / 2;
          const connColor = isSupply ? '#d32f2f' : '#212121';
          return (
            <g key={`left-${i}`}>
              <line
                x1={centerX - mainPipeWidth / 2}
                y1={y}
                x2={padding + 20}
                y2={y}
                stroke={connColor}
                strokeWidth={3}
                strokeLinecap="round"
              />
              <text
                x={padding + 10}
                y={y - 5}
                fontSize={9}
                fill={connColor}
                textAnchor="middle"
              >
                {i + 1}
              </text>
            </g>
          );
        })}
        
        {/* Правые подключения */}
        {rightConnectionsPositions.map((y, i) => {
          const isSupply = y < mainY + mainPipeHeight / 2;
          const connColor = isSupply ? '#d32f2f' : '#212121';
          return (
            <g key={`right-${i}`}>
              <line
                x1={centerX + mainPipeWidth / 2}
                y1={y}
                x2={svgWidth - padding - 20}
                y2={y}
                stroke={connColor}
                strokeWidth={3}
                strokeLinecap="round"
              />
              <text
                x={svgWidth - padding - 10}
                y={y - 5}
                fontSize={9}
                fill={connColor}
                textAnchor="middle"
              >
                {i + 1}
              </text>
            </g>
          );
        })}
        
        {/* Дополнительные выходы */}
        {params.additionalOutlets.map((outlet, i) => {
          const side = outlet.side || 'both';
          const positionType = outlet.positionType || 'center';
          
          // Расчет позиции по высоте (Y) с учетом направления отсчета
          // distanceDirection: 'left' = сверху, 'right' = снизу
          let yPos = mainY + mainPipeHeight / 2; // По умолчанию по центру
          if (positionType === 'distance') {
            const distanceFromEdge = (outlet.distanceFromEdge || 50) * scale;
            if (outlet.distanceDirection === 'left' || !outlet.distanceDirection) {
              yPos = mainY + distanceFromEdge;
            } else {
              yPos = mainEndY - distanceFromEdge;
            }
          }
          
          // На тёмной (обратной) половине трубы используем светлый цвет + обводку для видимости
          const onReturnZone = yPos >= mainY + mainPipeHeight / 2;
          const outletStroke = onReturnZone ? '#e0e0e0' : '#757575';
          const outletCircleFill = onReturnZone ? '#e0e0e0' : '#757575';
          const outletCircleStroke = onReturnZone ? '#424242' : 'none';
          const outletCircleStrokeWidth = onReturnZone ? 1 : 0;
          const outletLineBgStroke = onReturnZone ? '#424242' : 'none';
          const outletLineBgWidth = onReturnZone ? 1.5 : 0;
          const outletLineWidth = onReturnZone ? 0.8 : 2;
          
          const elements = [];
          
          // Отрисовка в зависимости от стороны
          if (side === 'left') {
            elements.push(
              <g key={`separator-add-left-${i}`}>
                <line x1={centerX - mainPipeWidth / 2} y1={yPos} x2={centerX - mainPipeWidth / 2 - 15} y2={yPos} stroke={outletLineBgStroke} strokeWidth={outletLineBgWidth} />
                <line x1={centerX - mainPipeWidth / 2} y1={yPos} x2={centerX - mainPipeWidth / 2 - 15} y2={yPos} stroke={outletStroke} strokeWidth={outletLineWidth} />
                <circle cx={centerX - mainPipeWidth / 2 - 18} cy={yPos} r={3} fill={outletCircleFill} stroke={outletCircleStroke} strokeWidth={outletCircleStrokeWidth} />
              </g>
            );
          }
          
          if (side === 'right') {
            elements.push(
              <g key={`separator-add-right-${i}`}>
                <line x1={centerX + mainPipeWidth / 2} y1={yPos} x2={centerX + mainPipeWidth / 2 + 15} y2={yPos} stroke={outletLineBgStroke} strokeWidth={outletLineBgWidth} />
                <line x1={centerX + mainPipeWidth / 2} y1={yPos} x2={centerX + mainPipeWidth / 2 + 15} y2={yPos} stroke={outletStroke} strokeWidth={outletLineWidth} />
                <circle cx={centerX + mainPipeWidth / 2 + 18} cy={yPos} r={3} fill={outletCircleFill} stroke={outletCircleStroke} strokeWidth={outletCircleStrokeWidth} />
              </g>
            );
          }
          
          if (side === 'front') {
            elements.push(
              <circle 
                key={`separator-add-front-${i}`}
                cx={centerX} 
                cy={yPos} 
                r={3} 
                fill={outletCircleFill}
                stroke={outletCircleStroke}
                strokeWidth={outletCircleStrokeWidth}
              />
            );
          }
          
          if (side === 'back') {
            elements.push(
              <circle 
                key={`separator-add-back-${i}`}
                cx={centerX} 
                cy={yPos} 
                r={3} 
                fill={outletCircleFill}
                stroke={outletCircleStroke}
                strokeWidth={outletCircleStrokeWidth}
              />
            );
          }
          
          if (side === 'both') {
            elements.push(
              <circle 
                key={`separator-add-both-${i}`}
                cx={centerX} 
                cy={yPos} 
                r={3} 
                fill={outletCircleFill}
                stroke={outletCircleStroke}
                strokeWidth={outletCircleStrokeWidth}
              />
            );
          }
          
          return elements;
        })}
        
        {/* Размерные линии межосевого расстояния для левых подключений */}
        {leftConnectionsPositions.length >= 2 && params.leftConnections.length >= 2 && (
          <>
            {params.connectionsPlacementMode === 'spacing' ? (
              // Режим межосевого: показываем размерные линии между соседними подключениями по порядку
              params.leftConnections.map((conn, index) => {
                if (index === params.leftConnections.length - 1) return null; // Последнее подключение не имеет "следующего"
                
                const y1 = leftConnectionsPositions[index];
                const y2 = leftConnectionsPositions[index + 1];
                const spacing = Number(conn.spacingToNext) || params.leftConnectionsSpacing;
                
                return (
                  <g key={`left-spacing-${index}`}>
                    <line 
                      x1={padding} 
                      y1={y1} 
                      x2={padding} 
                      y2={y2} 
                      stroke="#666" 
                      strokeWidth={0.5} 
                    />
                    <line 
                      x1={padding - 2} 
                      y1={y1} 
                      x2={padding + 2} 
                      y2={y1} 
                      stroke="#666" 
                      strokeWidth={0.5} 
                    />
                    <line 
                      x1={padding - 2} 
                      y1={y2} 
                      x2={padding + 2} 
                      y2={y2} 
                      stroke="#666" 
                      strokeWidth={0.5} 
                    />
                    <text 
                      x={padding - 4} 
                      y={(y1 + y2) / 2} 
                      fontSize={9} 
                      fill="#666" 
                      textAnchor="end" 
                      fontWeight="bold"
                    >
                      {spacing}
                    </text>
                  </g>
                );
              })
            ) : (
              // Режим отступов: сортируем по реальной позиции и показываем размерные линии между физически соседними
              (() => {
                // Создаём массив с индексами и позициями
                const positionsWithIndices = params.leftConnections.map((conn, index) => {
                  const offset = getConnectionPosition(
                    conn.offsetFromTop,
                    conn.offsetDirection,
                    calculatedSeparatorHeight,
                    0, 0, 0, 0
                  );
                  return {
                    index,
                    offset,
                    yPos: leftConnectionsPositions[index]
                  };
                });
                
                // Сортируем по реальной позиции (offset)
                positionsWithIndices.sort((a, b) => a.offset - b.offset);
                
                // Создаём размерные линии между физически соседними подключениями
                return positionsWithIndices.map((item, sortedIndex) => {
                  if (sortedIndex === positionsWithIndices.length - 1) return null;
                  
                  const nextItem = positionsWithIndices[sortedIndex + 1];
                  const distance = Math.abs(nextItem.offset - item.offset);
                  
                  return (
                    <g key={`left-offset-${item.index}-${nextItem.index}`}>
                      <line 
                        x1={padding} 
                        y1={item.yPos} 
                        x2={padding} 
                        y2={nextItem.yPos} 
                        stroke="#666" 
                        strokeWidth={0.5} 
                      />
                      <line 
                        x1={padding - 2} 
                        y1={item.yPos} 
                        x2={padding + 2} 
                        y2={item.yPos} 
                        stroke="#666" 
                        strokeWidth={0.5} 
                      />
                      <line 
                        x1={padding - 2} 
                        y1={nextItem.yPos} 
                        x2={padding + 2} 
                        y2={nextItem.yPos} 
                        stroke="#666" 
                        strokeWidth={0.5} 
                      />
                      <text 
                        x={padding - 4} 
                        y={(item.yPos + nextItem.yPos) / 2} 
                        fontSize={9} 
                        fill="#666" 
                        textAnchor="end" 
                        fontWeight="bold"
                      >
                        {Math.round(distance)}
                      </text>
                    </g>
                  );
                });
              })()
            )}
          </>
        )}
        
        {/* Размерные линии межосевого расстояния для правых подключений */}
        {rightConnectionsPositions.length >= 2 && params.rightConnections.length >= 2 && (
          <>
            {params.connectionsPlacementMode === 'spacing' ? (
              // Режим межосевого: показываем размерные линии между соседними подключениями по порядку
              params.rightConnections.map((conn, index) => {
                if (index === params.rightConnections.length - 1) return null; // Последнее подключение не имеет "следующего"
                
                const y1 = rightConnectionsPositions[index];
                const y2 = rightConnectionsPositions[index + 1];
                const spacing = Number(conn.spacingToNext) || params.rightConnectionsSpacing;
                
                return (
                  <g key={`right-spacing-${index}`}>
                    <line 
                      x1={svgWidth - padding} 
                      y1={y1} 
                      x2={svgWidth - padding} 
                      y2={y2} 
                      stroke="#666" 
                      strokeWidth={0.5} 
                    />
                    <line 
                      x1={svgWidth - padding - 2} 
                      y1={y1} 
                      x2={svgWidth - padding + 2} 
                      y2={y1} 
                      stroke="#666" 
                      strokeWidth={0.5} 
                    />
                    <line 
                      x1={svgWidth - padding - 2} 
                      y1={y2} 
                      x2={svgWidth - padding + 2} 
                      y2={y2} 
                      stroke="#666" 
                      strokeWidth={0.5} 
                    />
                    <text 
                      x={svgWidth - padding + 4} 
                      y={(y1 + y2) / 2} 
                      fontSize={9} 
                      fill="#666" 
                      textAnchor="start" 
                      fontWeight="bold"
                    >
                      {spacing}
                    </text>
                  </g>
                );
              })
            ) : (
              // Режим отступов: сортируем по реальной позиции и показываем размерные линии между физически соседними
              (() => {
                // Создаём массив с индексами и позициями
                const positionsWithIndices = params.rightConnections.map((conn, index) => {
                  const offset = getConnectionPosition(
                    conn.offsetFromTop,
                    conn.offsetDirection,
                    calculatedSeparatorHeight,
                    0, 0, 0, 0
                  );
                  return {
                    index,
                    offset,
                    yPos: rightConnectionsPositions[index]
                  };
                });
                
                // Сортируем по реальной позиции (offset)
                positionsWithIndices.sort((a, b) => a.offset - b.offset);
                
                // Создаём размерные линии между физически соседними подключениями
                return positionsWithIndices.map((item, sortedIndex) => {
                  if (sortedIndex === positionsWithIndices.length - 1) return null;
                  
                  const nextItem = positionsWithIndices[sortedIndex + 1];
                  const distance = Math.abs(nextItem.offset - item.offset);
                  
                  return (
                    <g key={`right-offset-${item.index}-${nextItem.index}`}>
                      <line 
                        x1={svgWidth - padding} 
                        y1={item.yPos} 
                        x2={svgWidth - padding} 
                        y2={nextItem.yPos} 
                        stroke="#666" 
                        strokeWidth={0.5} 
                      />
                      <line 
                        x1={svgWidth - padding - 2} 
                        y1={item.yPos} 
                        x2={svgWidth - padding + 2} 
                        y2={item.yPos} 
                        stroke="#666" 
                        strokeWidth={0.5} 
                      />
                      <line 
                        x1={svgWidth - padding - 2} 
                        y1={nextItem.yPos} 
                        x2={svgWidth - padding + 2} 
                        y2={nextItem.yPos} 
                        stroke="#666" 
                        strokeWidth={0.5} 
                      />
                      <text 
                        x={svgWidth - padding + 4} 
                        y={(item.yPos + nextItem.yPos) / 2} 
                        fontSize={9} 
                        fill="#666" 
                        textAnchor="start" 
                        fontWeight="bold"
                      >
                        {Math.round(distance)}
                      </text>
                    </g>
                  );
                });
              })()
            )}
          </>
        )}
        
        {/* Маркер стрелки */}
        <defs>
          <marker
            id="arrowhead-v"
            markerWidth="6"
            markerHeight="6"
            refX="3"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill="#999" />
          </marker>
        </defs>
      </svg>
    );
  }, [params, calculatedSeparatorHeight, effectiveLeftFirstOffset, effectiveRightFirstOffset]);

  // Генерация
  const handleGenerate = () => {
    // Проверяем совместимость всех фитингов перед генерацией
    const incompatibleConnections = [];
    
    if (params.shape === 'square') {
      // Для квадратных профилей используем специальную логику совместимости
      const profile = params.mainPipeProfile;
      
      // Проверяем левые подключения
      params.leftConnections.forEach((conn, index) => {
        if (!isEndFittingCompatibleWithSquareProfile(conn.fittingId, profile)) {
          incompatibleConnections.push(`Левое подключение ${index + 1}`);
        }
      });
      
      // Проверяем правые подключения
      params.rightConnections.forEach((conn, index) => {
        if (!isEndFittingCompatibleWithSquareProfile(conn.fittingId, profile)) {
          incompatibleConnections.push(`Правое подключение ${index + 1}`);
        }
      });
      
      // Проверяем верхний фитинг
      if (params.topCapFitting && !isEndFittingCompatibleWithSquareProfile(params.topCapFitting, profile)) {
        incompatibleConnections.push('Фитинг на верхней заглушке');
      }
      
      // Проверяем нижний фитинг
      if (params.bottomCapFitting && !isEndFittingCompatibleWithSquareProfile(params.bottomCapFitting, profile)) {
        incompatibleConnections.push('Фитинг на нижней заглушке');
      }
    } else {
      // Для круглых труб используем стандартную логику
      // Проверяем левые подключения
      params.leftConnections.forEach((conn, index) => {
        if (!isFittingCompatibleWithMainPipe(conn.fittingId, params.mainPipeDiameter)) {
          incompatibleConnections.push(`Левое подключение ${index + 1}`);
        }
      });
      
      // Проверяем правые подключения
      params.rightConnections.forEach((conn, index) => {
        if (!isFittingCompatibleWithMainPipe(conn.fittingId, params.mainPipeDiameter)) {
          incompatibleConnections.push(`Правое подключение ${index + 1}`);
        }
      });
      
      // Проверяем верхний фитинг
      if (params.topCapFitting && !isEndFittingCompatibleWithMainPipe(params.topCapFitting, params.mainPipeDiameter)) {
        incompatibleConnections.push('Фитинг на верхней заглушке');
      }
      
      // Проверяем нижний фитинг
      if (params.bottomCapFitting && !isEndFittingCompatibleWithMainPipe(params.bottomCapFitting, params.mainPipeDiameter)) {
        incompatibleConnections.push('Фитинг на нижней заглушке');
      }
    }
    
    if (incompatibleConnections.length > 0) {
      setIncompatibilityDialog({
        open: true,
        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
      });
      return;
    }
    
    // Нормализуем параметры перед генерацией
    const normalizedParams = {
      ...params,
      // Используем вычисленную высоту вместо исходной
      separatorHeight: calculatedSeparatorHeight,
      // Используем эффективные отступы для левых и правых подключений в режиме spacing
      leftConnectionsFirstOffset: effectiveLeftFirstOffset,
      rightConnectionsFirstOffset: effectiveRightFirstOffset,
      topCapFittingLength: params.topCapFittingLength === null || params.topCapFittingLength === undefined 
        ? null 
        : params.topCapFittingLength,
      bottomCapFittingLength: params.bottomCapFittingLength === null || params.bottomCapFittingLength === undefined 
        ? null 
        : params.bottomCapFittingLength,
    };
    
    // Для накидных гаек и шайб с резьбой: если значение равно старому дефолту, сбрасываем на null
    if (params.topCapFitting?.startsWith('Nak_Gaika_') || params.topCapFitting?.startsWith('Shaib_Nar_Rezba_')) {
      const wrongDefault = params.shape === 'square' ? 40 : 58;
      if (params.topCapFittingLength === wrongDefault) {
        normalizedParams.topCapFittingLength = null;
      }
    }
    
    if (params.bottomCapFitting?.startsWith('Nak_Gaika_') || params.bottomCapFitting?.startsWith('Shaib_Nar_Rezba_')) {
      const wrongDefault = params.shape === 'square' ? 40 : 58;
      if (params.bottomCapFittingLength === wrongDefault) {
        normalizedParams.bottomCapFittingLength = null;
      }
    }
    
    const objects = generateHydraulicSeparator(normalizedParams);
    onGenerate(objects, params);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        py: 3,
        overflow: 'visible',
      }}
    >
      <Container maxWidth="xl" sx={{ overflow: 'visible' }}>
        {/* Шапка */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <IconButton
              onClick={onBack}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <HomeIcon />
            </IconButton>
            {onBackToCharacteristics && (
              <IconButton
                onClick={onBackToCharacteristics}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {onBackToDrawing && (
              <IconButton
                onClick={onBackToDrawing}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Гидравлический разделитель
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Настройте параметры и нажмите "Сгенерировать"
                </Typography>
                {params._calculatedFrom === 'characteristics' && (
                  <Chip
                    label="Рассчитано из характеристик"
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
            <ToggleButtonGroup
              value={params.shape}
              exclusive
              onChange={(e, newShape) => {
                if (newShape !== null) {
                  const updates = { shape: newShape };
                  
                  // При переключении на квадратный устанавливаем профиль
                  if (newShape === 'square') {
                    // Сохраняем текущие типы заглушек перед переключением на квадратную форму
                    setSavedCapTypes({
                      topCapType: params.topCapType,
                      bottomCapType: params.bottomCapType,
                    });
                    
                    const newProfile = params.mainPipeProfile || '80x80';
                    updates.mainPipeProfile = newProfile;
                    
                    // Для квадратных только прямые заглушки
                    updates.topCapType = 'straight';
                    updates.bottomCapType = 'straight';
                    updates.topCapElementType = 'cap';
                    updates.bottomCapElementType = 'cap';
                    updates.hasTopCap = true;
                    updates.hasBottomCap = true;
                    
                    // Сбрасываем длины торцевых подключений для применения новых дефолтов
                    updates.topCapFittingLength = null;
                    updates.bottomCapFittingLength = null;
                    
                    // Обновляем левые подключения
                    updates.leftConnections = params.leftConnections.map((conn) => {
                      if (!isFittingCompatibleWithSquareProfile(conn.fittingId, newProfile)) {
                        const fittingInfo = fittingsData[conn.fittingId];
                        const category = fittingInfo?.category || 'Ниппели';
                        const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                        const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                        
                        if (compatibleFittings.length > 0) {
                          const firstCompatibleFitting = compatibleFittings[0];
                          return {
                            ...conn,
                            fittingId: firstCompatibleFitting.id,
                            fittingLength: null,
                          };
                        }
                      }
                      return conn;
                    });
                    
                    // Обновляем правые подключения
                    updates.rightConnections = params.rightConnections.map((conn) => {
                      if (!isFittingCompatibleWithSquareProfile(conn.fittingId, newProfile)) {
                        const fittingInfo = fittingsData[conn.fittingId];
                        const category = fittingInfo?.category || 'Ниппели';
                        const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                        const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                        
                        if (compatibleFittings.length > 0) {
                          const firstCompatibleFitting = compatibleFittings[0];
                          return {
                            ...conn,
                            fittingId: firstCompatibleFitting.id,
                            fittingLength: null,
                          };
                        }
                      }
                      return conn;
                    });
                    
                    // Обновляем верхнее подключение
                    if (params.topCapFitting && !isEndFittingCompatibleWithSquareProfile(params.topCapFitting, newProfile)) {
                      const topCategory = params.topCapFittingCategory || 'Муфты';
                      const topFittingsInCategory = getFittingsByCategory(topCategory, null, 'square', newProfile);
                      const topCompatibleFittings = topFittingsInCategory.filter(f => f.isCompatible);
                      
                      if (topCompatibleFittings.length > 0) {
                        const firstCompatibleFitting = topCompatibleFittings[0];
                        updates.topCapFitting = firstCompatibleFitting.id;
                        updates.topCapFittingLength = null;
                      }
                    }
                    
                    // Обновляем нижнее подключение
                    if (params.bottomCapFitting && !isEndFittingCompatibleWithSquareProfile(params.bottomCapFitting, newProfile)) {
                      const bottomCategory = params.bottomCapFittingCategory || 'Муфты';
                      const bottomFittingsInCategory = getFittingsByCategory(bottomCategory, null, 'square', newProfile);
                      const bottomCompatibleFittings = bottomFittingsInCategory.filter(f => f.isCompatible);
                      
                      if (bottomCompatibleFittings.length > 0) {
                        const firstCompatibleFitting = bottomCompatibleFittings[0];
                        updates.bottomCapFitting = firstCompatibleFitting.id;
                        updates.bottomCapFittingLength = null;
                      }
                    }
                    
                    // Обновляем дополнительные выходы
                    updates.additionalOutlets = params.additionalOutlets.map((outlet) => {
                      if (!isFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                        const fittingInfo = fittingsData[outlet.fittingId];
                        const category = fittingInfo?.category || 'Ниппели';
                        const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                        const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                        
                        if (compatibleFittings.length > 0) {
                          const firstCompatibleFitting = compatibleFittings[0];
                          return {
                            ...outlet,
                            fittingId: firstCompatibleFitting.id,
                            fittingLength: null,
                          };
                        }
                      }
                      return outlet;
                    });
                  } else if (newShape === 'round') {
                    // При переключении на круглый профиль
                    const newDiameter = params.mainPipeDiameter || 88.9;
                    updates.mainPipeDiameter = newDiameter;
                    
                    // Восстанавливаем сохраненные типы заглушек, если они были сохранены
                    if (savedCapTypes) {
                      updates.topCapType = savedCapTypes.topCapType;
                      updates.bottomCapType = savedCapTypes.bottomCapType;
                      
                      // Очищаем сохраненные значения
                      setSavedCapTypes(null);
                    } else {
                      // Если нет сохраненных значений, устанавливаем правильный тип заглушки в зависимости от диаметра
                      if (newDiameter > 101.6) {
                        updates.topCapType = 'elliptical';
                        updates.bottomCapType = 'elliptical';
                      } else {
                        updates.topCapType = 'straight';
                        updates.bottomCapType = 'straight';
                      }
                    }
                    
                    // Сбрасываем длины торцевых подключений для применения новых дефолтов
                    updates.topCapFittingLength = null;
                    updates.bottomCapFittingLength = null;
                  }
                  
                  updateParam(updates);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="round" aria-label="круглая форма">
                <Tooltip title="Круглая форма">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RadioButtonUncheckedIcon fontSize="small" />
                    <Typography variant="body2">Круглый</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="square" aria-label="квадратная форма">
                <Tooltip title="Квадратная форма">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CropSquareIcon fontSize="small" />
                    <Typography variant="body2">Квадратный</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ overflow: 'visible' }}>
          {/* Левая панель — схема-превью */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Схема
              </Typography>
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  p: 2,
                  minHeight: 320,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '0.0625rem solid',
                  borderColor: 'grey.200',
                }}
              >
                {schematicPreview}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Легенда */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip size="small" sx={{ bgcolor: '#d32f2f', color: 'white' }} label="Подача" />
                <Chip size="small" sx={{ bgcolor: '#212121', color: 'white' }} label="Обратка" />
                <Chip size="small" sx={{ bgcolor: '#bdbdbd', color: 'white' }} label="Заглушка" />
                <Chip size="small" sx={{ bgcolor: '#757575', color: 'white' }} label="Доп. выход" />
                <Chip size="small" sx={{ bgcolor: '#424242', color: 'white' }} label="Фитинг" />
              </Box>
              
              {/* Информация о рассчитанных параметрах */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Рассчитанные параметры:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="body2">
                  <strong>Высота трубы:</strong> {params.connectionsPlacementMode === 'spacing' ? calculatedSeparatorHeight : params.separatorHeight} мм
                  {params.connectionsPlacementMode === 'spacing' && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Авто)
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  <strong>Общая высота:</strong> {totalSeparatorHeight} мм
                </Typography>
                {params.hasTopConnection && params.topCapFitting && (
                  <Typography variant="caption" color="text.secondary">
                    (Верх: {getFittingLengthForUI(params.topCapFitting, params.topCapFittingLength)} мм)
                  </Typography>
                )}
                {params.hasBottomConnection && params.bottomCapFitting && (
                  <Typography variant="caption" color="text.secondary">
                    (Низ: {getFittingLengthForUI(params.bottomCapFitting, params.bottomCapFittingLength)} мм)
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Правая панель — параметры */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Параметры
              </Typography>

              {/* Основные параметры */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                Основная труба
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{params.shape === 'round' ? 'Диаметр' : 'Профиль'}</InputLabel>
                    <Select
                      value={params.shape === 'round' ? params.mainPipeDiameter : params.mainPipeProfile}
                      label={params.shape === 'round' ? 'Диаметр' : 'Профиль'}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        
                        if (params.shape === 'round') {
                          const newDiameter = newValue;
                        
                        // Проверяем и сбрасываем несовместимые фитинги в левых подключениях
                        const updatedLeftConnections = params.leftConnections.map((conn) => {
                          if (!isFittingCompatibleWithMainPipe(conn.fittingId, newDiameter)) {
                            // Ищем максимальный совместимый фитинг в той же категории
                            const fittingInfo = fittingsData[conn.fittingId];
                            const category = fittingInfo?.category || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(category, newDiameter);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...conn,
                                fittingId: maxCompatibleFitting.id,
                                fittingLength: null,
                              };
                            } else {
                              // Сбрасываем на дефолтный Nippel_3.4 если он совместим
                              const defaultFittingId = 'Nippel_3.4';
                              if (isFittingCompatibleWithMainPipe(defaultFittingId, newDiameter)) {
                                return {
                                  ...conn,
                                  fittingId: defaultFittingId,
                                  fittingLength: null,
                                };
                              } else {
                                // Ищем максимальный совместимый ниппель
                                const nippelFittings = getFittingsByCategory('Ниппели', newDiameter);
                                const compatibleNippels = nippelFittings.filter(f => f.isCompatible);
                                if (compatibleNippels.length > 0) {
                                  const maxCompatibleNippel = compatibleNippels[compatibleNippels.length - 1];
                                  return {
                                    ...conn,
                                    fittingId: maxCompatibleNippel.id,
                                    fittingLength: null,
                                  };
                                }
                              }
                            }
                          }
                          return conn;
                        });
                        
                        // Проверяем и сбрасываем несовместимые фитинги в правых подключениях
                        const updatedRightConnections = params.rightConnections.map((conn) => {
                          if (!isFittingCompatibleWithMainPipe(conn.fittingId, newDiameter)) {
                            // Аналогичная логика для правых подключений
                            const fittingInfo = fittingsData[conn.fittingId];
                            const category = fittingInfo?.category || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(category, newDiameter);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...conn,
                                fittingId: maxCompatibleFitting.id,
                                fittingLength: null,
                              };
                            } else {
                              const defaultFittingId = 'Nippel_3.4';
                              if (isFittingCompatibleWithMainPipe(defaultFittingId, newDiameter)) {
                                return {
                                  ...conn,
                                  fittingId: defaultFittingId,
                                  fittingLength: null,
                                };
                              } else {
                                const nippelFittings = getFittingsByCategory('Ниппели', newDiameter);
                                const compatibleNippels = nippelFittings.filter(f => f.isCompatible);
                                if (compatibleNippels.length > 0) {
                                  const maxCompatibleNippel = compatibleNippels[compatibleNippels.length - 1];
                                  return {
                                    ...conn,
                                    fittingId: maxCompatibleNippel.id,
                                    fittingLength: null,
                                  };
                                }
                              }
                            }
                          }
                          return conn;
                        });
                        
                        // Проверяем верхний фитинг
                        let updatedTopCapFitting = params.topCapFitting;
                        let updatedTopCapFittingCategory = params.topCapFittingCategory;
                        if (updatedTopCapFitting && !isEndFittingCompatibleWithMainPipe(updatedTopCapFitting, newDiameter)) {
                          // Верхний фитинг несовместим - ищем максимальный совместимый в той же категории
                          if (updatedTopCapFittingCategory) {
                            const fittingsInCategory = getFittingsByCategory(updatedTopCapFittingCategory, newDiameter, 'round', null, true);
                            const compatibleFittings = fittingsInCategory.filter(f => isEndFittingCompatibleWithMainPipe(f.id, newDiameter));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              updatedTopCapFitting = maxCompatibleFitting.id;
                            } else {
                              // Нет совместимых - сбрасываем выбор
                              updatedTopCapFitting = '';
                            }
                          } else {
                            updatedTopCapFitting = '';
                          }
                        } else if (updatedTopCapFitting) {
                          // Фитинг совместим, но проверяем, есть ли фитинг того же типа, но подходящий под новый диаметр
                          // Это нужно для случая, когда фланец Ду80 совместим с трубой 42.4, но лучше использовать Ду32
                          if (updatedTopCapFittingCategory) {
                            const fittingsInCategory = getFittingsByCategory(updatedTopCapFittingCategory, newDiameter, 'round', null, true);
                            // Ищем фитинг, который лучше подходит для данного диаметра
                            // Для фланцев это фланец, DN которого соответствует диаметру трубы
                            const betterFitting = fittingsInCategory.find(f => {
                              if (f.id.startsWith('Flanec_')) {
                                const dnMatch = f.id.match(/Flanec_(\d+)/);
                                if (dnMatch) {
                                  const dn = parseInt(dnMatch[1]);
                                  const DN_TO_PIPE_DIAMETER = {
                                    25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
                                    80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
                                  };
                                  return DN_TO_PIPE_DIAMETER[dn] === newDiameter;
                                }
                              }
                              return false;
                            });
                            if (betterFitting) {
                              updatedTopCapFitting = betterFitting.id;
                            }
                          }
                        }
                        
                        // Проверяем нижний фитинг
                        let updatedBottomCapFitting = params.bottomCapFitting;
                        let updatedBottomCapFittingCategory = params.bottomCapFittingCategory;
                        if (updatedBottomCapFitting && !isEndFittingCompatibleWithMainPipe(updatedBottomCapFitting, newDiameter)) {
                          // Нижний фитинг несовместим - ищем максимальный совместимый в той же категории
                          if (updatedBottomCapFittingCategory) {
                            const fittingsInCategory = getFittingsByCategory(updatedBottomCapFittingCategory, newDiameter, 'round', null, true);
                            const compatibleFittings = fittingsInCategory.filter(f => isEndFittingCompatibleWithMainPipe(f.id, newDiameter));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              updatedBottomCapFitting = maxCompatibleFitting.id;
                            } else {
                              // Нет совместимых - сбрасываем выбор
                              updatedBottomCapFitting = '';
                            }
                          } else {
                            updatedBottomCapFitting = '';
                          }
                        } else if (updatedBottomCapFitting) {
                          // Фитинг совместим, но проверяем, есть ли фитинг того же типа, но подходящий под новый диаметр
                          // Это нужно для случая, когда фланец Ду80 совместим с трубой 42.4, но лучше использовать Ду32
                          if (updatedBottomCapFittingCategory) {
                            const fittingsInCategory = getFittingsByCategory(updatedBottomCapFittingCategory, newDiameter, 'round', null, true);
                            // Ищем фитинг, который лучше подходит для данного диаметра
                            // Для фланцев это фланец, DN которого соответствует диаметру трубы
                            const betterFitting = fittingsInCategory.find(f => {
                              if (f.id.startsWith('Flanec_')) {
                                const dnMatch = f.id.match(/Flanec_(\d+)/);
                                if (dnMatch) {
                                  const dn = parseInt(dnMatch[1]);
                                  const DN_TO_PIPE_DIAMETER = {
                                    25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
                                    80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
                                  };
                                  return DN_TO_PIPE_DIAMETER[dn] === newDiameter;
                                }
                              }
                              return false;
                            });
                            if (betterFitting) {
                              updatedBottomCapFitting = betterFitting.id;
                            }
                          }
                        }
                        
                        // Обновляем заглушки/переходы для торцевых подключений
                        const capUpdates = {};
                        
                        // Верхнее подключение
                        if (params.hasTopConnection && updatedTopCapFitting) {
                          const topCapReq = getCapRequirementForFitting(updatedTopCapFitting, newDiameter);
                          capUpdates.hasTopCap = topCapReq.needsCap;
                          if (topCapReq.needsCap) {
                            if (topCapReq.capType === 'reducer' && topCapReq.reducer) {
                              capUpdates.topCapElementType = 'reducer';
                              capUpdates.topReducer = topCapReq.reducer.key;
                            } else {
                              // Эллиптическая заглушка (если нет перехода)
                              capUpdates.topCapElementType = 'cap';
                              capUpdates.topCapType = 'elliptical';
                              capUpdates.topReducer = null;
                            }
                          } else {
                            // Диаметры совпадают - впритык, заглушка не нужна
                            capUpdates.topCapElementType = 'cap';
                            capUpdates.topCapType = 'straight';
                            capUpdates.topReducer = null;
                          }
                        }
                        
                        // Нижнее подключение
                        if (params.hasBottomConnection && updatedBottomCapFitting) {
                          const bottomCapReq = getCapRequirementForFitting(updatedBottomCapFitting, newDiameter);
                          capUpdates.hasBottomCap = bottomCapReq.needsCap;
                          if (bottomCapReq.needsCap) {
                            if (bottomCapReq.capType === 'reducer' && bottomCapReq.reducer) {
                              capUpdates.bottomCapElementType = 'reducer';
                              capUpdates.bottomReducer = bottomCapReq.reducer.key;
                            } else {
                              // Эллиптическая заглушка (если нет перехода)
                              capUpdates.bottomCapElementType = 'cap';
                              capUpdates.bottomCapType = 'elliptical';
                              capUpdates.bottomReducer = null;
                            }
                          } else {
                            // Диаметры совпадают - впритык, заглушка не нужна
                            capUpdates.bottomCapElementType = 'cap';
                            capUpdates.bottomCapType = 'straight';
                            capUpdates.bottomReducer = null;
                          }
                        }
                        
                        // Если заглушка установлена, но нет подключения или фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                        if (!params.hasTopConnection && params.hasTopCap && params.topCapElementType === 'cap' && params.shape !== 'square') {
                          const ellipticalCap = findEllipticalCap(newDiameter);
                          if (ellipticalCap && params.topCapType !== 'elliptical') {
                            capUpdates.topCapType = 'elliptical';
                          }
                        }
                        if (!params.hasBottomConnection && params.hasBottomCap && params.bottomCapElementType === 'cap' && params.shape !== 'square') {
                          const ellipticalCap = findEllipticalCap(newDiameter);
                          if (ellipticalCap && params.bottomCapType !== 'elliptical') {
                            capUpdates.bottomCapType = 'elliptical';
                          }
                        }
                        
                        // Проверяем и обновляем дополнительные выходы
                        const updatedAdditionalOutlets = params.additionalOutlets.map((outlet) => {
                          if (outlet.fittingId && !isFittingCompatibleWithMainPipe(outlet.fittingId, newDiameter)) {
                            // Фитинг несовместим - ищем максимальный совместимый в той же категории
                            const category = outlet.fittingCategory || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(category, newDiameter);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...outlet,
                                fittingId: maxCompatibleFitting.id,
                                fittingLength: null,
                              };
                            } else {
                              // Нет совместимых фитингов - сбрасываем на дефолтный Nippel_3.4 если он совместим
                              const defaultFittingId = 'Nippel_3.4';
                              if (isFittingCompatibleWithMainPipe(defaultFittingId, newDiameter)) {
                                return {
                                  ...outlet,
                                  fittingId: defaultFittingId,
                                  fittingLength: null,
                                };
                              } else {
                                // Ищем максимальный совместимый ниппель
                                const nippelFittings = getFittingsByCategory('Ниппели', newDiameter);
                                const compatibleNippels = nippelFittings.filter(f => f.isCompatible);
                                if (compatibleNippels.length > 0) {
                                  const maxCompatibleNippel = compatibleNippels[compatibleNippels.length - 1];
                                  return {
                                    ...outlet,
                                    fittingId: maxCompatibleNippel.id,
                                    fittingLength: null,
                                  };
                                }
                              }
                            }
                          }
                          return outlet;
                        });
                        
                          updateParam({
                            mainPipeDiameter: newDiameter,
                            leftConnections: updatedLeftConnections,
                            rightConnections: updatedRightConnections,
                            additionalOutlets: updatedAdditionalOutlets,
                            topCapFitting: updatedTopCapFitting,
                            topCapFittingLength: updatedTopCapFitting === params.topCapFitting ? params.topCapFittingLength : null,
                            bottomCapFitting: updatedBottomCapFitting,
                            bottomCapFittingLength: updatedBottomCapFitting === params.bottomCapFitting ? params.bottomCapFittingLength : null,
                            ...capUpdates,
                          });
                        } else {
                          // Для квадратной формы
                          const newProfile = newValue;
                          
                          // Проверяем и сбрасываем несовместимые фитинги в левых подключениях
                          const updatedLeftConnections = params.leftConnections.map((conn) => {
                            if (!isFittingCompatibleWithSquareProfile(conn.fittingId, newProfile)) {
                              const fittingInfo = fittingsData[conn.fittingId];
                              const category = fittingInfo?.category || 'Ниппели';
                              const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                return {
                                  ...conn,
                                  fittingId: maxCompatibleFitting.id,
                                  fittingLength: null,
                                };
                              }
                            }
                            return conn;
                          });
                          
                          // Проверяем и сбрасываем несовместимые фитинги в правых подключениях
                          const updatedRightConnections = params.rightConnections.map((conn) => {
                            if (!isFittingCompatibleWithSquareProfile(conn.fittingId, newProfile)) {
                              const fittingInfo = fittingsData[conn.fittingId];
                              const category = fittingInfo?.category || 'Ниппели';
                              const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                return {
                                  ...conn,
                                  fittingId: maxCompatibleFitting.id,
                                  fittingLength: null,
                                };
                              }
                            }
                            return conn;
                          });
                          
                          // Проверяем верхний фитинг
                          let updatedTopCapFitting = params.topCapFitting;
                          if (updatedTopCapFitting && !isEndFittingCompatibleWithSquareProfile(updatedTopCapFitting, newProfile)) {
                            const topCategory = params.topCapFittingCategory || 'Муфты';
                            const topFittingsInCategory = getFittingsByCategory(topCategory, null, 'square', newProfile, true);
                            const topCompatibleFittings = topFittingsInCategory.filter(f => f.isCompatible);
                            
                            if (topCompatibleFittings.length > 0) {
                              const maxCompatibleFitting = topCompatibleFittings[topCompatibleFittings.length - 1];
                              updatedTopCapFitting = maxCompatibleFitting.id;
                            }
                          }
                          
                          // Проверяем нижний фитинг
                          let updatedBottomCapFitting = params.bottomCapFitting;
                          if (updatedBottomCapFitting && !isEndFittingCompatibleWithSquareProfile(updatedBottomCapFitting, newProfile)) {
                            const bottomCategory = params.bottomCapFittingCategory || 'Муфты';
                            const bottomFittingsInCategory = getFittingsByCategory(bottomCategory, null, 'square', newProfile, true);
                            const bottomCompatibleFittings = bottomFittingsInCategory.filter(f => f.isCompatible);
                            
                            if (bottomCompatibleFittings.length > 0) {
                              const maxCompatibleFitting = bottomCompatibleFittings[bottomCompatibleFittings.length - 1];
                              updatedBottomCapFitting = maxCompatibleFitting.id;
                            }
                          }
                          
                          // Проверяем дополнительные выходы
                          const updatedAdditionalOutlets = params.additionalOutlets.map((outlet) => {
                            if (!isFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                              const fittingInfo = fittingsData[outlet.fittingId];
                              const category = fittingInfo?.category || 'Ниппели';
                              const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                return {
                                  ...outlet,
                                  fittingId: maxCompatibleFitting.id,
                                  fittingLength: null,
                                };
                              }
                            }
                            return outlet;
                          });
                          
                          updateParam({
                            mainPipeProfile: newProfile,
                            leftConnections: updatedLeftConnections,
                            rightConnections: updatedRightConnections,
                            additionalOutlets: updatedAdditionalOutlets,
                            topCapFitting: updatedTopCapFitting,
                            topCapFittingLength: updatedTopCapFitting === params.topCapFitting ? params.topCapFittingLength : null,
                            bottomCapFitting: updatedBottomCapFitting,
                            bottomCapFittingLength: updatedBottomCapFitting === params.bottomCapFitting ? params.bottomCapFittingLength : null,
                          });
                        }
                      }}
                    >
                      {params.shape === 'round' 
                        ? availableDiameters.map((d) => (
                            <MenuItem key={d.value} value={d.value}>
                              {d.label}
                            </MenuItem>
                          ))
                        : availableSquareProfiles.map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                              {p.label}
                            </MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Высота"
                    type="number"
                    size="small"
                    value={params.connectionsPlacementMode === 'spacing' ? calculatedSeparatorHeight : params.separatorHeight}
                    onChange={(e) => updateParam('separatorHeight', Number(e.target.value))}
                    fullWidth
                    disabled={params.connectionsPlacementMode === 'spacing'}
                    InputProps={{ 
                      readOnly: params.connectionsPlacementMode === 'spacing',
                      endAdornment: <Typography variant="caption">мм</Typography> 
                    }}
                    inputProps={{ min: 200, max: 3000, step: 10 }}
                    helperText={params.connectionsPlacementMode === 'spacing' ? "Авто" : ""}
                  />
                </Grid>
              </Grid>

              {/* Заглушки */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Заглушки
              </Typography>
              <Grid container spacing={2}>
                {/* Верхняя заглушка */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={params.hasTopCap}
                              onChange={(e) => updateParam('hasTopCap', e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2" fontWeight={600}>Заглушка/переход сверху</Typography>}
                        />
                      </Grid>
                      {params.hasTopCap && (
                        <>
                          {/* Для квадратных форм только прямые заглушки */}
                          {params.shape === 'square' ? (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Прямая заглушка {params.mainPipeProfile?.replace('x', '×')}
                              </Typography>
                            </Grid>
                          ) : (
                            <>
                              <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Элемент</InputLabel>
                                  <Select
                                    value={params.topCapElementType}
                                    label="Элемент"
                                    onChange={(e) => updateParam('topCapElementType', e.target.value)}
                                  >
                                    <MenuItem value="cap">Заглушка</MenuItem>
                                    <MenuItem value="reducer">Переход</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              {params.topCapElementType === 'cap' ? (
                                <Grid item xs={6}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Тип</InputLabel>
                                    <Select
                                      value={params.topCapType}
                                      label="Тип"
                                      onChange={(e) => updateParam('topCapType', e.target.value)}
                                    >
                                      {params.shape === 'round' && (
                                        <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                      )}
                                      {(params.shape === 'square' || params.mainPipeDiameter <= 101.6) && (
                                        <MenuItem value="straight">Плоская</MenuItem>
                                      )}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              ) : (
                                <Grid item xs={6}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Переход</InputLabel>
                                    <Select
                                      value={params.topReducer || ''}
                                      label="Переход"
                                      onChange={(e) => updateParam('topReducer', e.target.value)}
                                    >
                                      {Object.keys(fittingsData)
                                        .filter(key => key.startsWith('Perehod_Konc_') && isAutoConstructorReducerAllowed(key))
                                        .map(key => (
                                          <MenuItem key={key} value={key}>
                                            {fittingsData[key].label || key.replace('Perehod_Konc_', '')}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>
                
                {/* Нижняя заглушка */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={params.hasBottomCap}
                              onChange={(e) => updateParam('hasBottomCap', e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2" fontWeight={600}>Заглушка/переход снизу</Typography>}
                        />
                      </Grid>
                      {params.hasBottomCap && (
                        <>
                          {/* Для квадратных форм только прямые заглушки */}
                          {params.shape === 'square' ? (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Прямая заглушка {params.mainPipeProfile?.replace('x', '×')}
                              </Typography>
                            </Grid>
                          ) : (
                            <>
                              <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Элемент</InputLabel>
                                  <Select
                                    value={params.bottomCapElementType}
                                    label="Элемент"
                                    onChange={(e) => updateParam('bottomCapElementType', e.target.value)}
                                  >
                                    <MenuItem value="cap">Заглушка</MenuItem>
                                    <MenuItem value="reducer">Переход</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              {params.bottomCapElementType === 'cap' ? (
                                <Grid item xs={6}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Тип</InputLabel>
                                    <Select
                                      value={params.bottomCapType}
                                      label="Тип"
                                      onChange={(e) => updateParam('bottomCapType', e.target.value)}
                                    >
                                      {params.shape === 'round' && (
                                        <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                      )}
                                      {(params.shape === 'square' || params.mainPipeDiameter <= 101.6) && (
                                        <MenuItem value="straight">Плоская</MenuItem>
                                      )}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              ) : (
                                <Grid item xs={6}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Переход</InputLabel>
                                    <Select
                                      value={params.bottomReducer || ''}
                                      label="Переход"
                                      onChange={(e) => updateParam('bottomReducer', e.target.value)}
                                    >
                                      {Object.keys(fittingsData)
                                        .filter(key => key.startsWith('Perehod_Konc_') && isAutoConstructorReducerAllowed(key))
                                        .map(key => (
                                          <MenuItem key={key} value={key}>
                                            {fittingsData[key].label || key.replace('Perehod_Konc_', '')}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              {/* Торцевые подключения */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Торцевые подключения
              </Typography>
              <Grid container spacing={2}>
                {/* Торцевое подключение сверху */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={params.hasTopConnection}
                              onChange={(e) => updateParam('hasTopConnection', e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2" fontWeight={600}>Подключение сверху</Typography>}
                        />
                      </Grid>
                      {params.hasTopConnection && (
                        <>
                          <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel shrink>Категория</InputLabel>
                              <Select
                                value={params.topCapFittingCategory || ''}
                                label="Категория"
                                displayEmpty
                                notched
                                onChange={(e) => {
                                  const newCategory = e.target.value;
                                  updateParam({
                                    topCapFittingCategory: newCategory,
                                    topCapFitting: '',
                                    topCapFittingLength: null
                                  });
                                }}
                              >
                                <MenuItem value="">
                                  <em>Выбрать</em>
                                </MenuItem>
                                {connectionFittingCategories.map((cat) => (
                                  <MenuItem key={cat.id} value={cat.id}>
                                    {cat.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {params.topCapFittingCategory && (
                            <>
                              <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel shrink>Фитинг</InputLabel>
                                  <Select
                                    value={params.topCapFitting || ''}
                                    label="Фитинг"
                                    displayEmpty
                                    notched
                                    onChange={(e) => {
                                      const newFitting = e.target.value;
                                      if (newFitting && !isEndFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter)) {
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                        return;
                                      }
                                      
                                      const updates = { topCapFitting: newFitting, topCapFittingLength: null };
                                      
                                      // Автоматическое управление заглушками/переходами
                                      // Для квадратных профилей не используем эллиптические заглушки и переходы
                                      if (newFitting && params.shape !== 'square') {
                                        const isFlange = newFitting.startsWith('Flanec_');
                                        const isNippel = newFitting.startsWith('Nippel_');
                                        const isMufta = newFitting.startsWith('Mufta_');
                                        const isNakGaika = newFitting.startsWith('Nak_Gaika_');
                                        const isShaibaNarRezba = newFitting.startsWith('Shaib_Nar_Rezba_');
                                        
                                        const mainDiameter = params.mainPipeDiameter;
                                        
                                        // Для фланцев: проверяем нужен ли переход
                                        if (isFlange) {
                                          const dn = extractDnFromFlangeKey(newFitting);
                                          const flangePipeDiameter = getPipeDiameterForDn(dn);
                                          
                                          if (flangePipeDiameter && flangePipeDiameter !== mainDiameter && mainDiameter > flangePipeDiameter) {
                                            // Гребёнка больше — ищем переход
                                            const reducer = findConcentricReducer(mainDiameter, flangePipeDiameter);
                                            if (reducer) {
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'reducer';
                                              updates.topReducer = reducer.key;
                                            } else {
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'cap';
                                              updates.topCapType = 'elliptical';
                                            }
                                          } else if (flangePipeDiameter && flangePipeDiameter === mainDiameter) {
                                            // Диаметры совпадают — закрываем
                                            updates.hasTopCap = false;
                                          }
                                        }
                                        
                                        // Для ниппелей/муфт/гаек/шайб
                                        if (isNippel || isMufta || isNakGaika || isShaibaNarRezba) {
                                          let fittingDiameter = fittingsData[newFitting]?.diameter;
                                          if (isNakGaika) {
                                            const size = extractSizeFromNakGaikaKey(newFitting);
                                            fittingDiameter = getPipeDiameterForNakGaika(size);
                                          } else if (isShaibaNarRezba) {
                                            const size = extractSizeFromShaibaNarRezba(newFitting);
                                            fittingDiameter = getPipeDiameterForShaibaNarRezba(size);
                                          }

                                          // Для муфт: используем getCapRequirementForFitting
                                          if (isMufta) {
                                            const capReq = getCapRequirementForFitting(newFitting, mainDiameter);
                                            
                                            if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                              // Нужен переход
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'reducer';
                                              updates.topReducer = capReq.reducer.key;
                                            } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                              // Нужна эллиптическая заглушка
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'cap';
                                              updates.topCapType = 'elliptical';
                                            } else if (capReq.fitsFlush) {
                                              // Впритык - закрываем заглушки
                                              updates.hasTopCap = false;
                                            }
                                          }
                                          // Для ниппелей: проверяем нужен ли переход
                                          else if (isNippel) {
                                            // Используем getCapRequirementForFitting для определения нужен ли переход
                                            const capReq = getCapRequirementForFitting(newFitting, mainDiameter);
                                            
                                            if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                              // Нужен переход
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'reducer';
                                              updates.topReducer = capReq.reducer.key;
                                            } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                              // Нужна эллиптическая заглушка
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'cap';
                                              updates.topCapType = 'elliptical';
                                            } else if (capReq.fitsFlush) {
                                              // Впритык - закрываем заглушки
                                              updates.hasTopCap = false;
                                            }
                                          }
                                          // Для накидных гаек и шайб с наружной резьбой: всегда эллиптические заглушки если диаметры не совпадают
                                          else if (isNakGaika || isShaibaNarRezba) {
                                            if (fittingDiameter && fittingDiameter !== mainDiameter) {
                                              // Диаметры НЕ совпадают — эллиптическая заглушка
                                              updates.hasTopCap = true;
                                              updates.topCapElementType = 'cap';
                                              updates.topCapType = 'elliptical';
                                            } else {
                                              // Диаметры совпадают — закрываем
                                              updates.hasTopCap = false;
                                            }
                                          }
                                        }
                                      }
                                      
                                      updateParam(updates);
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Выбрать</em>
                                    </MenuItem>
                                    {getFittingsByCategory(params.topCapFittingCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile, true).map((f) => {
                                      const isCompatible = f.isCompatible;
                                      return (
                                        <MenuItem 
                                          key={f.id} 
                                          value={f.id}
                                          onClick={(e) => {
                                            if (!isCompatible) {
                                              e.stopPropagation();
                                              setIncompatibilityDialog({
                                                open: true,
                                                message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                              });
                                            }
                                          }}
                                          sx={{
                                            textDecoration: !isCompatible ? 'line-through' : 'none',
                                            color: !isCompatible ? 'text.disabled' : 'inherit',
                                            cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                            '&:hover': {
                                              backgroundColor: !isCompatible ? 'transparent' : undefined,
                                            },
                                          }}
                                        >
                                          {f.label}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              </Grid>
                              {params.topCapFitting && (() => {
                                const capReq = getCapRequirementForFitting(params.topCapFitting, params.mainPipeDiameter);
                                // Показываем поле "Вылет фитинга" только если нужна заглушка (не впритык)
                                if (capReq.needsCap) {
                                  return (
                                    <Grid item xs={12}>
                                      <TextField
                                        label="Вылет фитинга"
                                        type="number"
                                        size="small"
                                        value={params.topCapFittingLength === 0 ? '' : (params.topCapFittingLength ?? '')}
                                        onChange={(e) => updateParam('topCapFittingLength', e.target.value === '' ? null : Number(e.target.value))}
                                        placeholder="Авто"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                      />
                                    </Grid>
                                  );
                                }
                                return null;
                              })()}
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>
                
                {/* Торцевое подключение снизу */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={params.hasBottomConnection}
                              onChange={(e) => updateParam('hasBottomConnection', e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2" fontWeight={600}>Подключение снизу</Typography>}
                        />
                      </Grid>
                      {params.hasBottomConnection && (
                        <>
                          <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel shrink>Категория</InputLabel>
                              <Select
                                value={params.bottomCapFittingCategory || ''}
                                label="Категория"
                                displayEmpty
                                notched
                                onChange={(e) => {
                                  const newCategory = e.target.value;
                                  updateParam({
                                    bottomCapFittingCategory: newCategory,
                                    bottomCapFitting: '',
                                    bottomCapFittingLength: null
                                  });
                                }}
                              >
                                <MenuItem value="">
                                  <em>Выбрать</em>
                                </MenuItem>
                                {connectionFittingCategories.map((cat) => (
                                  <MenuItem key={cat.id} value={cat.id}>
                                    {cat.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {params.bottomCapFittingCategory && (
                            <>
                              <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel shrink>Фитинг</InputLabel>
                                  <Select
                                    value={params.bottomCapFitting || ''}
                                    label="Фитинг"
                                    displayEmpty
                                    notched
                                    onChange={(e) => {
                                      const newFitting = e.target.value;
                                      if (newFitting && !isEndFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter)) {
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                        return;
                                      }
                                      
                                      const updates = { bottomCapFitting: newFitting, bottomCapFittingLength: null };
                                      
                                      // Автоматическое управление заглушками/переходами
                                      // Для квадратных профилей не используем эллиптические заглушки и переходы
                                      if (newFitting && params.shape !== 'square') {
                                        const isFlange = newFitting.startsWith('Flanec_');
                                        const isNippel = newFitting.startsWith('Nippel_');
                                        const isMufta = newFitting.startsWith('Mufta_');
                                        const isNakGaika = newFitting.startsWith('Nak_Gaika_');
                                        const isShaibaNarRezba = newFitting.startsWith('Shaib_Nar_Rezba_');
                                        
                                        const mainDiameter = params.mainPipeDiameter;
                                        
                                        // Для фланцев: проверяем нужен ли переход
                                        if (isFlange) {
                                          const dn = extractDnFromFlangeKey(newFitting);
                                          const flangePipeDiameter = getPipeDiameterForDn(dn);
                                          
                                          if (flangePipeDiameter && flangePipeDiameter !== mainDiameter && mainDiameter > flangePipeDiameter) {
                                            // Гребёнка больше — ищем переход
                                            const reducer = findConcentricReducer(mainDiameter, flangePipeDiameter);
                                            if (reducer) {
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'reducer';
                                              updates.bottomReducer = reducer.key;
                                            } else {
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'cap';
                                              updates.bottomCapType = 'elliptical';
                                            }
                                          } else if (flangePipeDiameter && flangePipeDiameter === mainDiameter) {
                                            // Диаметры совпадают — закрываем
                                            updates.hasBottomCap = false;
                                          }
                                        }
                                        
                                        // Для ниппелей/муфт/гаек/шайб
                                        if (isNippel || isMufta || isNakGaika || isShaibaNarRezba) {
                                          let fittingDiameter = fittingsData[newFitting]?.diameter;
                                          if (isNakGaika) {
                                            const size = extractSizeFromNakGaikaKey(newFitting);
                                            fittingDiameter = getPipeDiameterForNakGaika(size);
                                          } else if (isShaibaNarRezba) {
                                            const size = extractSizeFromShaibaNarRezba(newFitting);
                                            fittingDiameter = getPipeDiameterForShaibaNarRezba(size);
                                          }

                                          // Для муфт: используем getCapRequirementForFitting
                                          if (isMufta) {
                                            const capReq = getCapRequirementForFitting(newFitting, mainDiameter);
                                            
                                            if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                              // Нужен переход
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'reducer';
                                              updates.bottomReducer = capReq.reducer.key;
                                            } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                              // Нужна эллиптическая заглушка
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'cap';
                                              updates.bottomCapType = 'elliptical';
                                            } else if (capReq.fitsFlush) {
                                              // Впритык - закрываем заглушки
                                              updates.hasBottomCap = false;
                                            }
                                          }
                                          // Для ниппелей: используем getCapRequirementForFitting
                                          else if (isNippel) {
                                            const capReq = getCapRequirementForFitting(newFitting, mainDiameter);
                                            
                                            if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                              // Нужен переход
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'reducer';
                                              updates.bottomReducer = capReq.reducer.key;
                                            } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                              // Нужна эллиптическая заглушка
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'cap';
                                              updates.bottomCapType = 'elliptical';
                                            } else if (capReq.fitsFlush) {
                                              // Впритык - закрываем заглушки
                                              updates.hasBottomCap = false;
                                            }
                                          }
                                          // Для накидных гаек и шайб с наружной резьбой: всегда эллиптические заглушки если диаметры не совпадают
                                          else if (isNakGaika || isShaibaNarRezba) {
                                            if (fittingDiameter && fittingDiameter !== mainDiameter) {
                                              // Диаметры НЕ совпадают — эллиптическая заглушка
                                              updates.hasBottomCap = true;
                                              updates.bottomCapElementType = 'cap';
                                              updates.bottomCapType = 'elliptical';
                                            } else {
                                              // Диаметры совпадают — закрываем
                                              updates.hasBottomCap = false;
                                            }
                                          }
                                        }
                                      }
                                      
                                      updateParam(updates);
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Выбрать</em>
                                    </MenuItem>
                                    {getFittingsByCategory(params.bottomCapFittingCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile, true).map((f) => {
                                      const isCompatible = f.isCompatible;
                                      return (
                                        <MenuItem 
                                          key={f.id} 
                                          value={f.id}
                                          onClick={(e) => {
                                            if (!isCompatible) {
                                              e.stopPropagation();
                                              setIncompatibilityDialog({
                                                open: true,
                                                message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                              });
                                            }
                                          }}
                                          sx={{
                                            textDecoration: !isCompatible ? 'line-through' : 'none',
                                            color: !isCompatible ? 'text.disabled' : 'inherit',
                                            cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                            '&:hover': {
                                              backgroundColor: !isCompatible ? 'transparent' : undefined,
                                            },
                                          }}
                                        >
                                          {f.label}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              </Grid>
                              {params.bottomCapFitting && (() => {
                                const capReq = getCapRequirementForFitting(params.bottomCapFitting, params.mainPipeDiameter);
                                // Показываем поле "Вылет фитинга" только если нужна заглушка (не впритык)
                                if (capReq.needsCap) {
                                  return (
                                    <Grid item xs={12}>
                                      <TextField
                                        label="Вылет фитинга"
                                        type="number"
                                        size="small"
                                        value={params.bottomCapFittingLength === 0 ? '' : (params.bottomCapFittingLength ?? '')}
                                        onChange={(e) => updateParam('bottomCapFittingLength', e.target.value === '' ? null : Number(e.target.value))}
                                        placeholder="Авто"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                      />
                                    </Grid>
                                  );
                                }
                                return null;
                              })()}
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              {/* Общий переключатель режима размещения для левых и правых подключений */}
              <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Режим размещения боковых подключений:
                    </Typography>
                    <ToggleButtonGroup
                      value={params.connectionsPlacementMode}
                      exclusive
                      onChange={(e, newMode) => {
                        if (newMode) {
                          updateParam('connectionsPlacementMode', newMode);
                          // При переключении на режим spacing автоматически включаем автоматическую высоту
                          // (высота будет рассчитываться автоматически на основе подключений)
                        }
                      }}
                      size="small"
                    >
                      <ToggleButton value="spacing">
                        Межосевое (авто высота)
                      </ToggleButton>
                      <ToggleButton value="offsets">
                        Отступы
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Левые подключения */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Левые подключения
              </Typography>
              
              {/* Поля для режима межосевого */}
              {params.connectionsPlacementMode === 'spacing' && (
                <>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={params.leftConnectionsFirstOffsetMode === 'fromRightConnection'}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                // Включаем привязку - взаимная эксклюзивность
                                updateParam({
                                  leftConnectionsFirstOffsetMode: 'fromRightConnection',
                                  leftConnectionsFirstOffsetSaved: params.leftConnectionsFirstOffset,
                                  leftConnectionsFirstOffset: params.leftConnectionsFirstOffsetLastUsed || 0,
                                  // Отключаем привязку правых к левым
                                  rightConnectionsFirstOffsetMode: 'fromEdge',
                                  rightConnectionsFirstOffset: params.rightConnectionsFirstOffsetSaved !== null 
                                    ? params.rightConnectionsFirstOffsetSaved 
                                    : params.rightConnectionsFirstOffset,
                                  rightConnectionsFirstOffsetSaved: null
                                });
                              } else {
                                // Отключаем привязку
                                updateParam({
                                  leftConnectionsFirstOffsetMode: 'fromEdge',
                                  leftConnectionsFirstOffsetLastUsed: params.leftConnectionsFirstOffset,
                                  leftConnectionsFirstOffset: params.leftConnectionsFirstOffsetSaved !== null 
                                    ? params.leftConnectionsFirstOffsetSaved 
                                    : params.leftConnectionsFirstOffset,
                                  leftConnectionsFirstOffsetSaved: null
                                });
                              }
                            }}
                            disabled={params.rightConnections.length === 0}
                            size="small"
                          />
                        }
                        label="Привязать к правому подключению"
                      />
                    </Grid>
                  </Grid>
                  
                  {params.leftConnectionsFirstOffsetMode === 'fromRightConnection' && (
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>От подключения</InputLabel>
                          <Select
                            value={params.leftConnectionsFirstOffsetReferenceIndex}
                            onChange={(e) => updateParam('leftConnectionsFirstOffsetReferenceIndex', e.target.value)}
                            label="От подключения"
                          >
                            {params.rightConnections.map((conn, index) => (
                              <MenuItem key={index} value={index}>
                                {index + 1}-е правое
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Отступ от подключения"
                          type="number"
                          size="small"
                          value={params.leftConnectionsFirstOffset}
                          onChange={(e) => updateParam('leftConnectionsFirstOffset', Number(e.target.value))}
                          fullWidth
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                    </Grid>
                  )}
                  
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ первого"
                        type="number"
                        size="small"
                        value={params.leftConnectionsFirstOffsetMode === 'fromRightConnection' ? effectiveLeftFirstOffset : params.leftConnectionsFirstOffset}
                        onChange={(e) => updateParam('leftConnectionsFirstOffset', Number(e.target.value))}
                        fullWidth
                        disabled={params.leftConnectionsFirstOffsetMode === 'fromRightConnection'}
                        InputProps={{ 
                          readOnly: params.leftConnectionsFirstOffsetMode === 'fromRightConnection',
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ последнего"
                        type="number"
                        size="small"
                        value={params.leftConnectionsLastOffset}
                        onChange={(e) => updateParam('leftConnectionsLastOffset', Number(e.target.value))}
                        fullWidth
                        disabled={!shouldUseLeftOffset}
                        InputProps={{ 
                          readOnly: !shouldUseLeftOffset,
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                        helperText={!shouldUseLeftOffset ? "Использует правые" : ""}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Между подключениями"
                        type="number"
                        size="small"
                        value={params.leftConnectionsSpacing}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          updateParam('leftConnectionsSpacing', value);
                          // Применяем ко всем подключениям
                          const updatedConnections = params.leftConnections.map(conn => ({
                            ...conn,
                            spacingToNext: value,
                          }));
                          updateParam('leftConnections', updatedConnections);
                        }}
                        fullWidth
                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        helperText="Ко всем"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Количество:</Typography>
                    <IconButton
                      size="small"
                      onClick={() => deleteLeftConnection(params.leftConnections.length - 1)}
                      disabled={params.leftConnections.length <= 1}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                      {params.leftConnections.length}
                    </Typography>
                    <IconButton size="small" onClick={addLeftConnection}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
              
              <TableContainer sx={{ maxHeight: '12.5rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 20 }}></TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>
                        {params.connectionsPlacementMode === 'spacing' ? 'До след.' : 'Отступ'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {params.leftConnections.map((conn, index) => {
                      const fittingInfo = fittingsData[conn.fittingId];
                      const category = fittingInfo?.category || 'Ниппели';
                      const fittingsInCategory = getFittingsByCategory(
                        category,
                        params.mainPipeDiameter,
                        params.shape,
                        params.mainPipeProfile
                      );

                      return (
                        <TableRow 
                          key={conn.id}
                          draggable
                          onDragStart={() => setDraggedLeftIndex(index)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (draggedLeftIndex !== null && draggedLeftIndex !== index) {
                              moveLeftConnection(draggedLeftIndex, index);
                            }
                            setDraggedLeftIndex(null);
                          }}
                          onDragEnd={() => setDraggedLeftIndex(null)}
                          sx={{
                            cursor: 'move',
                            opacity: draggedLeftIndex === index ? 0.5 : 1,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <TableCell sx={{ p: 0.5 }}>
                            <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'grab' }} />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <Select
                              size="small"
                              value={category}
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                const newFittings = getFittingsByCategory(newCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile)
                                  .filter(f => f.isCompatible);
                                if (newFittings.length > 0) {
                                  updateLeftConnection(index, 'fittingId', newFittings[0].id);
                                }
                              }}
                              sx={{ minWidth: 90, fontSize: '0.75rem' }}
                            >
                              {connectionFittingCategories.map(cat => (
                                <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>{cat.label}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <Select
                              size="small"
                              value={conn.fittingId}
                              onChange={(e) => {
                                const newFitting = e.target.value;
                                const isCompatible = params.shape === 'square'
                                  ? isFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                  : isFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                if (!isCompatible) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                  });
                                  return;
                                }
                                updateLeftConnection(index, 'fittingId', newFitting);
                                updateLeftConnection(index, 'fittingLength', null);
                              }}
                              sx={{ minWidth: 90, fontSize: '0.75rem' }}
                            >
                              {fittingsInCategory.map(f => (
                                <MenuItem 
                                  key={f.id} 
                                  value={f.id} 
                                  onClick={(e) => {
                                    if (!f.isCompatible) {
                                      e.stopPropagation();
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                    }
                                  }}
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    textDecoration: !f.isCompatible ? 'line-through' : 'none',
                                    color: !f.isCompatible ? 'text.disabled' : 'inherit',
                                    cursor: !f.isCompatible ? 'not-allowed' : 'pointer',
                                    '&:hover': {
                                      backgroundColor: !f.isCompatible ? 'transparent' : undefined,
                                    },
                                  }}
                                >
                                  {f.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={conn.fittingLength === 0 ? '' : (conn.fittingLength ?? '')}
                              onChange={(e) => updateLeftConnection(index, 'fittingLength', e.target.value === '' ? null : Number(e.target.value))}
                              placeholder="Авто"
                              sx={{ width: 65 }}
                              InputProps={{ sx: { fontSize: '0.8rem' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {params.connectionsPlacementMode === 'spacing' ? (
                              // Режим межосевого: показываем "До след." или прочерк для последнего
                              index === params.leftConnections.length - 1 ? (
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                  —
                                </Typography>
                              ) : (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={conn.spacingToNext ?? params.leftConnectionsSpacing}
                                  onChange={(e) => updateLeftConnection(index, 'spacingToNext', e.target.value === '' ? null : Number(e.target.value))}
                                  placeholder={conn.spacingToNext === null ? 'Общее' : ''}
                                  sx={{ width: 65 }}
                                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                                />
                              )
                            ) : (
                              // Режим отступов: показываем "Отступ" с кнопкой направления
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={conn.offsetFromTop}
                                  onChange={(e) => updateLeftConnection(index, 'offsetFromTop', Number(e.target.value))}
                                  sx={{ width: 65 }}
                                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newDirection = (conn.offsetDirection || 'top') === 'top' ? 'bottom' : 'top';
                                    updateLeftConnection(index, 'offsetDirection', newDirection);
                                  }}
                                  title={`Отсчет: ${(conn.offsetDirection || 'top') === 'top' ? 'сверху' : 'снизу'}`}
                                  sx={{ p: 0.5 }}
                                >
                                  {(conn.offsetDirection || 'top') === 'top' ? 
                                    <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : 
                                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                  }
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {params.leftConnections.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => deleteLeftConnection(index)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Правые подключения */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Правые подключения
              </Typography>
              
              {/* Поля для режима межосевого */}
              {params.connectionsPlacementMode === 'spacing' && (
                <>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={params.rightConnectionsFirstOffsetMode === 'fromLeftConnection'}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                // Включаем привязку - взаимная эксклюзивность
                                updateParam({
                                  rightConnectionsFirstOffsetMode: 'fromLeftConnection',
                                  rightConnectionsFirstOffsetSaved: params.rightConnectionsFirstOffset,
                                  rightConnectionsFirstOffset: params.rightConnectionsFirstOffsetLastUsed || 0,
                                  // Отключаем привязку левых к правым
                                  leftConnectionsFirstOffsetMode: 'fromEdge',
                                  leftConnectionsFirstOffset: params.leftConnectionsFirstOffsetSaved !== null 
                                    ? params.leftConnectionsFirstOffsetSaved 
                                    : params.leftConnectionsFirstOffset,
                                  leftConnectionsFirstOffsetSaved: null
                                });
                              } else {
                                // Отключаем привязку
                                updateParam({
                                  rightConnectionsFirstOffsetMode: 'fromEdge',
                                  rightConnectionsFirstOffsetLastUsed: params.rightConnectionsFirstOffset,
                                  rightConnectionsFirstOffset: params.rightConnectionsFirstOffsetSaved !== null 
                                    ? params.rightConnectionsFirstOffsetSaved 
                                    : params.rightConnectionsFirstOffset,
                                  rightConnectionsFirstOffsetSaved: null
                                });
                              }
                            }}
                            disabled={params.leftConnections.length === 0}
                            size="small"
                          />
                        }
                        label="Привязать к левому подключению"
                      />
                    </Grid>
                  </Grid>
                  
                  {params.rightConnectionsFirstOffsetMode === 'fromLeftConnection' && (
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>От подключения</InputLabel>
                          <Select
                            value={params.rightConnectionsFirstOffsetReferenceIndex}
                            onChange={(e) => updateParam('rightConnectionsFirstOffsetReferenceIndex', e.target.value)}
                            label="От подключения"
                          >
                            {params.leftConnections.map((conn, index) => (
                              <MenuItem key={index} value={index}>
                                {index + 1}-е левое
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Отступ от подключения"
                          type="number"
                          size="small"
                          value={params.rightConnectionsFirstOffset}
                          onChange={(e) => updateParam('rightConnectionsFirstOffset', Number(e.target.value))}
                          fullWidth
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                    </Grid>
                  )}
                  
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ первого"
                        type="number"
                        size="small"
                        value={params.rightConnectionsFirstOffsetMode === 'fromLeftConnection' ? effectiveRightFirstOffset : params.rightConnectionsFirstOffset}
                        onChange={(e) => updateParam('rightConnectionsFirstOffset', Number(e.target.value))}
                        fullWidth
                        disabled={params.rightConnectionsFirstOffsetMode === 'fromLeftConnection'}
                        InputProps={{ 
                          readOnly: params.rightConnectionsFirstOffsetMode === 'fromLeftConnection',
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ последнего"
                        type="number"
                        size="small"
                        value={params.rightConnectionsLastOffset}
                        onChange={(e) => updateParam('rightConnectionsLastOffset', Number(e.target.value))}
                        fullWidth
                        disabled={shouldUseLeftOffset}
                        InputProps={{ 
                          readOnly: shouldUseLeftOffset,
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                        helperText={shouldUseLeftOffset ? "Использует левые" : ""}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Между подключениями"
                        type="number"
                        size="small"
                        value={params.rightConnectionsSpacing}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          updateParam('rightConnectionsSpacing', value);
                          // Применяем ко всем подключениям
                          const updatedConnections = params.rightConnections.map(conn => ({
                            ...conn,
                            spacingToNext: value,
                          }));
                          updateParam('rightConnections', updatedConnections);
                        }}
                        fullWidth
                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        helperText="Ко всем"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Количество:</Typography>
                    <IconButton
                      size="small"
                      onClick={() => deleteRightConnection(params.rightConnections.length - 1)}
                      disabled={params.rightConnections.length <= 1}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                      {params.rightConnections.length}
                    </Typography>
                    <IconButton size="small" onClick={addRightConnection}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
              
              <TableContainer sx={{ maxHeight: '12.5rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 20 }}></TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>
                        {params.connectionsPlacementMode === 'spacing' ? 'До след.' : 'Отступ'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {params.rightConnections.map((conn, index) => {
                      const fittingInfo = fittingsData[conn.fittingId];
                      const category = fittingInfo?.category || 'Ниппели';
                      const fittingsInCategory = getFittingsByCategory(
                        category,
                        params.mainPipeDiameter,
                        params.shape,
                        params.mainPipeProfile
                      );

                      return (
                        <TableRow 
                          key={conn.id}
                          draggable
                          onDragStart={() => setDraggedRightIndex(index)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (draggedRightIndex !== null && draggedRightIndex !== index) {
                              moveRightConnection(draggedRightIndex, index);
                            }
                            setDraggedRightIndex(null);
                          }}
                          onDragEnd={() => setDraggedRightIndex(null)}
                          sx={{
                            cursor: 'move',
                            opacity: draggedRightIndex === index ? 0.5 : 1,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <TableCell sx={{ p: 0.5 }}>
                            <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'grab' }} />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <Select
                              size="small"
                              value={category}
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                const newFittings = getFittingsByCategory(newCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile)
                                  .filter(f => f.isCompatible);
                                if (newFittings.length > 0) {
                                  updateRightConnection(index, 'fittingId', newFittings[0].id);
                                }
                              }}
                              sx={{ minWidth: 90, fontSize: '0.75rem' }}
                            >
                              {connectionFittingCategories.map(cat => (
                                <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>{cat.label}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <Select
                              size="small"
                              value={conn.fittingId}
                              onChange={(e) => {
                                const newFitting = e.target.value;
                                const isCompatible = params.shape === 'square'
                                  ? isFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                  : isFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                if (!isCompatible) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                  });
                                  return;
                                }
                                updateRightConnection(index, 'fittingId', newFitting);
                                updateRightConnection(index, 'fittingLength', null);
                              }}
                              sx={{ minWidth: 90, fontSize: '0.75rem' }}
                            >
                              {fittingsInCategory.map(f => (
                                <MenuItem 
                                  key={f.id} 
                                  value={f.id}
                                  onClick={(e) => {
                                    if (!f.isCompatible) {
                                      e.stopPropagation();
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                    }
                                  }}
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    textDecoration: !f.isCompatible ? 'line-through' : 'none',
                                    color: !f.isCompatible ? 'text.disabled' : 'inherit',
                                    cursor: !f.isCompatible ? 'not-allowed' : 'pointer',
                                    '&:hover': {
                                      backgroundColor: !f.isCompatible ? 'transparent' : undefined,
                                    },
                                  }}
                                >
                                  {f.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={conn.fittingLength === 0 ? '' : (conn.fittingLength ?? '')}
                              onChange={(e) => updateRightConnection(index, 'fittingLength', e.target.value === '' ? null : Number(e.target.value))}
                              placeholder="Авто"
                              sx={{ width: 65 }}
                              InputProps={{ sx: { fontSize: '0.8rem' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {params.connectionsPlacementMode === 'spacing' ? (
                              // Режим межосевого: показываем "До след." или прочерк для последнего
                              index === params.rightConnections.length - 1 ? (
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                  —
                                </Typography>
                              ) : (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={conn.spacingToNext ?? params.rightConnectionsSpacing}
                                  onChange={(e) => updateRightConnection(index, 'spacingToNext', e.target.value === '' ? null : Number(e.target.value))}
                                  placeholder={conn.spacingToNext === null ? 'Общее' : ''}
                                  sx={{ width: 65 }}
                                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                                />
                              )
                            ) : (
                              // Режим отступов: показываем "Отступ" с кнопкой направления
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={conn.offsetFromTop}
                                  onChange={(e) => updateRightConnection(index, 'offsetFromTop', Number(e.target.value))}
                                  sx={{ width: 65 }}
                                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newDirection = (conn.offsetDirection || 'top') === 'top' ? 'bottom' : 'top';
                                    updateRightConnection(index, 'offsetDirection', newDirection);
                                  }}
                                  title={`Отсчет: ${(conn.offsetDirection || 'top') === 'top' ? 'сверху' : 'снизу'}`}
                                  sx={{ p: 0.5 }}
                                >
                                  {(conn.offsetDirection || 'top') === 'top' ? 
                                    <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : 
                                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                  }
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {params.rightConnections.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => deleteRightConnection(index)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Дополнительные выходы */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Дополнительные выходы
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Количество:</Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeAdditionalOutlet(params.additionalOutlets.length - 1)}
                      disabled={params.additionalOutlets.length === 0}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                      {params.additionalOutlets.length}
                    </Typography>
                    <IconButton size="small" onClick={addAdditionalOutlet}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
              
              {params.additionalOutlets.length > 0 && (
                <TableContainer sx={{ maxHeight: '12.5rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, p: 0.5, width: 20 }}></TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Размещение</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Сторона</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Значение</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {params.additionalOutlets.map((outlet, index) => {
                        const category = outlet.fittingCategory || 'Ниппели';
                        const fittingsInCategory = getFittingsByCategory(
                          category,
                          params.mainPipeDiameter,
                          params.shape,
                          params.mainPipeProfile
                        );
                        
                        const allowedCategories = connectionFittingCategories;

                        return (
                          <TableRow 
                            key={outlet.id}
                            sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                          >
                            <TableCell sx={{ p: 0.5 }}>
                              <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                            
                            {/* Категория */}
                            <TableCell sx={{ p: 0.5 }}>
                              <Select
                                size="small"
                                value={category}
                                onChange={(e) => {
                                  const newCategory = e.target.value;
                                  const newFittings = getFittingsByCategory(newCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile);
                                  const compatibleFittings = newFittings.filter(f => f.isCompatible);
                                  if (compatibleFittings.length > 0) {
                                    updateAdditionalOutlet(index, 'fittingCategory', newCategory);
                                    updateAdditionalOutlet(index, 'fittingId', compatibleFittings[0].id);
                                  } else if (newFittings.length > 0) {
                                    // Если нет совместимых, берем первый несовместимый
                                    updateAdditionalOutlet(index, 'fittingCategory', newCategory);
                                    updateAdditionalOutlet(index, 'fittingId', newFittings[0].id);
                                  }
                                }}
                                sx={{ minWidth: 90, fontSize: '0.75rem' }}
                              >
                                {allowedCategories.map(cat => (
                                  <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>{cat.label}</MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            
                            {/* Фитинг */}
                            <TableCell sx={{ p: 0.5 }}>
                              <Select
                                size="small"
                                value={outlet.fittingId}
                                onChange={(e) => {
                                  const newFitting = e.target.value;
                                  const isCompatible = params.shape === 'square'
                                    ? isFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                    : isFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                  if (!isCompatible) {
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                    return;
                                  }
                                  updateAdditionalOutlet(index, 'fittingId', newFitting);
                                }}
                                sx={{ minWidth: 100, fontSize: '0.75rem' }}
                              >
                                {fittingsInCategory.map(fitting => (
                                  <MenuItem 
                                    key={fitting.id} 
                                    value={fitting.id}
                                    onClick={(e) => {
                                      if (!fitting.isCompatible) {
                                        e.stopPropagation();
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      fontSize: '0.8rem',
                                      textDecoration: !fitting.isCompatible ? 'line-through' : 'none',
                                      color: !fitting.isCompatible ? 'text.disabled' : 'inherit',
                                      cursor: !fitting.isCompatible ? 'not-allowed' : 'pointer',
                                      '&:hover': {
                                        backgroundColor: !fitting.isCompatible ? 'transparent' : undefined,
                                      },
                                    }}
                                  >
                                    {fitting.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            
                            {/* Размещение */}
                            <TableCell sx={{ p: 0.5 }}>
                              <Select
                                size="small"
                                value={outlet.positionType || 'center'}
                                onChange={(e) => updateAdditionalOutlet(index, 'positionType', e.target.value)}
                                sx={{ minWidth: 110, fontSize: '0.75rem' }}
                              >
                                <MenuItem value="center" sx={{ fontSize: '0.8rem' }}>По середине</MenuItem>
                                <MenuItem value="distance" sx={{ fontSize: '0.8rem' }}>На расстоянии</MenuItem>
                              </Select>
                            </TableCell>
                            
                            {/* Сторона */}
                            <TableCell sx={{ p: 0.5 }}>
                              <Select
                                size="small"
                                value={outlet.side || 'both'}
                                onChange={(e) => updateAdditionalOutlet(index, 'side', e.target.value)}
                                sx={{ minWidth: 110, fontSize: '0.75rem' }}
                              >
                                <MenuItem value="both" sx={{ fontSize: '0.8rem' }}>С 2-х сторон</MenuItem>
                                <MenuItem value="front" sx={{ fontSize: '0.8rem' }}>Спереди</MenuItem>
                                <MenuItem value="back" sx={{ fontSize: '0.8rem' }}>Сзади</MenuItem>
                                <MenuItem value="left" sx={{ fontSize: '0.8rem' }}>Слева</MenuItem>
                                <MenuItem value="right" sx={{ fontSize: '0.8rem' }}>Справа</MenuItem>
                              </Select>
                            </TableCell>
                            
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={(outlet.flangePipeLength ?? outlet.fittingLength) ?? ''}
                                onChange={(e) => {
                                  const next = e.target.value === '' ? null : Number(e.target.value);
                                  updateAdditionalOutlet(index, 'fittingLength', next);
                                  updateAdditionalOutlet(index, 'flangePipeLength', next);
                                }}
                                sx={{ width: 65 }}
                                placeholder="Авто"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                title="Вылет от наружной стенки"
                              />
                            </TableCell>
                            
                            {/* Значение (динамическое поле) */}
                            <TableCell sx={{ p: 0.5 }}>
                              {outlet.positionType === 'center' ? (
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                  —
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={outlet.distanceFromEdge === 0 ? '' : (outlet.distanceFromEdge ?? 50)}
                                    onChange={(e) => updateAdditionalOutlet(index, 'distanceFromEdge', parseFloat(e.target.value) || 0)}
                                    sx={{ width: 80 }}
                                    InputProps={{ 
                                      endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                      sx: { fontSize: '0.8rem' }
                                    }}
                                    title="Расстояние от края гидроразделителя"
                                  />
                                  <Tooltip title={outlet.distanceDirection === 'right' ? 'Отсчёт снизу' : 'Отсчёт сверху'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => updateAdditionalOutlet(index, 'distanceDirection', outlet.distanceDirection === 'right' ? 'left' : 'right')}
                                      sx={{ 
                                        p: 0.3,
                                        '&:hover': { bgcolor: 'action.hover' }
                                      }}
                                    >
                                      {outlet.distanceDirection === 'right' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </TableCell>
                            
                            {/* Удалить */}
                            <TableCell sx={{ p: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => removeAdditionalOutlet(index)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Кнопка генерации */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGenerate}
                startIcon={<PlayArrowIcon />}
                fullWidth
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Сгенерировать чертёж
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Диалог предупреждения о несовместимости */}
      <Dialog
        open={incompatibilityDialog.open}
        onClose={() => setIncompatibilityDialog({ open: false, message: '' })}
      >
        <DialogTitle>Предупреждение</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {incompatibilityDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIncompatibilityDialog({ open: false, message: '' })}
            color="primary"
            autoFocus
          >
            ОК
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Создает объект pipeSpec с правильным покрытием
 * @param {number} diameter - Диаметр трубы
 * @param {string} thickness - Толщина трубы
 * @param {string} preferredCoating - Предпочитаемое покрытие
 * @returns {object} - Объект pipeSpec
 */
const createPipeSpec = (diameter, thickness, preferredCoating) => {
  const actualCoating = getActualCoating(diameter, thickness, preferredCoating);
  return {
    shape: 'round',
    diameter: diameter,
    thickness: thickness,
    coating: actualCoating,
  };
};

/**
 * Функция генерации объектов гидравлического разделителя
 * Аналогично этажному коллектору, но повёрнуто на 90 градусов (вертикально)
 */
function generateHydraulicSeparator(params) {
  const {
    shape = 'round',
    mainPipeDiameter,
    mainPipeProfile,
    separatorHeight,
    topCapType,
    topCapFitting,
    topCapFittingLength,
    bottomCapType,
    bottomCapFitting,
    bottomCapFittingLength,
    leftConnections = [],
    rightConnections = [],
    additionalOutlets = [],
  } = params;

  // Импортируем константы для квадратных профилей
  const { SQUARE_PROFILE_DISPLAY_SIZE, DEFAULT_SQUARE_PIPE_COMBOS } = require('../pipeCatalog');
  
  // Определяем размер основной трубы в зависимости от формы
  let mainPipeSize;
  if (shape === 'square') {
    const displaySize = SQUARE_PROFILE_DISPLAY_SIZE[mainPipeProfile] || 80;
    mainPipeSize = displaySize;
  } else {
    mainPipeSize = mainPipeDiameter;
  }

  // Определяем покрытие для всей системы
  const allDiameters = [mainPipeDiameter];
  
  // Импортируем функции для получения диаметров промежуточных труб
  const {
    extractDnFromFlangeKey,
    extractSizeFromNakGaikaKey,
    extractSizeFromShaibaNarRezba,
    getPipeDiameterForDn,
    getPipeDiameterForNakGaika,
    getPipeDiameterForShaibaNarRezba,
  } = require('./collectorUtils');
  
  // Функция для получения диаметра промежуточной трубы для фитинга
  const getPipeDiameterForFitting = (fittingId) => {
    if (!fittingId) return null;
    
    if (fittingId.startsWith('Flanec_')) {
      const dn = extractDnFromFlangeKey(fittingId);
      return getPipeDiameterForDn(dn);
    }
    
    if (fittingId.startsWith('Nak_Gaika_')) {
      const size = extractSizeFromNakGaikaKey(fittingId);
      return getPipeDiameterForNakGaika(size);
    }
    
    if (fittingId.startsWith('Shaib_Nar_Rezba_')) {
      const size = extractSizeFromShaibaNarRezba(fittingId);
      return getPipeDiameterForShaibaNarRezba(size);
    }
    
    return null;
  };
  
  // Добавляем диаметры ТОЛЬКО промежуточных труб фланцев из подключений
  // (сами подключения - муфты, ниппели и т.д. - НЕ влияют на покрытие основной системы)
  leftConnections.forEach(conn => {
    if (conn.fittingId && conn.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(conn.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  
  rightConnections.forEach(conn => {
    if (conn.fittingId && conn.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(conn.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  
  // Добавляем диаметры из торцевых подключений
  if (params.hasTopConnection && topCapFitting) {
    const pipeDiameter = getPipeDiameterForFitting(topCapFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (params.hasBottomConnection && bottomCapFitting) {
    const pipeDiameter = getPipeDiameterForFitting(bottomCapFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  
  // Убираем дубликаты
  const uniqueDiameters = [...new Set(allDiameters)];
  
  // Проверяем наличие оцинкованных фланцев в системе
  const hasGalvanizedFlanges = () => {
    // Проверяем левые и правые подключения
    if (leftConnections.some(conn => conn.fittingId && conn.fittingId.includes('_galv'))) return true;
    if (rightConnections.some(conn => conn.fittingId && conn.fittingId.includes('_galv'))) return true;
    
    // Проверяем торцевые подключения
    if ((params.hasTopConnection && topCapFitting && topCapFitting.includes('_galv')) ||
        (params.hasBottomConnection && bottomCapFitting && bottomCapFitting.includes('_galv'))) return true;
    
    return false;
  };
  
  // Определяем покрытие:
  // - Если квадратный профиль → всегда Матовый (профили только матовые)
  // - Если есть оцинкованные фланцы → всегда Матовый (для аннотации)
  // - Иначе: проверяем реальное покрытие основной трубы с учётом переданной толщины
  const { determineCoating, getActualCoating } = require('./pipeThicknessUtils');
  const hasGalvFlanges = hasGalvanizedFlanges();
  
  // Сначала определяем реальное покрытие основной трубы (если толщина задана явно из калькулятора)
  let coating;
  if (shape === 'square') {
    // Квадратные профили всегда матовые
    coating = 'Матовый';
  } else if (hasGalvFlanges) {
    coating = 'Матовый';
  } else if (params.mainPipeWallThickness) {
    // Если толщина задана явно (из калькулятора), проверяем реальное покрытие для этой комбинации
    coating = getActualCoating(mainPipeDiameter, params.mainPipeWallThickness, 'Зеркало');
  } else {
    // Иначе используем стандартную логику для всех диаметров
    coating = determineCoating(uniqueDiameters);
  }

  const cylinders = [];
  const fittings = [];
  const dimensionLines = [];

  // Функция для получения ключа заглушки по типу и диаметру
  const getCapKey = (capType, diameter, isSquare = false, profile = null) => {
    if (isSquare) {
      // Для квадратных труб всегда прямые заглушки с профилем
      // Заменяем латинскую x на русскую х для соответствия ключам в каталоге
      const profileKey = profile.replace('x', 'х');
      return `Zaglushka_PR_${profileKey}`;
    }
    
    if (capType === 'straight') {
      return `Zaglushka_PR_${diameter}`;
    } else {
      return `Zaglushka_EL_${diameter}`;
    }
  };
  
  // Локальная функция для получения длины фитинга с учетом формы трубы
  const getFittingLength = (fittingId, customLength) => {
    if (customLength !== null && customLength !== undefined) {
      return customLength;
    }
    
    const fittingData = fittingsData[fittingId];
    if (!fittingData) return 100;
    
    const isFlange = fittingId.startsWith('Flanec_');
    const isNakGaika = fittingId.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = fittingId.startsWith('Shaib_Nar_Rezba_');
    
    // Для квадратных профилей:
    if (shape === 'square') {
      // Накидные гайки и шайбы с резьбой: дефолт 58мм
      if (isNakGaika || isShaibaNarRezba) {
        return 58;
      }
    }
    
    // Для круглых или остальных фитингов:
    if (isFlange) {
      return 100; // Дефолт для фланцев
    }
    if (isNakGaika || isShaibaNarRezba) {
      return 40; // Дефолт для накидных гаек/шайб (круглые)
    }
    
    return fittingData.length; // Для остальных - из каталога
  };

  // Вычисляем длину заглушек (из параметров UI)
  const topCapKey = getCapKey(topCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
  const bottomCapKey = getCapKey(bottomCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
  const topCapData = fittingsData[topCapKey];
  const bottomCapData = fittingsData[bottomCapKey];
  
  // Для квадратных профилей: если заглушка не найдена в каталоге (для больших размеров),
  // используем правильную длину в зависимости от размера профиля
  let topCapLength = topCapData?.length || 0;
  let bottomCapLength = bottomCapData?.length || 0;
  
  if (shape === 'square') {
    // Маппинг профилей на толщину плоских заглушек
    const getSquareCapLength = (profile) => {
      // 150x150 имеет толщину 3мм, остальные 2мм
      if (profile === '150x150') return 3;
      return 2; // Для всех остальных квадратных профилей
    };
    
    if (topCapType === 'straight' && !topCapData) {
      topCapLength = getSquareCapLength(mainPipeProfile);
    }
    if (bottomCapType === 'straight' && !bottomCapData) {
      bottomCapLength = getSquareCapLength(mainPipeProfile);
    }
  }
  
  // === ОПРЕДЕЛЯЕМ ДЛИНУ ЗАГЛУШЕК ДЛЯ ПОЗИЦИОНИРОВАНИЯ ФИТИНГОВ ===
  // Длина зависит от типа заглушки, выбранного пользователем
  let topEllipticalCapLength = 0;
  let bottomEllipticalCapLength = 0;
  
  // Если включена верхняя заглушка и она эллиптическая - используем её длину
  if (params.hasTopCap && topCapType === 'elliptical') {
    topEllipticalCapLength = topCapLength;
  }
  
  // Если включена нижняя заглушка и она эллиптическая - используем её длину
  if (params.hasBottomCap && bottomCapType === 'elliptical') {
    bottomEllipticalCapLength = bottomCapLength;
  }
  
  // Для прямых заглушек они входят в общую высоту
  const topStraightCapLength = (topCapType === 'straight' && params.hasTopCap) ? topCapLength : 0;
  const bottomStraightCapLength = (bottomCapType === 'straight' && params.hasBottomCap) ? bottomCapLength : 0;
  
  // Преобразуем длины фитингов в числа (могут приходить как строки из UI)
  const topCapFittingLengthNum = topCapFittingLength !== null && topCapFittingLength !== undefined ? Number(topCapFittingLength) : null;
  const bottomCapFittingLengthNum = bottomCapFittingLength !== null && bottomCapFittingLength !== undefined ? Number(bottomCapFittingLength) : null;
  
  // === БОРТШАЙБЫ/ШАЙБЫ ДЛЯ ТОРЦЕВЫХ ПОДКЛЮЧЕНИЙ ===
  // Когда диаметры совпадают (впритык), бортшайбы/шайбы входят в общую высоту
  let topBortshaibaLengthForHeight = 0;
  let bottomBortshaibaLengthForHeight = 0;
  
  // Верхнее торцевое подключение
  if (params.hasTopConnection && topCapFitting) {
    const isFlange = topCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !topCapFitting.endsWith('_welded');
    const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
    
    // Проверяем нужен ли переход/заглушка
    const topCapReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    const fitsFlush = !topCapReq.needsCap; // Диаметры совпадают - впритык
    
    if (fitsFlush) {
      if (isFreeFlange || isNakGaika) {
        // Бортшайба/шайба входит в размер гребёнки
        const shaibaKey = getShaibaKeyForFitting(topCapFitting);
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            topBortshaibaLengthForHeight = shaibaInfo.length;
          }
        }
      } else if (isShaibaNarRezba) {
        // Сама шайба входит в размер гребёнки
        const shaibaInfo = fittingsData[topCapFitting];
        if (shaibaInfo) {
          topBortshaibaLengthForHeight = shaibaInfo.length;
        }
      }
    }
  }
  
  // Нижнее торцевое подключение
  if (params.hasBottomConnection && bottomCapFitting) {
    const isFlange = bottomCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !bottomCapFitting.endsWith('_welded');
    const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
    
    // Проверяем нужен ли переход/заглушка
    const bottomCapReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    const fitsFlush = !bottomCapReq.needsCap; // Диаметры совпадают - впритык
    
    if (fitsFlush) {
      if (isFreeFlange || isNakGaika) {
        // Бортшайба/шайба входит в размер гребёнки
        const shaibaKey = getShaibaKeyForFitting(bottomCapFitting);
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            bottomBortshaibaLengthForHeight = shaibaInfo.length;
          }
        }
      } else if (isShaibaNarRezba) {
        // Сама шайба входит в размер гребёнки
        const shaibaInfo = fittingsData[bottomCapFitting];
        if (shaibaInfo) {
          bottomBortshaibaLengthForHeight = shaibaInfo.length;
        }
      }
    }
  }
  
  // Фактическая высота основной трубы (без прямых заглушек и бортшайб)
  const actualMainPipeHeight = separatorHeight - topStraightCapLength - bottomStraightCapLength - topBortshaibaLengthForHeight - bottomBortshaibaLengthForHeight;
  
  // Вычисляем длину фитингов на заглушках
  const topFittingData = topCapFitting ? fittingsData[topCapFitting] : null;
  const bottomFittingData = bottomCapFitting ? fittingsData[bottomCapFitting] : null;
  const topFittingLength = topFittingData
    ? endFittingAutoLength(topCapFitting, topCapFittingLength, getFittingLength(topCapFitting, null))
    : 0;
  // eslint-disable-next-line no-unused-vars
  const bottomFittingLength = bottomFittingData
    ? endFittingAutoLength(bottomCapFitting, bottomCapFittingLength, getFittingLength(bottomCapFitting, null))
    : 0;
  
  // Вычисляем максимальную длину левых подключений
  let maxLeftFittingLength = 0;
  leftConnections.forEach(conn => {
    const connFittingData = fittingsData[conn.fittingId];
    if (connFittingData) {
      const fittingLength = getFittingLength(conn.fittingId, conn.fittingLength);
      maxLeftFittingLength = Math.max(maxLeftFittingLength, fittingLength);
    }
  });

  // Вычисляем максимальную длину правых подключений
  let maxRightFittingLength = 0;
  rightConnections.forEach(conn => {
    const connFittingData = fittingsData[conn.fittingId];
    if (connFittingData) {
      const fittingLength = getFittingLength(conn.fittingId, conn.fittingLength);
      maxRightFittingLength = Math.max(maxRightFittingLength, fittingLength);
    }
  });

  // Размеры холста (портретная ориентация A3: 297мм x 420мм при 72 DPI)
  // В пикселях: ~842px x 1191px
  const canvasWidth = 842;
  
  // Центрируем конструкцию на холсте
  // startX - это горизонтальный центр (центр основной трубы)
  const startX = canvasWidth / 2;
  
  // startY - верхняя точка конструкции
  // Фиксированный отступ сверху
  const startY = 100;
  
  // Позиция верхнего края основной трубы
  // startY - это верхняя точка всей конструкции
  // Добавляем длину верхнего фитинга, эллиптической заглушки, бортшайбы и прямой заглушки (если есть)
  // Прямая заглушка входит в общую высоту, поэтому смещаем начало трубы вниз
  const mainPipeTopY = startY + topFittingLength + topEllipticalCapLength + topBortshaibaLengthForHeight + topStraightCapLength;

  // 1. Основная труба (вертикальная)
  // Для вертикальной трубы: x - левый край, y - верхний край
  // width - диаметр/размер (горизонтальный размер), height - высота (вертикальный размер)
  const mainPipeId = `cyl-main-${Date.now()}`;
  let mainPipeSpec; // Объявляем заранее для использования в размерных линиях
  
  if (shape === 'square') {
    // Для квадратного профиля создаем прямоугольник
    // Ищем подходящую комбинацию профиль|толщина|покрытие
    const profileKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => 
      key.startsWith(`SQUARE_PROFILE|${mainPipeProfile}|`)
    );
    
    let thickness = '2';
    // Для квадратных профилей всегда используем 'Матовый', так как они не бывают зеркальными
    let squareCoating = 'Матовый';
    
    if (profileKey) {
      const parts = profileKey.split('|');
      thickness = parts[2] || '2';
      squareCoating = parts[3] || 'Матовый';
    }
    
    // Создаем объект pipeSpec для квадратных труб (как ожидает PriceDialog)
    // Используем squareCoating для спецификации трубы, но coating для аннотации
    mainPipeSpec = {
      shape: 'square',
      profile: mainPipeProfile,
      thickness: thickness,
      coating: squareCoating,
      squareType: 'profile'
    };
    
    cylinders.push({
      id: mainPipeId,
      x: startX - mainPipeSize / 2,
      y: mainPipeTopY,
      width: mainPipeSize,
      height: actualMainPipeHeight,
      pipeSpec: mainPipeSpec,
      shape: 'square', // Флаг для отрисовки прямоугольника
    });
  } else {
    // Для круглой трубы
    const wallThickness = resolveCatalogThickness(
      mainPipeDiameter,
      params.mainPipeWallThickness || getMinThickness(mainPipeDiameter, coating),
      coating
    );
    mainPipeSpec = createPipeSpec(mainPipeDiameter, wallThickness, coating);
    cylinders.push({
      id: mainPipeId,
      x: startX - mainPipeSize / 2,
      y: mainPipeTopY,
      width: mainPipeDiameter,
      height: actualMainPipeHeight,
      pipeSpec: mainPipeSpec,
      shape: 'round',
    });
  }

  // ID для заглушек (для привязки размерных линий)
  let topStraightCapIdForDimLine = null;
  let bottomStraightCapIdForDimLine = null;
  let topEllipticalCapIdForDimLine = null;
  let bottomEllipticalCapIdForDimLine = null;
  let topCapReducerId = null;
  let bottomCapReducerId = null;

  // 2. Верхняя заглушка или переход
  // Генерируем заглушку/переход только если включена в параметрах
  let generateTopCap = params.hasTopCap;
  
  // Если есть торцевое подключение - проверяем совместимость
  if (params.hasTopConnection && topCapFitting && topFittingData) {
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    
    // Если фитинг впритык - заглушка не нужна (отключаем даже если была включена)
    if (capReq.fitsFlush) {
      generateTopCap = false;
    }
    // Если нужна заглушка/переход, но пользователь их отключил - НЕ создаем принудительно
    // (пользователь имеет контроль)
  }

  if (generateTopCap) {
    if (params.topCapElementType === 'reducer' && params.topReducer) {
      // Генерируем переход концентрический
      const reducerData = fittingsData[params.topReducer];
      if (reducerData) {
        topCapReducerId = `fitting-reducer-top-${Date.now()}`;
        fittings.push({
          id: topCapReducerId,
          key: params.topReducer,
          itemKey: params.topReducer,
          x: 0,
          y: 0,
          width: reducerData.diameter,
          height: reducerData.length,
          groupX: startX,
          groupY: mainPipeTopY,
          rotation: 0,
          offsetX: reducerData.diameter / 2,
          offsetY: reducerData.length,
        });
      }
    } else {
      // Генерируем заглушку - используем тип, выбранный пользователем
      const capKey = getCapKey(params.topCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
      const capData = fittingsData[capKey];
      
      if (capData) {
        const topCapId = `fitting-cap-top-${Date.now()}`;
        // Сохраняем ID для размерных линий
        if (params.topCapType === 'straight') {
          topStraightCapIdForDimLine = topCapId;
        } else if (params.topCapType === 'elliptical') {
          topEllipticalCapIdForDimLine = topCapId;
        }
        
        fittings.push({
          id: topCapId,
          key: capKey,
          itemKey: capKey,
          x: 0,
          y: 0,
          width: capData.diameter,
          height: capData.length,
          groupX: startX,
          groupY: mainPipeTopY,
          rotation: 0, // Направлена вверх
          offsetX: capData.diameter / 2,
          offsetY: capData.length,
        });
      }
    }
  }

  // 3. Нижняя заглушка или переход
  // Генерируем заглушку/переход только если включена в параметрах
  let generateBottomCap = params.hasBottomCap;
  
  // Если есть торцевое подключение - проверяем совместимость
  if (params.hasBottomConnection && bottomCapFitting && bottomFittingData) {
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    
    // Если фитинг впритык - заглушка не нужна (отключаем даже если была включена)
    if (capReq.fitsFlush) {
      generateBottomCap = false;
    }
    // Если нужна заглушка/переход, но пользователь их отключил - НЕ создаем принудительно
    // (пользователь имеет контроль)
  }

  if (generateBottomCap) {
    if (params.bottomCapElementType === 'reducer' && params.bottomReducer) {
      // Генерируем переход концентрический
      const reducerData = fittingsData[params.bottomReducer];
      if (reducerData) {
        bottomCapReducerId = `fitting-reducer-bottom-${Date.now()}`;
        fittings.push({
          id: bottomCapReducerId,
          key: params.bottomReducer,
          itemKey: params.bottomReducer,
          x: 0,
          y: 0,
          width: reducerData.diameter,
          height: reducerData.length,
          groupX: startX,
          groupY: mainPipeTopY + actualMainPipeHeight,
          rotation: 180,
          offsetX: reducerData.diameter / 2,
          offsetY: reducerData.length,
        });
      }
    } else {
      // Генерируем заглушку - используем тип, выбранный пользователем
      const capKey = getCapKey(params.bottomCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
      const capData = fittingsData[capKey];
      
      if (capData) {
        const bottomCapId = `fitting-cap-bottom-${Date.now()}`;
        // Сохраняем ID для размерных линий
        if (params.bottomCapType === 'straight') {
          bottomStraightCapIdForDimLine = bottomCapId;
        } else if (params.bottomCapType === 'elliptical') {
          bottomEllipticalCapIdForDimLine = bottomCapId;
        }
        fittings.push({
          id: bottomCapId,
          key: capKey,
          itemKey: capKey,
          x: 0,
          y: 0,
          width: capData.diameter,
          height: capData.length,
          groupX: startX,
          groupY: mainPipeTopY + actualMainPipeHeight,
          rotation: 180, // Направлена вниз
          offsetX: capData.diameter / 2,
          offsetY: capData.length,
        });
      }
    }
  }

  // 4. Фитинг на верхней заглушке
  // Логика полностью аналогична левому/правому подключению в FloorCollectorEditor
  // Генерируем только если включено торцевое подключение сверху
  
  // Переменные для хранения ID объектов (для привязки размерных линий)
  let topReducerId = null;
  let topIntermediatePipeId = null;
  let topFittingEndObjectId = null; // ID конечного объекта (фитинг или шайба)
  let topFittingEndObjectType = null; // 'fitting' или 'cylinder'
  let topFittingId = null; // ID основного фитинга (для balloons)
  let topBortshaibaIdForDimLine = null; // ID бортшайбы для размерной линии общей высоты
  
  if (params.hasTopConnection && topCapFitting && topFittingData) {
    topFittingId = `fitting-top-${Date.now()}`;
    
    // Определяем тип фитинга
    const isFlange = topCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !topCapFitting.endsWith('_welded');
    const isWeldedFlange = isFlange && !isFreeFlange;
    const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
    const isNippel = topCapFitting.startsWith('Nippel_');
    const isMufta = topCapFitting.startsWith('Mufta_');
    const needsWasher = isFreeFlange || isNakGaika;
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting = topFittingData.diameter;
    if (isFlange) {
      const dn = extractDnFromFlangeKey(topCapFitting);
      pipeDiameterForFitting = getPipeDiameterForDn(dn) || topFittingData.diameter;
    } else if (isNakGaika) {
      const size = extractSizeFromNakGaikaKey(topCapFitting);
      pipeDiameterForFitting = getPipeDiameterForNakGaika(size) || topFittingData.diameter;
    } else if (isShaibaNarRezba) {
      const size = extractSizeFromShaibaNarRezba(topCapFitting);
      pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size) || topFittingData.diameter;
    }
    
    // Проверяем нужен ли переход/заглушка
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    const needsReducer = capReq.needsCap && capReq.capType === 'reducer';
    const needsEllipticalCap = capReq.needsCap && capReq.capType === 'elliptical';
    const fitsFlush = capReq.fitsFlush; // Диаметры совпадают - впритык
    
    // Начальная точка - верх основной трубы
    // ВАЖНО: Когда есть торцевое подключение, заглушка/переход НЕ создаются отдельно в секции заглушек,
    // поэтому переход/фитинг всегда крепится впритык к основной трубе (mainPipeTopY)
    const capTopY = mainPipeTopY;
    
    // Получаем ключ шайбы/бортшайбы
    const shaibaKey = getShaibaKeyForFitting(topCapFitting);
    let bortshaibaLength = 0;
    if (needsWasher && shaibaKey) {
      const shaibaInfo = fittingsData[shaibaKey];
      bortshaibaLength = shaibaInfo?.length || 10;
    }
    if (isShaibaNarRezba) {
      bortshaibaLength = topFittingData.length || 10;
    }
    
    // === ЛОГИКА 1: Диаметры совпадают (впритык) БЕЗ заглушки ===
    if (fitsFlush && !params.hasTopCap) {
      if (isNippel || isMufta) {
        // Ниппель/муфта впритык к основной трубе
        const defaultLength = topFittingData.length;
        const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, defaultLength);
        
        // Логика для "Измененное значение"
        let dimensionOverride = {};
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: mainPipeTopY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else if (isFreeFlange || isNakGaika) {
        // Свободный фланец или накидная гайка впритык: шайба/бортшайба входит в размер гребенки
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
            topBortshaibaIdForDimLine = bortshaibaId; // Сохраняем для размерной линии
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0,
              y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: startX,
              groupY: mainPipeTopY, // От верха гребенки (шайба входит в размер)
              rotation: 0,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
          }
        }
        // Фланец/гайка надет на шайбу (шайба на конце трубы, фланец перед ней)
        const fLength = topFittingData.length;
        // Фланец располагается от конца трубы (mainPipeTopY) до бортшайбы
        // mainPipeTopY - это верх трубы (после учёта бортшайбы в высоте)
        // Фланец надет на трубу, его низ на уровне mainPipeTopY, верх выше
        
        // Логика для "Измененное значение" - накидные гайки
        let dimensionOverride = {};
        const nakGaikaDefaultLength = shape === 'square' ? 58 : 40;
        if (isNakGaika && (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === nakGaikaDefaultLength)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
            bortshaibaId: topBortshaibaIdForDimLine,
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: mainPipeTopY + fLength, // Фланец надет на трубу, его верх выше mainPipeTopY
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      } else if (isShaibaNarRezba) {
        // Шайба с наружной резьбой впритык: сама шайба входит в размер гребенки
        topBortshaibaIdForDimLine = topFittingId; // Сохраняем для размерной линии (сама шайба)
        
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        const shaibaDefaultLength = shape === 'square' ? 58 : 40;
        if (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === shaibaDefaultLength) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: topFittingData.length,
          groupX: startX,
          groupY: mainPipeTopY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: topFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      } else if (isWeldedFlange) {
        // Приварной фланец впритык
        const fLength = topFittingData.length;
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: mainPipeTopY + fLength,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      }
    }
    // === ЛОГИКА 1.5: Ниппели/муфты С заглушкой (прямой или эллиптической), БЕЗ перехода ===
    else if ((isNippel || isMufta) && params.hasTopCap && params.topCapElementType !== 'reducer') {
      // Если есть заглушка (не переход) - ниппель/муфта размещается от конца заглушки
      const defaultLength = topFittingData.length;
      const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, defaultLength);
      
      // Для прямой заглушки: она входит в размер, ниппель от (mainPipeTopY - topStraightCapLength)
      // Для эллиптической заглушки: она выступает, ниппель от (mainPipeTopY - topEllipticalCapLength)
      let connectionStartY = mainPipeTopY;
      if (topCapType === 'elliptical') {
        connectionStartY = mainPipeTopY - topEllipticalCapLength;
      } else if (topCapType === 'straight') {
        connectionStartY = mainPipeTopY - topStraightCapLength;
      }
      
      // Логика для "Измененное значение"
      let dimensionOverride = {};
      
      fittings.push({
        id: topFittingId,
        key: topCapFitting,
        itemKey: topCapFitting,
        x: 0,
        y: 0,
        width: topFittingData.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 0,
        offsetX: topFittingData.diameter / 2,
        offsetY: fLength,
        ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      topFittingEndObjectId = topFittingId;
      topFittingEndObjectType = 'fitting';
    }
    // === ЛОГИКА 1.6: Фланцы/Накидные гайки/шайбы с резьбой С ПРЯМОЙ заглушкой ===
    else if ((isFlange || isNakGaika || isShaibaNarRezba) && params.hasTopCap && topCapType === 'straight') {
      // Прямая заглушка - фитинги идут от её конца
      const capEndY = mainPipeTopY - topStraightCapLength;
      
      // Промежуточная труба для фланцев/гаек
      const defaultNakGaikaShaiba = shape === 'square' ? 58 : 40;
      const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba : (isFlange ? 100 : getFittingLength(topCapFitting, null));
      const totalConnectionLength = topCapFittingLengthNum !== null && topCapFittingLengthNum !== undefined ? topCapFittingLengthNum : defaultLength;
      // Для прямой заглушки: вылет отсчитывается ОТ заглушки, НЕ вычитаем длину заглушки
      const intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
      
      let pipeEndY = capEndY;
      if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
        const intermediatePipeId = `cyl-top-intermediate-${Date.now()}`;
        topIntermediatePipeId = intermediatePipeId;
        // Для накидных гаек и шайб с резьбой: фиксированная толщина x3
        const intThickness16top = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
        cylinders.push({
          id: intermediatePipeId,
          x: startX - pipeDiameterForFitting / 2,
          y: capEndY - intermediatePipeLength,
          width: pipeDiameterForFitting,
          height: intermediatePipeLength,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, intThickness16top, coating),
          insertionDepth: 0,
        });
        pipeEndY = capEndY - intermediatePipeLength;
      }
      
      // Бортшайба/шайба
      let topBortshaibaId = null;
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
          topBortshaibaId = bortshaibaId;
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 0,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Шайба с наружной резьбой
      if (isShaibaNarRezba) {
        let dimensionOverride = {};
        const shaibaDefaultLength2 = shape === 'square' ? 58 : 40;
        if (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === shaibaDefaultLength2) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: topFittingData.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: topFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = topFittingData.length;
        const fittingY = pipeEndY + fLength;
        
        let dimensionOverride = {};
        const nakGaikaDefaultLength2 = shape === 'square' ? 58 : 40;
        if (isNakGaika && (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === nakGaikaDefaultLength2)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
            bortshaibaId: topBortshaibaId,
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        if (topBortshaibaId) {
          topFittingEndObjectId = topBortshaibaId;
          topFittingEndObjectType = 'fitting';
        } else {
          topFittingEndObjectId = topFittingId;
          topFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 2: С переходом концентрическим ===
    else if (needsReducer && capReq.reducer) {
      const reducerKey = capReq.reducer.key;
      const reducerInfo = capReq.reducer.info;
      const reducerLength = reducerInfo.length;
      
      // Переход от заглушки вверх
      const reducerId = `fitting-top-reducer-${Date.now()}`;
      // eslint-disable-next-line no-unused-vars
      topReducerId = reducerId; // Сохраняем ID для размерных линий
      
      fittings.push({
        id: reducerId,
        key: reducerKey,
        itemKey: reducerKey,
        x: 0,
        y: 0,
        width: reducerInfo.diameter,
        height: reducerLength,
        groupX: startX,
        groupY: capTopY,
        rotation: 0,
        offsetX: reducerInfo.diameter / 2,
        offsetY: reducerLength,
      });
      
      // Точка подключения после перехода
      const reducerEndY = capTopY - reducerLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от перехода)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = reducerEndY;
      if (!isNippel && !isMufta) {
        // Длина вылета (общая длина - переход - бортшайба)
        const defaultNakGaikaShaiba = shape === 'square' ? 58 : 40;
        const totalConnectionLength = topCapFittingLengthNum ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba : (isFlange ? 100 : getFittingLength(topCapFitting, null)));
        const intermediatePipeLength = Math.max(0, totalConnectionLength - reducerLength - bortshaibaLength);
        
        // Промежуточная труба
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-top-intermediate-${Date.now()}`;
          topIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3
          const intThicknessTopReducer = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: reducerEndY - intermediatePipeLength,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intThicknessTopReducer, coating),
            insertionDepth: 0,
          });
          pipeEndY = reducerEndY - intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба (для свободных фланцев и накидных гаек)
      let topBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
          topBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 0,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Фитинг
      if (isNippel || isMufta) {
        // Ниппель/муфта с переходом - размещаем сразу от перехода
        const defaultLength = topFittingData.length;
        const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, defaultLength);
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = topFittingData.length;
        const fittingY = pipeEndY + fLength;
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (topBortshaibaId) {
          topFittingEndObjectId = topBortshaibaId;
          topFittingEndObjectType = 'fitting';
        } else {
          topFittingEndObjectId = topFittingId;
          topFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 3: С эллиптической заглушкой (разные диаметры, нет перехода) ===
    else if (needsEllipticalCap) {
      // Эллиптическая заглушка уже есть (topCapType = 'elliptical')
      // ВАЖНО: Эллиптическая заглушка выступает наружу
      
      // Начальная точка - от конца эллиптической заглушки
      const capEndY = mainPipeTopY - topEllipticalCapLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от заглушки)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = capEndY;
      if (!isNippel && !isMufta) {
        // Промежуточная труба для фланцев/гаек
        const defaultNakGaikaShaiba = shape === 'square' ? 58 : 40;
        const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba : (isFlange ? 100 : getFittingLength(topCapFitting, null));
        const totalConnectionLength = topCapFittingLengthNum !== null && topCapFittingLengthNum !== undefined ? topCapFittingLengthNum : defaultLength;
        const capLength = topEllipticalCapLength; // Длина эллиптической заглушки
        // Для накидных гаек и шайб с резьбой: вылет от заглушки, не вычитаем capLength
        const intermediatePipeLength = (isNakGaika || isShaibaNarRezba) 
          ? Math.max(0, totalConnectionLength - bortshaibaLength)
          : Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
        
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-top-intermediate-${Date.now()}`;
          // eslint-disable-next-line no-unused-vars
          topIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3
          const intThicknessTopElliptical = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: capEndY - intermediatePipeLength,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intThicknessTopElliptical, coating),
            insertionDepth: 0,
          });
          pipeEndY = capEndY - intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба
      let topBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-top-shaiba-${Date.now()}`;
          topBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 0,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Шайба с наружной резьбой (сама шайба = фитинг)
      if (isShaibaNarRezba) {
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        const shaibaDefaultLength3 = shape === 'square' ? 58 : 40;
        if (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === shaibaDefaultLength3) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: topFittingData.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: topFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else if (isNippel || isMufta) {
        // Ниппель/муфта с эллиптической заглушкой - размещаем фитинг сразу от заглушки (без промежуточной трубы)
        const defaultLength = topFittingData.length;
        const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, defaultLength);
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        topFittingEndObjectId = topFittingId;
        topFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = topFittingData.length;
        const fittingY = pipeEndY + fLength;
        
        // Логика для "Измененное значение" - накидные гайки
        let dimensionOverride = {};
        const nakGaikaDefaultLength3 = shape === 'square' ? 58 : 40;
        if (isNakGaika && (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === nakGaikaDefaultLength3)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
            bortshaibaId: topBortshaibaId,
          };
        }
        
        fittings.push({
          id: topFittingId,
          key: topCapFitting,
          itemKey: topCapFitting,
          x: 0,
          y: 0,
          width: topFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 0,
          offsetX: topFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (topBortshaibaId) {
          topFittingEndObjectId = topBortshaibaId;
          topFittingEndObjectType = 'fitting';
        } else {
          topFittingEndObjectId = topFittingId;
          topFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 4: Обычный фитинг с заглушкой (ниппель/муфта) ===
    // Аналогично распределительному коллектору: фитинг размещается от края заглушки
    else {
      const defaultLength = topFittingData.length;
      const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, defaultLength);
      
      // Начальная точка подключения - от края заглушки (если есть)
      // Для эллиптической заглушки: она создана в секции 2 и размещена от mainPipeTopY вверх на topEllipticalCapLength
      // Поэтому фитинг должен начинаться от (mainPipeTopY - topEllipticalCapLength)
      let connectionStartY = mainPipeTopY;
      if (needsEllipticalCap) {
        // Эллиптическая заглушка выступает наружу - фитинг от её конца
        connectionStartY = mainPipeTopY - topEllipticalCapLength;
      }
      // Прямая заглушка входит в размер гребёнки - фитинг от mainPipeTopY
      
      
      fittings.push({
        id: topFittingId,
        key: topCapFitting,
        itemKey: topCapFitting,
        x: 0,
        y: 0,
        width: topFittingData.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 0,
        offsetX: topFittingData.diameter / 2,
        offsetY: fLength,
        // IGES: торцевые подключения НЕ создают отверстия
        igesCreateHole: false,
      });
    }
  }

  // 5. Фитинг на нижней заглушке
  // Логика полностью аналогична левому/правому подключению в FloorCollectorEditor
  // Генерируем только если включено торцевое подключение снизу
  
  // Переменные для хранения ID объектов (для привязки размерных линий)
  let bottomReducerId = null;
  let bottomIntermediatePipeId = null;
  let bottomFittingEndObjectId = null;
  let bottomFittingEndObjectType = null;
  let bottomFittingId = null; // ID основного фитинга (для balloons)
  let bottomBortshaibaIdForDimLine = null; // ID бортшайбы для размерной линии общей высоты

  if (params.hasBottomConnection && bottomCapFitting && bottomFittingData) {
    bottomFittingId = `fitting-bottom-${Date.now()}`;
    
    // Определяем тип фитинга
    const isFlange = bottomCapFitting.startsWith('Flanec_');
    const isFreeFlange = isFlange && !bottomCapFitting.endsWith('_welded');
    const isWeldedFlange = isFlange && !isFreeFlange;
    const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
    const isNippel = bottomCapFitting.startsWith('Nippel_');
    const isMufta = bottomCapFitting.startsWith('Mufta_');
    const needsWasher = isFreeFlange || isNakGaika;
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting = bottomFittingData.diameter;
    if (isFlange) {
      const dn = extractDnFromFlangeKey(bottomCapFitting);
      pipeDiameterForFitting = getPipeDiameterForDn(dn) || bottomFittingData.diameter;
    } else if (isNakGaika) {
      const size = extractSizeFromNakGaikaKey(bottomCapFitting);
      pipeDiameterForFitting = getPipeDiameterForNakGaika(size) || bottomFittingData.diameter;
    } else if (isShaibaNarRezba) {
      const size = extractSizeFromShaibaNarRezba(bottomCapFitting);
      pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size) || bottomFittingData.diameter;
    }
    
    // Проверяем нужен ли переход/заглушка
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    const needsReducer = capReq.needsCap && capReq.capType === 'reducer';
    const needsEllipticalCap = capReq.needsCap && capReq.capType === 'elliptical';
    const fitsFlush = capReq.fitsFlush; // Диаметры совпадают - впритык
    
    // Начальная точка - низ основной трубы
    // ВАЖНО: Когда есть торцевое подключение, заглушка/переход НЕ создаются отдельно в секции заглушек,
    // поэтому переход/фитинг всегда крепится впритык к основной трубе (bottomEdgeY)
    const bottomEdgeY = mainPipeTopY + actualMainPipeHeight;
    const capBottomY = bottomEdgeY;
    
    // Получаем ключ шайбы/бортшайбы
    const shaibaKey = getShaibaKeyForFitting(bottomCapFitting);
    let bortshaibaLength = 0;
    if (needsWasher && shaibaKey) {
      const shaibaInfo = fittingsData[shaibaKey];
      bortshaibaLength = shaibaInfo?.length || 10;
    }
    if (isShaibaNarRezba) {
      bortshaibaLength = bottomFittingData.length || 10;
    }
    
    // === ЛОГИКА 1: Диаметры совпадают (впритык) БЕЗ заглушки ===
    if (fitsFlush && !params.hasBottomCap) {
      if (isNippel || isMufta) {
        // Ниппель/муфта впритык к основной трубе
        const defaultLength = bottomFittingData.length;
        const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, defaultLength);
        
        // Логика для "Измененное значение"
        let dimensionOverride = {};
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: bottomEdgeY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else if (isFreeFlange || isNakGaika) {
        // Свободный фланец или накидная гайка впритык: шайба/бортшайба входит в размер гребенки
        let bottomBortshaibaId = null; // Сохраняем ID бортшайбы
        if (shaibaKey) {
          const shaibaInfo = fittingsData[shaibaKey];
          if (shaibaInfo) {
            const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
            bottomBortshaibaId = bortshaibaId; // Сохраняем ID
            bottomBortshaibaIdForDimLine = bortshaibaId; // Сохраняем для размерной линии
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0,
              y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: startX,
              groupY: bottomEdgeY, // От низа гребенки (шайба входит в размер)
              rotation: 180,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
          }
        }
        // Фланец/гайка надет на шайбу
        const fLength = bottomFittingData.length;
        
        // Логика для "Измененное значение" - накидные гайки
        let dimensionOverride = {};
        const nakGaikaDefaultLengthBottom = shape === 'square' ? 58 : 40;
        if (isNakGaika && (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === nakGaikaDefaultLengthBottom)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
            bortshaibaId: bottomBortshaibaId,
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: bottomEdgeY - fLength,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть)
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        }
      } else if (isShaibaNarRezba) {
        // Шайба с наружной резьбой впритык: сама шайба входит в размер гребенки
        bottomBortshaibaIdForDimLine = bottomFittingId; // Сохраняем для размерной линии (сама шайба)
        
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        const shaibaDefaultLengthBottom = shape === 'square' ? 58 : 40;
        if (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === shaibaDefaultLengthBottom) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: bottomFittingData.length,
          groupX: startX,
          groupY: bottomEdgeY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: bottomFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      } else if (isWeldedFlange) {
        // Приварной фланец впритык
        const fLength = bottomFittingData.length;
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: bottomEdgeY - fLength,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      }
    }
    // === ЛОГИКА 1.5: Ниппели/муфты С заглушкой (прямой или эллиптической), БЕЗ перехода ===
    else if ((isNippel || isMufta) && params.hasBottomCap && params.bottomCapElementType !== 'reducer') {
      // Если есть заглушка (не переход) - ниппель/муфта размещается от конца заглушки
      const defaultLength = bottomFittingData.length;
      const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, defaultLength);
      
      // Для прямой заглушки: она входит в размер, ниппель от (bottomEdgeY + bottomStraightCapLength)
      // Для эллиптической заглушки: она выступает, ниппель от (bottomEdgeY + bottomEllipticalCapLength)
      let connectionStartY = bottomEdgeY;
      if (bottomCapType === 'elliptical') {
        connectionStartY = bottomEdgeY + bottomEllipticalCapLength;
      } else if (bottomCapType === 'straight') {
        connectionStartY = bottomEdgeY + bottomStraightCapLength;
      }
      
      // Логика для "Измененное значение"
      let dimensionOverride = {};
      
      fittings.push({
        id: bottomFittingId,
        key: bottomCapFitting,
        itemKey: bottomCapFitting,
        x: 0,
        y: 0,
        width: bottomFittingData.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 180,
        offsetX: bottomFittingData.diameter / 2,
        offsetY: fLength,
        ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
      bottomFittingEndObjectId = bottomFittingId;
      bottomFittingEndObjectType = 'fitting';
    }
    // === ЛОГИКА 1.6: Фланцы/Накидные гайки/шайбы с резьбой С ПРЯМОЙ заглушкой ===
    else if ((isFlange || isNakGaika || isShaibaNarRezba) && params.hasBottomCap && bottomCapType === 'straight') {
      // Прямая заглушка - фитинги идут от её конца
      const capEndY = bottomEdgeY + bottomStraightCapLength;
      
      // Промежуточная труба для фланцев/гаек
      const defaultNakGaikaShaiba = shape === 'square' ? 58 : 40;
      const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba : (isFlange ? 100 : getFittingLength(bottomCapFitting, null));
      const totalConnectionLength = bottomCapFittingLengthNum !== null && bottomCapFittingLengthNum !== undefined ? bottomCapFittingLengthNum : defaultLength;
      // Для прямой заглушки: вылет отсчитывается ОТ заглушки, НЕ вычитаем длину заглушки
      const intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
      
      let pipeEndY = capEndY;
      if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
        const intermediatePipeId = `cyl-bottom-intermediate-${Date.now()}`;
        bottomIntermediatePipeId = intermediatePipeId;
        // Для накидных гаек и шайб с резьбой: фиксированная толщина x3
        const intThickness16bottom = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
        cylinders.push({
          id: intermediatePipeId,
          x: startX - pipeDiameterForFitting / 2,
          y: capEndY,
          width: pipeDiameterForFitting,
          height: intermediatePipeLength,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, intThickness16bottom, coating),
          insertionDepth: 0,
        });
        pipeEndY = capEndY + intermediatePipeLength;
      }
      
      // Бортшайба/шайба
      let bottomBortshaibaId = null;
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
          bottomBortshaibaId = bortshaibaId;
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 180,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Шайба с наружной резьбой
      if (isShaibaNarRezba) {
        let dimensionOverride = {};
        const shaibaDefaultLengthBottom2 = shape === 'square' ? 58 : 40;
        if (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === shaibaDefaultLengthBottom2) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: bottomFittingData.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: bottomFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = bottomFittingData.length;
        const fittingY = pipeEndY - fLength;
        
        let dimensionOverride = {};
        const nakGaikaDefaultLengthBottom2 = shape === 'square' ? 58 : 40;
        if (isNakGaika && (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === nakGaikaDefaultLengthBottom2)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
            bortshaibaId: bottomBortshaibaId,
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        } else {
          bottomFittingEndObjectId = bottomFittingId;
          bottomFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 2: С переходом концентрическим ===
    else if (needsReducer && capReq.reducer) {
      const reducerKey = capReq.reducer.key;
      const reducerInfo = capReq.reducer.info;
      const reducerLength = reducerInfo.length;
      
      // Переход от заглушки вниз
      const reducerId = `fitting-bottom-reducer-${Date.now()}`;
      // eslint-disable-next-line no-unused-vars
      bottomReducerId = reducerId; // Сохраняем ID для размерных линий
      fittings.push({
        id: reducerId,
        key: reducerKey,
        itemKey: reducerKey,
        x: 0,
        y: 0,
        width: reducerInfo.diameter,
        height: reducerLength,
        groupX: startX,
        groupY: capBottomY,
        rotation: 180,
        offsetX: reducerInfo.diameter / 2,
        offsetY: reducerLength,
      });
      
      // Точка подключения после перехода
      const reducerEndY = capBottomY + reducerLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от перехода)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = reducerEndY;
      if (!isNippel && !isMufta) {
        // Длина вылета (общая длина - переход - бортшайба)
        const defaultNakGaikaShaiba2 = shape === 'square' ? 58 : 40;
        const totalConnectionLength = bottomCapFittingLengthNum ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba2 : (isFlange ? 100 : getFittingLength(bottomCapFitting, null)));
        const intermediatePipeLength = Math.max(0, totalConnectionLength - reducerLength - bortshaibaLength);
        
        // Промежуточная труба
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-bottom-intermediate-${Date.now()}`;
          bottomIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3
          const intThicknessBottomReducer = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: reducerEndY,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intThicknessBottomReducer, coating),
            insertionDepth: 0,
          });
          pipeEndY = reducerEndY + intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба (для свободных фланцев и накидных гаек)
      let bottomBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
          bottomBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 180,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Фитинг
      if (isNippel || isMufta) {
        // Ниппель/муфта с переходом - размещаем сразу от перехода
        const defaultLength = bottomFittingData.length;
        const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, defaultLength);
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = bottomFittingData.length;
        const fittingY = pipeEndY - fLength;
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        } else {
          bottomFittingEndObjectId = bottomFittingId;
          bottomFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 3: С эллиптической заглушкой (разные диаметры, нет перехода) ===
    else if (needsEllipticalCap) {
      // Эллиптическая заглушка уже есть (bottomCapType = 'elliptical')
      // ВАЖНО: Эллиптическая заглушка выступает наружу
      
      // Начальная точка - от конца эллиптической заглушки
      const capEndY = bottomEdgeY + bottomEllipticalCapLength;
      
      // Для ниппелей/муфт: промежуточной трубы НЕТ (они идут сразу от заглушки)
      // Для фланцев/гаек: нужна промежуточная труба (они надеваются на трубку)
      let pipeEndY = capEndY;
      if (!isNippel && !isMufta) {
        // Промежуточная труба для фланцев/гаек
        const defaultNakGaikaShaiba3 = shape === 'square' ? 58 : 40;
        const totalConnectionLength = bottomCapFittingLengthNum ?? ((isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba3 : (isFlange ? 100 : getFittingLength(bottomCapFitting, null)));
        const capLength = bottomEllipticalCapLength; // Длина эллиптической заглушки
        // Для накидных гаек и шайб с резьбой: вылет от заглушки, не вычитаем capLength
        const intermediatePipeLength = (isNakGaika || isShaibaNarRezba) 
          ? Math.max(0, totalConnectionLength - bortshaibaLength)
          : Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
        
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          const intermediatePipeId = `cyl-bottom-intermediate-${Date.now()}`;
          // eslint-disable-next-line no-unused-vars
          bottomIntermediatePipeId = intermediatePipeId; // Сохраняем ID для размерных линий
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3
          const intThicknessBottomElliptical = (isNakGaika || isShaibaNarRezba) ? 'x3' : determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating);
          cylinders.push({
            id: intermediatePipeId,
            x: startX - pipeDiameterForFitting / 2,
            y: capEndY,
            width: pipeDiameterForFitting,
            height: intermediatePipeLength,
            pipeSpec: createPipeSpec(pipeDiameterForFitting, intThicknessBottomElliptical, coating),
            insertionDepth: 0,
          });
          pipeEndY = capEndY + intermediatePipeLength;
        }
      }
      
      // Бортшайба/шайба
      let bottomBortshaibaId = null; // Сохраняем ID бортшайбы
      if (needsWasher && shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          const bortshaibaId = `fitting-bottom-shaiba-${Date.now()}`;
          bottomBortshaibaId = bortshaibaId; // Сохраняем ID
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0,
            y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: startX,
            groupY: pipeEndY,
            rotation: 180,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
        }
      }
      
      // Шайба с наружной резьбой (сама шайба = фитинг)
      if (isShaibaNarRezba) {
        // Логика для "Измененное значение" - шайбы с резьбой
        let dimensionOverride = {};
        const shaibaDefaultLengthBottom3 = shape === 'square' ? 58 : 40;
        if (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === shaibaDefaultLengthBottom3) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: bottomFittingData.length,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: bottomFittingData.length,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else if (isNippel || isMufta) {
        // Ниппель/муфта с эллиптической заглушкой - просто размещаем фитинг на конце промежуточной трубы
        const defaultLength = bottomFittingData.length;
        const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, defaultLength);
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: pipeEndY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        bottomFittingEndObjectId = bottomFittingId;
        bottomFittingEndObjectType = 'fitting';
      } else {
        // Фланец/гайка надет на трубку
        const fLength = bottomFittingData.length;
        const fittingY = pipeEndY - fLength;
        
        // Логика для "Измененное значение" - накидные гайки
        let dimensionOverride = {};
        const nakGaikaDefaultLengthBottom3 = shape === 'square' ? 58 : 40;
        if (isNakGaika && (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === nakGaikaDefaultLengthBottom3)) {
          dimensionOverride = {
            overrideEnabled: false,
            overrideText: shape === 'square' ? '54' : '32',
            bortshaibaId: bottomBortshaibaId,
          };
        }
        
        fittings.push({
          id: bottomFittingId,
          key: bottomCapFitting,
          itemKey: bottomCapFitting,
          x: 0,
          y: 0,
          width: bottomFittingData.diameter,
          height: fLength,
          groupX: startX,
          groupY: fittingY,
          rotation: 180,
          offsetX: bottomFittingData.diameter / 2,
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        // Для накидной гайки конечный объект - это бортшайба (если есть), иначе - сама гайка
        if (bottomBortshaibaId) {
          bottomFittingEndObjectId = bottomBortshaibaId;
          bottomFittingEndObjectType = 'fitting';
        } else {
          bottomFittingEndObjectId = bottomFittingId;
          bottomFittingEndObjectType = 'fitting';
        }
      }
    }
    // === ЛОГИКА 4: Обычный фитинг с заглушкой (ниппель/муфта) ===
    // Аналогично распределительному коллектору: фитинг размещается от края заглушки
    else {
      const defaultLength = bottomFittingData.length;
      const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, defaultLength);
      
      // Начальная точка подключения - от края заглушки (если есть)
      // Для эллиптической заглушки: она создана в секции 3 и размещена от (mainPipeTopY + actualMainPipeHeight) вниз на bottomEllipticalCapLength
      // Поэтому фитинг должен начинаться от (capBottomY + bottomEllipticalCapLength)
      let connectionStartY = capBottomY;
      if (needsEllipticalCap) {
        // Эллиптическая заглушка выступает наружу - фитинг от её конца
        connectionStartY = capBottomY + bottomEllipticalCapLength;
      }
      // Прямая заглушка входит в размер гребёнки - фитинг от capBottomY
      
      fittings.push({
        id: bottomFittingId,
        key: bottomCapFitting,
        itemKey: bottomCapFitting,
        x: 0,
        y: 0,
        width: bottomFittingData.diameter,
        height: fLength,
        groupX: startX,
        groupY: connectionStartY,
        rotation: 180,
        offsetX: bottomFittingData.diameter / 2,
        offsetY: fLength,
        // IGES: торцевые подключения НЕ создают отверстия
        igesCreateHole: false,
      });
    }
  }

  // 6. Левые подключения (горизонтальные, направлены влево)
  // Логика аналогична контурам в FloorCollectorEditor, но повёрнута на -90°
  const leftFittingIds = [];
  leftConnections.forEach((conn, index) => {
    const fittingData = fittingsData[conn.fittingId];
    if (!fittingData) return;

    // Вычисляем реальную позицию
    let actualOffset;
    if (params.connectionsPlacementMode === 'spacing') {
      // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
      actualOffset = params.leftConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов: используем offsetFromTop и offsetDirection
      actualOffset = getConnectionPosition(
        conn.offsetFromTop, 
        conn.offsetDirection, 
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }

    // Отступ считается от начала общей высоты (с учётом верхней бортшайбы и прямой заглушки)
    const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
    const connectionX = startX - mainPipeSize / 2; // Левый край основной трубы
    
    // Определяем тип фитинга
    const isFlange = conn.fittingId.startsWith('Flanec_');
    const isFreeFlange = isFlange && !conn.fittingId.endsWith('_welded');
    const isNakGaika = conn.fittingId.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = conn.fittingId.startsWith('Shaib_Nar_Rezba_');
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting = fittingData.diameter;
    if (isFlange) {
      const dnMatch = conn.fittingId.match(/Flanec_(\d+)/);
      if (dnMatch) {
        const dn = parseInt(dnMatch[1]);
        const DN_TO_PIPE_DIAMETER = {
          25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
          80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
          200: 219.1, 250: 273, 300: 323.9, 350: 355.6,
        };
        pipeDiameterForFitting = DN_TO_PIPE_DIAMETER[dn] || fittingData.diameter;
      }
    } else if (isNakGaika) {
      const sizeMatch = conn.fittingId.match(/Nak_Gaika_(.+)/);
      if (sizeMatch) {
        const NAK_GAIKA_TO_PIPE_DIAMETER = {
          '1': 21.3, '1_1.4': 32, '1_1.2': 32, '2': 42.4,
        };
        pipeDiameterForFitting = NAK_GAIKA_TO_PIPE_DIAMETER[sizeMatch[1]] || fittingData.diameter;
      }
    } else if (isShaibaNarRezba) {
      const sizeMatch = conn.fittingId.match(/Shaib_Nar_Rezba_(.+)/);
      if (sizeMatch) {
        const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
          '1': 26.9, '1_1.4': 32, '1_1.2': 32, '2': 42.4,
        };
        pipeDiameterForFitting = SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[sizeMatch[1]] || fittingData.diameter;
      }
    }
    
    // Используем дефолтные значения: для квадратных профилей - 58 для накидных гаек/шайб, для круглых - 40
    let defaultLength;
    if (isNakGaika || isShaibaNarRezba) {
      defaultLength = shape === 'square' ? 58 : 40;
    } else if (isFlange) {
      defaultLength = 100;
    } else {
      defaultLength = null; // Для остальных - из каталога
    }
    const fittingLength = conn.fittingLength ?? (defaultLength !== null ? defaultLength : getFittingLength(conn.fittingId, null));
    
    if (isFlange) {
      // === ФЛАНЕЦ НА ПОДКЛЮЧЕНИИ ===
      // Свободный: трубка → бортшайба → фланец (надет на трубку)
      // Приварной: трубка → фланец (надет на трубку, без бортшайбы)
      // Вылет = длина трубки (фланец надевается сверху)
      const dnMatch = conn.fittingId.match(/Flanec_(\d+)/);
      const dn = dnMatch ? parseInt(dnMatch[1]) : 0;
      const flangeLength = fittingData.length;
      const flangeDiameter = fittingData.diameter;
      
      // Бортшайба для свободных фланцев
      let bortshaibaLength = 0;
      if (isFreeFlange) {
        const bortshaibaKey = `Bortshaiba_${dn}`;
        const bortshaibaInfo = fittingsData[bortshaibaKey];
        if (bortshaibaInfo) {
          bortshaibaLength = bortshaibaInfo.length || 10;
        }
      }
      
      // Длина трубки:
      // Для свободного: вылет - бортшайба (бортшайба на конце, общий вылет = трубка + бортшайба)
      // Для приварного: вылет (фланец надет на трубку)
      const actualPipeLength = isFreeFlange
        ? Math.max(0, fittingLength - bortshaibaLength)
        : fittingLength;
      
      let currentX = connectionX; // Начинаем от края основной трубы
      
      // 1. Добавляем трубку (горизонтальная, влево от разделителя)
      const circuitPipeId = `cyl-left-flange-${index}-${Date.now()}`;
      if (actualPipeLength > 0) {
        cylinders.push({
          id: circuitPipeId,
          x: connectionX - actualPipeLength,
          y: connectionY - pipeDiameterForFitting / 2,
          width: actualPipeLength,
          height: pipeDiameterForFitting,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating), coating),
          // Глубина вставки патрубка фланца в основную трубу (для производственной длины)
          insertionDepth: flangePipeInsertion(mainPipeDiameter, pipeDiameterForFitting, shape),
        });
        currentX = connectionX - actualPipeLength;
      }
      
      // 2. Добавляем бортшайбу (для свободных фланцев)
      let bortshaibaId = null;
      if (isFreeFlange) {
        const bortshaibaKey = `Bortshaiba_${dn}`;
        const bortshaibaInfo = fittingsData[bortshaibaKey];
        if (bortshaibaInfo) {
          bortshaibaId = `fitting-left-bortshaiba-${index}-${Date.now()}`;
          // Бортшайба на левом конце трубки (rotation = -90, смотрит влево)
          fittings.push({
            id: bortshaibaId,
            key: bortshaibaKey,
            itemKey: bortshaibaKey,
            x: 0,
            y: 0,
            width: bortshaibaInfo.diameter,
            height: bortshaibaLength,
            snapPoints: bortshaibaInfo.snapPoints,
            correctionOffset: 3,
            useOriginalSize: true,
            groupX: currentX,
            groupY: connectionY,
            rotation: -90,
            offsetX: bortshaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
            igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
            igesCreateHole: true, // IGES: создаёт отверстие
            holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
            igesHoleDirection: 'left', // IGES: левое подключение
          });
          currentX = currentX - bortshaibaLength;
        }
      }
      
      // 3. Добавляем фланец (надет на трубку)
      const leftFittingId = `fitting-left-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - бортшайба (для свободных) или труба (для приварных)
      const outerElementId = isFreeFlange && bortshaibaId ? bortshaibaId : circuitPipeId;
      const outerElementType = isFreeFlange && bortshaibaId ? 'fitting' : 'cylinder';
      leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId, outerElementType });
      
      // Фланец надет на трубку, его "верх" (внешний край) = левому краю трубки
      // Для свободного: бортшайба на конце трубки, фланец перед ней
      // Для приварного: фланец на конце трубки (приварен)
      const flangeX = connectionX - actualPipeLength + flangeLength;
      
      fittings.push({
        id: leftFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: flangeDiameter,
        height: flangeLength,
        groupX: flangeX,
        groupY: connectionY,
        rotation: -90,
        offsetX: flangeDiameter / 2,
        offsetY: flangeLength,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: !isFreeFlange, // IGES: для приварных создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: 'left', // IGES: левое подключение
      });
    } else if (isNakGaika) {
      // === НАКИДНАЯ ГАЙКА НА ПОДКЛЮЧЕНИИ ===
      // Трубка → шайба накидной гайки → накидная гайка (надета на трубку)
      const sizeMatch = conn.fittingId.match(/Nak_Gaika_(.+)/);
      const size = sizeMatch ? sizeMatch[1] : '';
      const nakGaikaLength = fittingData.length;
      const nakGaikaDiameter = fittingData.diameter;
      
      // Шайба накидной гайки
      const shaibaKey = `Shaib_Gaika_${size}`;
      const shaibaInfo = fittingsData[shaibaKey];
      const shaibaLength = shaibaInfo?.length || 10;
      
      const nakMeasureLeft = nakShaibaPipeMeasure(fittingLength, shaibaLength, shape, false);
      const actualPipeLength = nakMeasureLeft.outside;
      
      let currentX = connectionX;
      
      // 1. Добавляем трубку
      const circuitPipeId = `cyl-left-nakgaika-${index}-${Date.now()}`;
      if (actualPipeLength > 0) {
        // Для накидных гаек: фиксированная толщина x3
        const nakGaikaThicknessLeft = 'x3';
        cylinders.push({
          id: circuitPipeId,
          x: connectionX - actualPipeLength,
          y: connectionY - pipeDiameterForFitting / 2,
          width: actualPipeLength,
          height: pipeDiameterForFitting,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, nakGaikaThicknessLeft, coating),
          insertionDepth: nakMeasureLeft.insertion,
        });
        currentX = connectionX - actualPipeLength;
      }
      
      // 2. Добавляем шайбу накидной гайки
      let shaibaId = null;
      if (shaibaInfo) {
        shaibaId = `fitting-left-shaiba-${index}-${Date.now()}`;
        // Шайба на левом конце трубки (rotation = -90, смотрит влево)
        fittings.push({
          id: shaibaId,
          key: shaibaKey,
          itemKey: shaibaKey,
          x: 0,
          y: 0,
          width: shaibaInfo.diameter,
          height: shaibaLength,
          groupX: currentX,
          groupY: connectionY,
          rotation: -90,
          offsetX: shaibaInfo.diameter / 2,
          offsetY: shaibaLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'left', // IGES: левое подключение
        });
        currentX = currentX - shaibaLength;
      }
      
      // 3. Добавляем накидную гайку (надета на трубку)
      const leftFittingId = `fitting-left-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - шайба накидной гайки
      const outerElementId = shaibaId || circuitPipeId;
      const outerElementType = shaibaId ? 'fitting' : 'cylinder';
      leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId, outerElementType });
      
      // Накидная гайка надета на трубку
      const nakGaikaX = connectionX - actualPipeLength + nakGaikaLength;
      
      // Логика для "Измененное значение" - накидные гайки
      let dimensionOverride = {};
      const defaultNakGaikaLength = shape === 'square' ? 58 : 40;
      if (conn.fittingLength === null || conn.fittingLength === undefined || conn.fittingLength === defaultNakGaikaLength) {
        dimensionOverride = {
          overrideEnabled: true,
          overrideText: shape === 'square' ? '54' : '32',
          bortshaibaId: shaibaId,
        };
      }
      
      fittings.push({
        id: leftFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: nakGaikaDiameter,
        height: nakGaikaLength,
        groupX: nakGaikaX,
        groupY: connectionY,
        rotation: -90,
        offsetX: nakGaikaDiameter / 2,
        offsetY: nakGaikaLength,
        ...dimensionOverride,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: false, // IGES: отверстие создано шайбой
        holeDiameter: nakGaikaDiameter, // IGES: для справки
        igesHoleDirection: 'left', // IGES: левое подключение
      });
    } else if (isShaibaNarRezba) {
      // === ШАЙБА С НАРУЖНОЙ РЕЗЬБОЙ НА ПОДКЛЮЧЕНИИ ===
      // Трубка → шайба с наружной резьбой (шайба идет ПОСЛЕ трубы, как бортшайба)
      const shaibaLength = fittingData.length;
      const shaibaDiameter = fittingData.diameter;
      
      const shaibaMeasureLeft = nakShaibaPipeMeasure(fittingLength, shaibaLength, shape, true);
      const actualPipeLength = shaibaMeasureLeft.outside;
      
      let currentX = connectionX;
      
      // 1. Добавляем трубку
      const circuitPipeId = `cyl-left-shaiba-${index}-${Date.now()}`;
      if (actualPipeLength > 0) {
        // Для шайб с резьбой: фиксированная толщина x3
        const shaibaThicknessLeft = 'x3';
        cylinders.push({
          id: circuitPipeId,
          x: connectionX - actualPipeLength,
          y: connectionY - pipeDiameterForFitting / 2,
          width: actualPipeLength,
          height: pipeDiameterForFitting,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, shaibaThicknessLeft, coating),
          insertionDepth: shaibaMeasureLeft.insertion,
        });
        currentX = connectionX - actualPipeLength;
      }
      
      // 2. Добавляем шайбу с наружной резьбой (на левом конце трубки)
      const leftFittingId = `fitting-left-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - сама шайба
      leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId: leftFittingId, outerElementType: 'fitting' });
      
      // Логика для "Измененное значение" - шайбы с резьбой
      let dimensionOverride = {};
      const defaultShaibaLength = shape === 'square' ? 58 : 40;
      if (conn.fittingLength === null || conn.fittingLength === undefined || conn.fittingLength === defaultShaibaLength) {
        dimensionOverride = {
          overrideEnabled: true,
          overrideText: shape === 'square' ? '54' : '32',
        };
      }
      
      fittings.push({
        id: leftFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: shaibaDiameter,
        height: shaibaLength,
        groupX: currentX,
        groupY: connectionY,
        rotation: -90,
        offsetX: shaibaDiameter / 2,
        offsetY: shaibaLength,
        ...dimensionOverride,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: true, // IGES: создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: 'left', // IGES: левое подключение
      });
    } else {
      // Обычный фитинг (ниппель, муфта без промежуточной трубы)
      const leftFittingId = `fitting-left-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - сам фитинг
      leftFittingIds.push({ id: leftFittingId, offsetFromTop: actualOffset, outerElementId: leftFittingId, outerElementType: 'fitting' });
      
      // Логика для "Измененное значение"
      const isNippel = conn.fittingId.startsWith('Nippel_');
      const isMufta = conn.fittingId.startsWith('Mufta_');
      let dimensionOverride = {};
      let actualFittingLength = fittingLength;

      if (isNippel || isMufta) {
        const resolved = resolveNippelMuftaFitting({
          fittingId: conn.fittingId,
          fittingLength: conn.fittingLength ?? null,
          pipeDiameter: mainPipeDiameter,
          catalogLength: fittingData.length,
          shape,
        });
        actualFittingLength = resolved.length;
        dimensionOverride = resolved.dimensionOverride;
      }
      
      fittings.push({
        id: leftFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: fittingData.diameter,
        height: actualFittingLength,
        groupX: connectionX,
        groupY: connectionY,
        rotation: -90,
        offsetX: fittingData.diameter / 2,
        offsetY: actualFittingLength,
        ...dimensionOverride,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: true, // IGES: создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: 'left', // IGES: левое подключение
      });
    }
  });

  // 7. Правые подключения (горизонтальные, направлены вправо)
  // Логика аналогична контурам в FloorCollectorEditor, но повёрнута на 90°
  const rightFittingIds = [];
  rightConnections.forEach((conn, index) => {
    const fittingData = fittingsData[conn.fittingId];
    if (!fittingData) return;

    // Вычисляем реальную позицию
    let actualOffset;
    if (params.connectionsPlacementMode === 'spacing') {
      // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
      actualOffset = params.rightConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(rightConnections[i].spacingToNext) || params.rightConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов: используем offsetFromTop и offsetDirection
      actualOffset = getConnectionPosition(
        conn.offsetFromTop, 
        conn.offsetDirection, 
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }

    // Отступ считается от начала общей высоты (с учётом верхней бортшайбы и прямой заглушки)
    const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
    const connectionX = startX + mainPipeSize / 2; // Правый край основной трубы
    
    // Определяем тип фитинга
    const isFlange = conn.fittingId.startsWith('Flanec_');
    const isFreeFlange = isFlange && !conn.fittingId.endsWith('_welded');
    const isNakGaika = conn.fittingId.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = conn.fittingId.startsWith('Shaib_Nar_Rezba_');
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting = fittingData.diameter;
    if (isFlange) {
      const dnMatch = conn.fittingId.match(/Flanec_(\d+)/);
      if (dnMatch) {
        const dn = parseInt(dnMatch[1]);
        const DN_TO_PIPE_DIAMETER = {
          25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
          80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
          200: 219.1, 250: 273, 300: 323.9, 350: 355.6,
        };
        pipeDiameterForFitting = DN_TO_PIPE_DIAMETER[dn] || fittingData.diameter;
      }
    } else if (isNakGaika) {
      const sizeMatch = conn.fittingId.match(/Nak_Gaika_(.+)/);
      if (sizeMatch) {
        const NAK_GAIKA_TO_PIPE_DIAMETER = {
          '1': 21.3, '1_1.4': 32, '1_1.2': 32, '2': 42.4,
        };
        pipeDiameterForFitting = NAK_GAIKA_TO_PIPE_DIAMETER[sizeMatch[1]] || fittingData.diameter;
      }
    } else if (isShaibaNarRezba) {
      const sizeMatch = conn.fittingId.match(/Shaib_Nar_Rezba_(.+)/);
      if (sizeMatch) {
        const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
          '1': 26.9, '1_1.4': 32, '1_1.2': 32, '2': 42.4,
        };
        pipeDiameterForFitting = SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[sizeMatch[1]] || fittingData.diameter;
      }
    }
    
    // Используем дефолтные значения: для квадратных профилей - 58 для накидных гаек/шайб, для круглых - 40
    let defaultLength;
    if (isNakGaika || isShaibaNarRezba) {
      defaultLength = shape === 'square' ? 58 : 40;
    } else if (isFlange) {
      defaultLength = 100;
    } else {
      defaultLength = null; // Для остальных - из каталога
    }
    const fittingLength = conn.fittingLength ?? (defaultLength !== null ? defaultLength : getFittingLength(conn.fittingId, null));
    
    if (isFlange) {
      // === ФЛАНЕЦ НА ПОДКЛЮЧЕНИИ ===
      // Свободный: трубка → бортшайба → фланец (надет на трубку)
      // Приварной: трубка → фланец (надет на трубку, без бортшайбы)
      // Вылет = длина трубки (фланец надевается сверху)
      const dnMatch = conn.fittingId.match(/Flanec_(\d+)/);
      const dn = dnMatch ? parseInt(dnMatch[1]) : 0;
      const flangeLength = fittingData.length;
      const flangeDiameter = fittingData.diameter;
      
      // Бортшайба для свободных фланцев
      let bortshaibaLength = 0;
      if (isFreeFlange) {
        const bortshaibaKey = `Bortshaiba_${dn}`;
        const bortshaibaInfo = fittingsData[bortshaibaKey];
        if (bortshaibaInfo) {
          bortshaibaLength = bortshaibaInfo.length || 10;
        }
      }
      
      // Длина трубки:
      // Для свободного: вылет - бортшайба (бортшайба на конце, общий вылет = трубка + бортшайба)
      // Для приварного: вылет (фланец надет на трубку)
      const actualPipeLength = isFreeFlange
        ? Math.max(0, fittingLength - bortshaibaLength)
        : fittingLength;
      
      let currentX = connectionX; // Начинаем от края основной трубы
      
      // 1. Добавляем трубку (горизонтальная, вправо от разделителя)
      const circuitPipeId = `cyl-right-flange-${index}-${Date.now()}`;
      if (actualPipeLength > 0) {
        cylinders.push({
          id: circuitPipeId,
          x: connectionX,
          y: connectionY - pipeDiameterForFitting / 2,
          width: actualPipeLength,
          height: pipeDiameterForFitting,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, determineThickness(mainPipeDiameter, pipeDiameterForFitting, coating), coating),
          // Глубина вставки патрубка фланца в основную трубу (для производственной длины)
          insertionDepth: flangePipeInsertion(mainPipeDiameter, pipeDiameterForFitting, shape),
        });
        currentX = connectionX + actualPipeLength;
      }
      
      // 2. Добавляем бортшайбу (для свободных фланцев)
      let bortshaibaId = null;
      if (isFreeFlange) {
        const bortshaibaKey = `Bortshaiba_${dn}`;
        const bortshaibaInfo = fittingsData[bortshaibaKey];
        if (bortshaibaInfo) {
          bortshaibaId = `fitting-right-bortshaiba-${index}-${Date.now()}`;
          // Бортшайба на правом конце трубки (rotation = 90, смотрит вправо)
          fittings.push({
            id: bortshaibaId,
            key: bortshaibaKey,
            itemKey: bortshaibaKey,
            x: 0,
            y: 0,
            width: bortshaibaInfo.diameter,
            height: bortshaibaLength,
            snapPoints: bortshaibaInfo.snapPoints,
            correctionOffset: 3,
            useOriginalSize: true,
            groupX: currentX,
            groupY: connectionY,
            rotation: 90,
            offsetX: bortshaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
            igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
            igesCreateHole: true, // IGES: создаёт отверстие
            holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
            igesHoleDirection: 'right', // IGES: правое подключение
          });
          currentX = currentX + bortshaibaLength;
        }
      }
      
      // 3. Добавляем фланец (надет на трубку)
      const rightFittingId = `fitting-right-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - бортшайба (для свободных) или труба (для приварных)
      const outerElementId = isFreeFlange && bortshaibaId ? bortshaibaId : circuitPipeId;
      const outerElementType = isFreeFlange && bortshaibaId ? 'fitting' : 'cylinder';
      rightFittingIds.push({ id: rightFittingId, offsetFromTop: actualOffset, outerElementId, outerElementType });
      
      // Фланец надет на трубку, его "верх" (внешний край) = правому краю трубки
      // Для свободного: бортшайба на конце трубки, фланец перед ней
      // Для приварного: фланец на конце трубки (приварен)
      const flangeX = connectionX + actualPipeLength - flangeLength;
      
      fittings.push({
        id: rightFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: flangeDiameter,
        height: flangeLength,
        groupX: flangeX,
        groupY: connectionY,
        rotation: 90,
        offsetX: flangeDiameter / 2,
        offsetY: flangeLength,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: !isFreeFlange, // IGES: для приварных создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: 'right', // IGES: правое подключение
      });
    } else if (isNakGaika) {
      // === НАКИДНАЯ ГАЙКА НА ПОДКЛЮЧЕНИИ ===
      // Трубка → шайба накидной гайки → накидная гайка (надета на трубку)
      const sizeMatch = conn.fittingId.match(/Nak_Gaika_(.+)/);
      const size = sizeMatch ? sizeMatch[1] : '';
      const nakGaikaLength = fittingData.length;
      const nakGaikaDiameter = fittingData.diameter;
      
      // Шайба накидной гайки
      const shaibaKey = `Shaib_Gaika_${size}`;
      const shaibaInfo = fittingsData[shaibaKey];
      const shaibaLength = shaibaInfo?.length || 10;
      
      const nakMeasureRight = nakShaibaPipeMeasure(fittingLength, shaibaLength, shape, false);
      const actualPipeLength = nakMeasureRight.outside;
      
      let currentX = connectionX;
      
      // 1. Добавляем трубку
      const circuitPipeId = `cyl-right-nakgaika-${index}-${Date.now()}`;
      if (actualPipeLength > 0) {
        // Для накидных гаек: фиксированная толщина x3
        cylinders.push({
          id: circuitPipeId,
          x: connectionX,
          y: connectionY - pipeDiameterForFitting / 2,
          width: actualPipeLength,
          height: pipeDiameterForFitting,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, 'x3', coating),
          insertionDepth: nakMeasureRight.insertion,
        });
        currentX = connectionX + actualPipeLength;
      }
      
      // 2. Добавляем шайбу накидной гайки
      let shaibaId = null;
      if (shaibaInfo) {
        shaibaId = `fitting-right-shaiba-${index}-${Date.now()}`;
        // Шайба на правом конце трубки (rotation = 90, смотрит вправо)
        fittings.push({
          id: shaibaId,
          key: shaibaKey,
          itemKey: shaibaKey,
          x: 0,
          y: 0,
          width: shaibaInfo.diameter,
          height: shaibaLength,
          groupX: currentX,
          groupY: connectionY,
          rotation: 90,
          offsetX: shaibaInfo.diameter / 2,
          offsetY: shaibaLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'right', // IGES: правое подключение
        });
        currentX = currentX + shaibaLength;
      }
      
      // 3. Добавляем накидную гайку (надета на трубку)
      const rightFittingId = `fitting-right-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - шайба накидной гайки
      const outerElementId = shaibaId || circuitPipeId;
      const outerElementType = shaibaId ? 'fitting' : 'cylinder';
      rightFittingIds.push({ id: rightFittingId, offsetFromTop: actualOffset, outerElementId, outerElementType });
      
      // Накидная гайка надета на трубку
      const nakGaikaX = connectionX + actualPipeLength - nakGaikaLength;
      
      // Логика для "Измененное значение" - накидные гайки
      let dimensionOverride = {};
      const defaultNakGaikaLength = shape === 'square' ? 58 : 40;
      if (conn.fittingLength === null || conn.fittingLength === undefined || conn.fittingLength === defaultNakGaikaLength) {
        dimensionOverride = {
          overrideEnabled: true,
          overrideText: shape === 'square' ? '54' : '32',
          bortshaibaId: shaibaId,
        };
      }
      
      fittings.push({
        id: rightFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: nakGaikaDiameter,
        height: nakGaikaLength,
        groupX: nakGaikaX,
        groupY: connectionY,
        rotation: 90,
        offsetX: nakGaikaDiameter / 2,
        offsetY: nakGaikaLength,
        ...dimensionOverride,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: false, // IGES: отверстие создано шайбой
        holeDiameter: nakGaikaDiameter, // IGES: для справки
        igesHoleDirection: 'right', // IGES: правое подключение
      });
    } else if (isShaibaNarRezba) {
      // === ШАЙБА С НАРУЖНОЙ РЕЗЬБОЙ НА ПОДКЛЮЧЕНИИ ===
      // Трубка → шайба с наружной резьбой (шайба идет ПОСЛЕ трубы, как бортшайба)
      const shaibaLength = fittingData.length;
      const shaibaDiameter = fittingData.diameter;
      
      const shaibaMeasureRight = nakShaibaPipeMeasure(fittingLength, shaibaLength, shape, true);
      const actualPipeLength = shaibaMeasureRight.outside;
      
      let currentX = connectionX;
      
      // 1. Добавляем трубку
      const circuitPipeId = `cyl-right-shaiba-${index}-${Date.now()}`;
      if (actualPipeLength > 0) {
        // Для шайб с резьбой: фиксированная толщина x3
        cylinders.push({
          id: circuitPipeId,
          x: connectionX,
          y: connectionY - pipeDiameterForFitting / 2,
          width: actualPipeLength,
          height: pipeDiameterForFitting,
          pipeSpec: createPipeSpec(pipeDiameterForFitting, 'x3', coating),
          insertionDepth: shaibaMeasureRight.insertion,
        });
        currentX = connectionX + actualPipeLength;
      }
      
      // 2. Добавляем шайбу с наружной резьбой (на правом конце трубки)
      const rightFittingId = `fitting-right-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - сама шайба
      rightFittingIds.push({ id: rightFittingId, offsetFromTop: actualOffset, outerElementId: rightFittingId, outerElementType: 'fitting' });
      
      // Логика для "Измененное значение" - шайбы с резьбой
      let dimensionOverride = {};
      const defaultShaibaLength = shape === 'square' ? 58 : 40;
      if (conn.fittingLength === null || conn.fittingLength === undefined || conn.fittingLength === defaultShaibaLength) {
        dimensionOverride = {
          overrideEnabled: true,
          overrideText: shape === 'square' ? '54' : '32',
        };
      }
      
      fittings.push({
        id: rightFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: shaibaDiameter,
        height: shaibaLength,
        groupX: currentX,
        groupY: connectionY,
        rotation: 90,
        offsetX: shaibaDiameter / 2,
        offsetY: shaibaLength,
        ...dimensionOverride,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: true, // IGES: создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: 'right', // IGES: правое подключение
      });
    } else {
      // Обычный фитинг (ниппель, муфта без промежуточной трубы)
      const rightFittingId = `fitting-right-${index}-${Date.now()}`;
      // Для размерной линии: крайний элемент - сам фитинг
      rightFittingIds.push({ id: rightFittingId, offsetFromTop: actualOffset, outerElementId: rightFittingId, outerElementType: 'fitting' });
      
      // Логика для "Измененное значение"
      const isNippel = conn.fittingId.startsWith('Nippel_');
      const isMufta = conn.fittingId.startsWith('Mufta_');
      let dimensionOverride = {};
      let actualFittingLength = fittingLength;

      if (isNippel || isMufta) {
        const resolved = resolveNippelMuftaFitting({
          fittingId: conn.fittingId,
          fittingLength: conn.fittingLength ?? null,
          pipeDiameter: mainPipeDiameter,
          catalogLength: fittingData.length,
          shape,
        });
        actualFittingLength = resolved.length;
        dimensionOverride = resolved.dimensionOverride;
      }
      
      fittings.push({
        id: rightFittingId,
        key: conn.fittingId,
        itemKey: conn.fittingId,
        x: 0,
        y: 0,
        width: fittingData.diameter,
        height: actualFittingLength,
        groupX: connectionX,
        groupY: connectionY,
        rotation: 90,
        offsetX: fittingData.diameter / 2,
        offsetY: actualFittingLength,
        ...dimensionOverride,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        igesCreateHole: true, // IGES: создаёт отверстие
        holeDiameter: getHoleDiameterForPipe(pipeDiameterForFitting, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
        igesHoleDirection: 'right', // IGES: правое подключение
      });
    }
  });

  // 8. Размерные линии
  
  // Размерная линия для общей высоты
  // Учитываем бортшайбы и прямые заглушки, которые входят в общую высоту
  // totalHeightTopY - верхняя точка, от которой считается общая высота
  // Если есть бортшайба - от неё, если нет - от верха трубы (или заглушки, если она есть)
  const totalHeightTopY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
  const totalHeightBottomY = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
  
  // Определяем объекты для привязки размерной линии
  // Приоритет: прямая заглушка > бортшайба > основная труба
  const topDimElement = topStraightCapIdForDimLine
    ? { objectId: topStraightCapIdForDimLine, objectType: 'fitting', x: startX - mainPipeSize / 2, y: totalHeightTopY, type: 'top' }
    : topBortshaibaIdForDimLine
    ? { objectId: topBortshaibaIdForDimLine, objectType: 'fitting', x: startX - mainPipeSize / 2, y: totalHeightTopY, type: 'top' }
    : { objectId: mainPipeId, objectType: 'cylinder', x: startX - mainPipeSize / 2, y: mainPipeTopY, type: 'top' };
  
  const bottomDimElement = bottomStraightCapIdForDimLine
    ? { objectId: bottomStraightCapIdForDimLine, objectType: 'fitting', x: startX - mainPipeSize / 2, y: totalHeightBottomY, type: 'top' }
    : bottomBortshaibaIdForDimLine
    ? { objectId: bottomBortshaibaIdForDimLine, objectType: 'fitting', x: startX - mainPipeSize / 2, y: totalHeightBottomY, type: 'top' } // 'top' для фитингов - привязка к началу
    : { objectId: mainPipeId, objectType: 'cylinder', x: startX - mainPipeSize / 2, y: mainPipeTopY + actualMainPipeHeight, type: 'bottom' };
  
  dimensionLines.push({
    isHorizontal: false,
    lockOrientation: true,
    angle: 0, // Вертикальная линия
    x1: startX + mainPipeSize / 2 + 240,
    y1: totalHeightTopY,
    x2: startX + mainPipeSize / 2 + 240,
    y2: totalHeightBottomY,
    position: { x: startX + mainPipeSize / 2 + 240 + 30, y: (totalHeightTopY + totalHeightBottomY) / 2 },
    value: String(Math.round(separatorHeight)),
    rawValue: separatorHeight,
    extensionLines: [],
    precision: 0,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { ...topDimElement, x: startX + mainPipeSize / 2 + 240 },
      { ...bottomDimElement, x: startX + mainPipeSize / 2 + 240 },
    ],
  });

  // Размерная линия диаметра основной трубы (горизонтальная, сверху, выше верхнего подключения)
  // Вычисляем Y позицию: выше верхнего фитинга, заглушки и перехода
  let diameterLineY = mainPipeTopY - 60; // По умолчанию над основной трубой
  
  // Если есть верхнее подключение, размещаем выше него
  if (params.hasTopConnection && topCapFitting && topFittingData) {
    let topConnectionHeight = 0;
    
    // Высота заглушки/перехода
    if (params.hasTopCap) {
      if (params.topCapElementType === 'reducer' && params.topReducer) {
        const reducerData = fittingsData[params.topReducer];
        if (reducerData) topConnectionHeight += reducerData.length;
      } else if (params.topCapType === 'elliptical') {
        topConnectionHeight += topEllipticalCapLength;
      } else if (params.topCapType === 'straight') {
        topConnectionHeight += topStraightCapLength;
      }
    }
    
    // Высота фитинга
    const fittingLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, topFittingData.length);
    topConnectionHeight += fittingLength;
    
    diameterLineY = mainPipeTopY - topConnectionHeight - 30;
  }
  
  // Создаем размерную линию для основной трубы
  const mainPipeDimensionLine = {
    isHorizontal: true,
    lockOrientation: true,
    angle: 0, // Горизонтальное направление размера
    x1: startX - mainPipeSize / 2,
    y1: diameterLineY,
    x2: startX + mainPipeSize / 2,
    y2: diameterLineY,
    position: { x: startX, y: diameterLineY - 20 },
    value: shape === 'square' ? mainPipeProfile.replace('x', '×') : `⌀${mainPipeDiameter}`,
    rawValue: shape === 'square' ? mainPipeSize : mainPipeDiameter,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: mainPipeId, objectType: 'cylinder', x: startX - mainPipeSize / 2, y: diameterLineY, type: 'left' },
      { objectId: mainPipeId, objectType: 'cylinder', x: startX + mainPipeSize / 2, y: diameterLineY, type: 'right' },
    ],
  };
  
  // Для квадратных труб добавляем измененное значение (профиль вместо размера)
  if (shape === 'square') {
    const profileThickness = mainPipeSpec.thickness || '2';
    mainPipeDimensionLine.overrideEnabled = true;
    mainPipeDimensionLine.overrideText = `${mainPipeProfile.replace('x', '×')} ×${profileThickness}`; // "80×80 ×2"
  } else {
    // Для круглых труб добавляем толщину к диаметру
    const pipeThickness = typeof mainPipeSpec.thickness === 'string' 
      ? mainPipeSpec.thickness.replace('x', '') 
      : mainPipeSpec.thickness || 2; // если число, используем как есть
    mainPipeDimensionLine.overrideEnabled = true;
    mainPipeDimensionLine.overrideText = `${mainPipeDiameter} ×${pipeThickness}`; // "60.3 ×2"
  }
  
  dimensionLines.push(mainPipeDimensionLine);

  // Межосевые расстояния левых подключений
  // Создаём массив с позициями подключений с учётом режима
  const leftFittingsWithPositions = leftConnections.map((conn, index) => {
    let actualOffset;
    if (params.connectionsPlacementMode === 'spacing') {
      // Режим межосевого
      actualOffset = params.leftConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов
      actualOffset = getConnectionPosition(
        conn.offsetFromTop,
        conn.offsetDirection,
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }
    return {
      ...leftFittingIds[index],
      actualOffset,
    };
  });
  
  const sortedLeftFittings = [...leftFittingsWithPositions].sort((a, b) => a.actualOffset - b.actualOffset);
  
  // Для левых подключений показываем:
  // 1. От верха (с учётом бортшайбы и прямой заглушки) до первого подключения
  if (sortedLeftFittings.length > 0) {
    const firstFitting = sortedLeftFittings[0];
    const y1 = totalHeightTopY; // С учётом верхней бортшайбы и заглушки
    const y2 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + firstFitting.actualOffset;
    const distance = firstFitting.actualOffset;
    const dimX = startX - mainPipeSize / 2 - 200; // Сдвигаем влево, чтобы не пересекаться с позициями

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0, // Вертикальная линия
        x1: dimX,
        y1: y1,
        x2: dimX,
        y2: y2,
        position: { x: dimX - 30, y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          topDimElement, // Используем тот же элемент, что и для общей высоты
          { objectId: firstFitting.id, objectType: 'fitting', x: dimX, y: y2, type: 'top' },
        ],
      });
    }
  }
  
  // 2. Между подключениями
  for (let i = 0; i < sortedLeftFittings.length - 1; i++) {
    const fitting1 = sortedLeftFittings[i];
    const fitting2 = sortedLeftFittings[i + 1];
    const y1 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + fitting1.actualOffset;
    const y2 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + fitting2.actualOffset;
    const distance = fitting2.actualOffset - fitting1.actualOffset;
    const dimX = startX - mainPipeSize / 2 - 200; // Сдвигаем влево, чтобы не пересекаться с позициями

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0, // Вертикальная линия
        x1: dimX,
        y1: y1,
        x2: dimX,
        y2: y2,
        position: { x: dimX - 30, y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: fitting1.id, objectType: 'fitting', x: dimX, y: y1, type: 'top' },
          { objectId: fitting2.id, objectType: 'fitting', x: dimX, y: y2, type: 'top' },
        ],
      });
    }
  }
  
  // 3. От последнего подключения до низа (с учётом бортшайбы и прямой заглушки)
  if (sortedLeftFittings.length > 0) {
    const lastFitting = sortedLeftFittings[sortedLeftFittings.length - 1];
    const y1 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + lastFitting.actualOffset;
    const y2 = totalHeightBottomY; // С учётом нижней бортшайбы и заглушки
    const totalHeightFromTop = actualMainPipeHeight + topBortshaibaLengthForHeight + topStraightCapLength + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
    const distance = totalHeightFromTop - lastFitting.actualOffset;
    const dimX = startX - mainPipeSize / 2 - 200; // Сдвигаем влево, чтобы не пересекаться с позициями

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0, // Вертикальная линия
        x1: dimX,
        y1: y1,
        x2: dimX,
        y2: y2,
        position: { x: dimX - 30, y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: lastFitting.id, objectType: 'fitting', x: dimX, y: y1, type: 'top' },
          bottomDimElement, // Используем тот же элемент, что и для общей высоты
        ],
      });
    }
  }

  // Межосевые расстояния правых подключений
  // Создаём массив с позициями подключений с учётом режима
  const rightFittingsWithPositions = rightConnections.map((conn, index) => {
    let actualOffset;
    if (params.connectionsPlacementMode === 'spacing') {
      // Режим межосевого
      actualOffset = params.rightConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(rightConnections[i].spacingToNext) || params.rightConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов
      actualOffset = getConnectionPosition(
        conn.offsetFromTop,
        conn.offsetDirection,
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }
    return {
      ...rightFittingIds[index],
      actualOffset,
    };
  });
  
  const sortedRightFittings = [...rightFittingsWithPositions].sort((a, b) => a.actualOffset - b.actualOffset);
  
  // Проверяем, совпадают ли межосевые расстояния слева и справа
  const leftDistances = [];
  const rightDistances = [];
  
  // Собираем расстояния слева
  if (sortedLeftFittings.length > 0) {
    // От верха до первого
    leftDistances.push(sortedLeftFittings[0].actualOffset);
    // Между подключениями
    for (let i = 0; i < sortedLeftFittings.length - 1; i++) {
      leftDistances.push(sortedLeftFittings[i + 1].actualOffset - sortedLeftFittings[i].actualOffset);
    }
    // От последнего до низа
    const totalHeightFromTop = actualMainPipeHeight + topBortshaibaLengthForHeight + topStraightCapLength + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
    leftDistances.push(totalHeightFromTop - sortedLeftFittings[sortedLeftFittings.length - 1].actualOffset);
  }
  
  // Собираем расстояния справа
  if (sortedRightFittings.length > 0) {
    // От верха до первого
    rightDistances.push(sortedRightFittings[0].actualOffset);
    // Между подключениями
    for (let i = 0; i < sortedRightFittings.length - 1; i++) {
      rightDistances.push(sortedRightFittings[i + 1].actualOffset - sortedRightFittings[i].actualOffset);
    }
    // От последнего до низа
    const totalHeightFromTop = actualMainPipeHeight + topBortshaibaLengthForHeight + topStraightCapLength + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
    rightDistances.push(totalHeightFromTop - sortedRightFittings[sortedRightFittings.length - 1].actualOffset);
  }
  
  // Проверяем совпадение расстояний (с округлением до целых)
  const distancesMatch = leftDistances.length === rightDistances.length && 
    leftDistances.every((dist, i) => Math.round(dist) === Math.round(rightDistances[i]));
  
  // Для правых подключений показываем размеры ТОЛЬКО если они отличаются от левых
  if (!distancesMatch) {
    // 1. От верха (с учётом бортшайбы и прямой заглушки) до первого подключения
  if (sortedRightFittings.length > 0) {
    const firstFitting = sortedRightFittings[0];
    const y1 = totalHeightTopY; // С учётом верхней бортшайбы и заглушки
    const y2 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + firstFitting.actualOffset;
    const distance = firstFitting.actualOffset;
    const dimX = startX + mainPipeSize / 2 + 200; // Сдвигаем вправо, чтобы не пересекаться с позициями

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0, // Вертикальная линия
        x1: dimX,
        y1: y1,
        x2: dimX,
        y2: y2,
        position: { x: dimX + 30, y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { ...topDimElement, x: dimX }, // Используем тот же элемент с другой X координатой
          { objectId: firstFitting.id, objectType: 'fitting', x: dimX, y: y2, type: 'top' },
        ],
      });
    }
  }
  
  // 2. Между подключениями
  for (let i = 0; i < sortedRightFittings.length - 1; i++) {
    const fitting1 = sortedRightFittings[i];
    const fitting2 = sortedRightFittings[i + 1];
    const y1 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + fitting1.actualOffset;
    const y2 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + fitting2.actualOffset;
    const distance = fitting2.actualOffset - fitting1.actualOffset;
    const dimX = startX + mainPipeSize / 2 + 200; // Сдвигаем вправо, чтобы не пересекаться с позициями

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0, // Вертикальная линия
        x1: dimX,
        y1: y1,
        x2: dimX,
        y2: y2,
        position: { x: dimX + 30, y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: fitting1.id, objectType: 'fitting', x: dimX, y: y1, type: 'top' },
          { objectId: fitting2.id, objectType: 'fitting', x: dimX, y: y2, type: 'top' },
        ],
      });
    }
  }
  
  // 3. От последнего подключения до низа (с учётом бортшайбы и прямой заглушки)
  if (sortedRightFittings.length > 0) {
    const lastFitting = sortedRightFittings[sortedRightFittings.length - 1];
    const y1 = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + lastFitting.actualOffset;
    const y2 = totalHeightBottomY; // С учётом нижней бортшайбы и заглушки
    const totalHeightFromTop = actualMainPipeHeight + topBortshaibaLengthForHeight + topStraightCapLength + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
    const distance = totalHeightFromTop - lastFitting.actualOffset;
    const dimX = startX + mainPipeSize / 2 + 200; // Сдвигаем вправо, чтобы не пересекаться с позициями

    if (distance > 0) {
      dimensionLines.push({
        isHorizontal: false,
        lockOrientation: true,
        angle: 0, // Вертикальная линия
        x1: dimX,
        y1: y1,
        x2: dimX,
        y2: y2,
        position: { x: dimX + 30, y: (y1 + y2) / 2 },
        value: String(Math.round(distance)),
        rawValue: distance,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: lastFitting.id, objectType: 'fitting', x: dimX, y: y1, type: 'top' },
          { ...bottomDimElement, x: dimX }, // Используем тот же элемент с другой X координатой
        ],
      });
    }
  }
  } // Конец блока if (!distancesMatch)

  // Середина разделителя для смещения размерных линий и позиций к центру
  const midSeparatorY = mainPipeTopY + actualMainPipeHeight / 2;

  // Проверяем, совпадают ли все фитинги и вылеты у левых подключений
  const leftConnectionsAreSame = leftConnections.length > 1 && leftConnections.every((conn, idx) => {
    if (idx === 0) return true;
    const firstConn = leftConnections[0];
    const firstLength = getFittingLength(firstConn.fittingId, firstConn.fittingLength);
    const currentLength = getFittingLength(conn.fittingId, conn.fittingLength);
    return conn.fittingId === firstConn.fittingId && Math.abs(firstLength - currentLength) < 0.01;
  });

  // Размерные линии для вылетов левых подключений (горизонтальные)
  // Размерные линии для вылетов левых подключений (горизонтальные)
  if (params.connectionsPlacementMode === 'spacing') {
    // Режим межосевого: показываем вылет для каждого уникального типа фитинга
    const processedFittingTypes = new Set();
    
    leftConnections.forEach((conn, index) => {
      const fittingData = fittingsData[conn.fittingId];
      if (!fittingData) return;
      
      // Создаём ключ для идентификации уникального типа фитинга
      const fittingKey = `${conn.fittingId}_${conn.fittingLength || ''}`;
      
      // Пропускаем, если уже обработали такой тип фитинга
      if (processedFittingTypes.has(fittingKey)) return;
      processedFittingTypes.add(fittingKey);
      
      // Вычисляем позицию текущего подключения
      let actualOffset = params.leftConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
        actualOffset += spacing;
      }
      
      const fallbackLength = getFittingLength(conn.fittingId, conn.fittingLength);
      const { geometricLength, dimensionOverride } = resolveSideConnectionOutletDimension({
        fittingId: conn.fittingId,
        connFittingLength: conn.fittingLength,
        pipeDiameter: mainPipeDiameter,
        catalogLength: fittingData.length,
        shape,
        fallbackLength,
      });
      // Отступ считается от начала общей высоты (с учётом верхней бортшайбы и прямой заглушки)
      const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
      const connectionX = startX - mainPipeSize / 2;
      
      // Внешний край (левый конец) фитинга/подключения
      const outerEdgeX = connectionX - geometricLength;
      
      // Получаем информацию о крайнем элементе для привязки
      const leftFittingInfo = leftFittingIds[index];
      const outerElementId = leftFittingInfo?.outerElementId;
      const outerElementType = leftFittingInfo?.outerElementType || 'fitting';
      
      // Размерная линия от края основной трубы до внешнего края подключения
      const dimY = connectionY - 30;
      
      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: connectionX,
        y1: dimY,
        x2: outerEdgeX,
        y2: dimY,
        position: { x: (connectionX + outerEdgeX) / 2, y: dimY },
        value: String(Math.round(geometricLength)),
        rawValue: geometricLength,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'left' },
          { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'left' },
        ],
        ...dimensionOverride,
      });
    });
  } else {
    // Режим отступов: используем старую логику
  leftConnections.forEach((conn, index) => {
    const fittingData = fittingsData[conn.fittingId];
    if (!fittingData) return;
    
    // Если все подключения одинаковые, показываем размер только для первого (самого верхнего)
    if (leftConnectionsAreSame && index !== 0) return;
    
    // Вычисляем реальную позицию с учетом направления отсчета
    const actualOffset = getConnectionPosition(
      conn.offsetFromTop, 
      conn.offsetDirection, 
      actualMainPipeHeight,
      topBortshaibaLengthForHeight,
      topStraightCapLength,
      bottomBortshaibaLengthForHeight,
      bottomStraightCapLength
    );
    
    const fallbackLength = getFittingLength(conn.fittingId, conn.fittingLength);
    const { geometricLength, dimensionOverride } = resolveSideConnectionOutletDimension({
      fittingId: conn.fittingId,
      connFittingLength: conn.fittingLength,
      pipeDiameter: mainPipeDiameter,
      catalogLength: fittingData.length,
      shape,
      fallbackLength,
    });
    // Отступ считается от начала общей высоты (с учётом верхней бортшайбы и прямой заглушки)
    const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
    const connectionX = startX - mainPipeSize / 2;
    
    // Внешний край (левый конец) фитинга/подключения
    const outerEdgeX = connectionX - geometricLength;
    
    // Получаем информацию о крайнем элементе для привязки
    const leftFittingInfo = leftFittingIds[index];
    const outerElementId = leftFittingInfo?.outerElementId;
    const outerElementType = leftFittingInfo?.outerElementType || 'fitting';
    
    // Размерная линия от края основной трубы до внешнего края подключения
    const dimY = connectionY - 30;
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connectionX,
      y1: dimY,
      x2: outerEdgeX,
      y2: dimY,
      position: { x: (connectionX + outerEdgeX) / 2, y: dimY },
      value: String(Math.round(geometricLength)),
      rawValue: geometricLength,
      extensionLines: [],
      precision: 0,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'left' },
        // Привязываем к крайнему элементу подключения (бортшайба, шайба или фитинг)
        // Для фитингов используем type: 'top' (привязка к началу фитинга), для цилиндров - 'left'
        { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'left' },
      ],
      ...dimensionOverride,
    });
  });
  }

  // Проверяем, совпадают ли все фитинги и вылеты у правых подключений
  const rightConnectionsAreSame = rightConnections.length > 1 && rightConnections.every((conn, idx) => {
    if (idx === 0) return true;
    const firstConn = rightConnections[0];
    const firstLength = getFittingLength(firstConn.fittingId, firstConn.fittingLength);
    const currentLength = getFittingLength(conn.fittingId, conn.fittingLength);
    return conn.fittingId === firstConn.fittingId && Math.abs(firstLength - currentLength) < 0.01;
  });

  // Размерные линии для вылетов правых подключений (горизонтальные)
  if (params.connectionsPlacementMode === 'spacing') {
    // Режим межосевого: показываем вылет для каждого уникального типа фитинга
    const processedFittingTypes = new Set();
    
    rightConnections.forEach((conn, index) => {
      const fittingData = fittingsData[conn.fittingId];
      if (!fittingData) return;
      
      // Создаём ключ для идентификации уникального типа фитинга
      const fittingKey = `${conn.fittingId}_${conn.fittingLength || ''}`;
      
      // Пропускаем, если уже обработали такой тип фитинга
      if (processedFittingTypes.has(fittingKey)) return;
      processedFittingTypes.add(fittingKey);
      
      // Вычисляем позицию текущего подключения
      let actualOffset = params.rightConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(rightConnections[i].spacingToNext) || params.rightConnectionsSpacing || 100;
        actualOffset += spacing;
      }
      
      const fallbackLength = getFittingLength(conn.fittingId, conn.fittingLength);
      const { geometricLength, dimensionOverride } = resolveSideConnectionOutletDimension({
        fittingId: conn.fittingId,
        connFittingLength: conn.fittingLength,
        pipeDiameter: mainPipeDiameter,
        catalogLength: fittingData.length,
        shape,
        fallbackLength,
      });
      const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
      const connectionX = startX + mainPipeSize / 2;
      const outerEdgeX = connectionX + geometricLength;
      
      const rightFittingInfo = rightFittingIds[index];
      const outerElementId = rightFittingInfo?.outerElementId;
      const outerElementType = rightFittingInfo?.outerElementType || 'fitting';
      
      const dimY = connectionY - 30;
      
      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: connectionX,
        y1: dimY,
        x2: outerEdgeX,
        y2: dimY,
        position: { x: (connectionX + outerEdgeX) / 2, y: dimY },
        value: String(Math.round(geometricLength)),
        rawValue: geometricLength,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'right' },
          { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'right' },
        ],
        ...dimensionOverride,
      });
    });
  } else {
    // Режим отступов: используем старую логику
  rightConnections.forEach((conn, index) => {
    const fittingData = fittingsData[conn.fittingId];
    if (!fittingData) return;
    
    // Если все подключения одинаковые, показываем размер только для первого (самого верхнего)
    if (rightConnectionsAreSame && index !== 0) return;
    
    // Вычисляем реальную позицию с учетом направления отсчета
    const actualOffset = getConnectionPosition(
      conn.offsetFromTop, 
      conn.offsetDirection, 
      actualMainPipeHeight,
      topBortshaibaLengthForHeight,
      topStraightCapLength,
      bottomBortshaibaLengthForHeight,
      bottomStraightCapLength
    );
    
    const fallbackLength = getFittingLength(conn.fittingId, conn.fittingLength);
    const { geometricLength, dimensionOverride } = resolveSideConnectionOutletDimension({
      fittingId: conn.fittingId,
      connFittingLength: conn.fittingLength,
      pipeDiameter: mainPipeDiameter,
      catalogLength: fittingData.length,
      shape,
      fallbackLength,
    });
    // Отступ считается от начала общей высоты (с учётом верхней бортшайбы и прямой заглушки)
    const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
    const connectionX = startX + mainPipeSize / 2;
    
    // Внешний край (правый конец) фитинга/подключения
    const outerEdgeX = connectionX + geometricLength;
    
    // Получаем информацию о крайнем элементе для привязки
    const rightFittingInfo = rightFittingIds[index];
    const outerElementId = rightFittingInfo?.outerElementId;
    const outerElementType = rightFittingInfo?.outerElementType || 'fitting';
    
    const dimY = connectionY - 30;
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connectionX,
      y1: dimY,
      x2: outerEdgeX,
      y2: dimY,
      position: { x: (connectionX + outerEdgeX) / 2, y: dimY },
      value: String(Math.round(geometricLength)),
      rawValue: geometricLength,
      extensionLines: [],
      precision: 0,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: mainPipeId, objectType: 'cylinder', x: connectionX, y: dimY, type: 'right' },
        // Привязываем к крайнему элементу подключения (бортшайба, шайба или фитинг)
        // Для фитингов используем type: 'top' (привязка к началу фитинга), для цилиндров - 'right'
        { objectId: outerElementId, objectType: outerElementType, x: outerEdgeX, y: dimY, type: outerElementType === 'fitting' ? 'top' : 'right' },
      ],
      ...dimensionOverride,
    });
  });
  }

  // Размерные линии для вылетов верхнего и нижнего фитингов (вертикальные)
  // Показываем только если включено торцевое подключение и есть вылет (диаметры не совпадают)
  if (params.hasTopConnection && topCapFitting && topFittingData) {
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    
    // Показываем размерную линию только если нужен переход/заглушка (есть вылет)
    if (capReq.needsCap) {
      const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
      const isFlange = topCapFitting.startsWith('Flanec_');
      const defaultNakGaikaShaiba = shape === 'square' ? 58 : 40;
      const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba : (isFlange ? 100 : getFittingLength(topCapFitting, null));
      const fittingLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, defaultLength);
      const capTopY = mainPipeTopY;
      
      const dimX = startX + mainPipeSize / 2 + 30;
      
      // Для ниппелей/муфт: размерная линия показывает только длину фитинга (от начала до конца фитинга)
      const isNippel = topCapFitting.startsWith('Nippel_');
      const isMufta = topCapFitting.startsWith('Mufta_');
      
      let elements;
      if (isNippel || isMufta) {
        // Размерная линия от начала фитинга до конца фитинга
        // Начало фитинга - это connectionStartY (уже вычислено в ЛОГИКЕ 1.5)
        // Вычислим его здесь снова
        let connectionStartY = mainPipeTopY;
        if (params.hasTopCap) {
          if (topCapType === 'elliptical') {
            const ellipticalCapKey = getCapKey('elliptical', mainPipeDiameter, shape === 'square', mainPipeProfile);
            const ellipticalCapData = fittingsData[ellipticalCapKey];
            const ellipticalCapLength = ellipticalCapData?.length || 0;
            connectionStartY = mainPipeTopY - ellipticalCapLength;
          } else if (topCapType === 'straight') {
            const straightCapKey = getCapKey('straight', mainPipeDiameter, shape === 'square', mainPipeProfile);
            const straightCapData = fittingsData[straightCapKey];
            const straightCapLength = straightCapData?.length || 0;
            connectionStartY = mainPipeTopY - straightCapLength;
          }
        }
        
        const fittingActualLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, topFittingData.length);
        
        elements = [
          // Верхняя точка - к началу фитинга (не к трубе!)
          { objectId: topFittingId, objectType: 'fitting', x: dimX, y: connectionStartY, type: 'bottom' },
          // Нижняя точка - к концу фитинга
          { objectId: topFittingId, objectType: 'fitting', x: dimX, y: connectionStartY - fittingActualLength, type: 'top' }
        ];
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimX,
          y1: connectionStartY,
          x2: dimX,
          y2: connectionStartY - fittingActualLength,
          position: { x: dimX, y: (connectionStartY + connectionStartY - fittingActualLength) / 2 },
          value: String(Math.round(fittingActualLength)),
          rawValue: fittingActualLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        });
      } else {
        // Для других фитингов (фланцев/гаек) - размерная линия от заглушки до конца подключения
        const isNakGaika = topCapFitting.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = topCapFitting.startsWith('Shaib_Nar_Rezba_');
        const isFlange = topCapFitting.startsWith('Flanec_');
        
        const hasStraightCap = params.hasTopCap && topCapType === 'straight';
        
        // Для фланцев с прямой заглушкой: размерная линия от заглушки
        // Для накидных гаек и шайб с резьбой: размерная линия от заглушки (прямой или эллиптической)
        const useCapAsReference = (isFlange && hasStraightCap && topStraightCapIdForDimLine) ||
                                   ((isNakGaika || isShaibaNarRezba) && (topStraightCapIdForDimLine || topEllipticalCapIdForDimLine));
        const capId = topStraightCapIdForDimLine || topEllipticalCapIdForDimLine;
        
        // Для прямой заглушки: начальная точка - от края заглушки
        let referenceY = capTopY;
        if (hasStraightCap) {
          referenceY = capTopY - topStraightCapLength;
        }
        
        elements = [
          // Верхняя точка - к заглушке (для фланцев/гаек/шайб с прямой заглушкой) или к основной трубе
          useCapAsReference
            ? { objectId: capId, objectType: 'fitting', x: dimX, y: referenceY, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capTopY, type: 'top' },
          // Нижняя точка - к конечному объекту (фитинг или промежуточная труба)
          topFittingEndObjectId 
            ? { objectId: topFittingEndObjectId, objectType: topFittingEndObjectType, x: dimX, y: referenceY - fittingLength, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capTopY - fittingLength, type: 'top' }
        ];
        
        // Добавляем overrideText для накидных гаек и шайб с резьбой
        const dimLineData = {
          isHorizontal: false,
          lockOrientation: true,
          angle: 0, // Вертикальная линия
          x1: dimX,
          y1: referenceY,
          x2: dimX,
          y2: referenceY - fittingLength,
          position: { x: dimX, y: (referenceY + referenceY - fittingLength) / 2 },
          value: String(Math.round(fittingLength)),
          rawValue: fittingLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        };
        
        // Применяем overrideText если это накидная гайка или шайба с резьбой
        if ((isNakGaika || isShaibaNarRezba) && (topCapFittingLengthNum === null || topCapFittingLengthNum === undefined || topCapFittingLengthNum === (shape === 'square' ? 58 : 40))) {
          dimLineData.overrideEnabled = true;
          dimLineData.overrideText = shape === 'square' ? '54' : '32';
        }
        
        dimensionLines.push(dimLineData);
      }
    }
    // Для фланцев/гаек/шайб впритык - не показываем размерную линию
  }

  if (params.hasBottomConnection && bottomCapFitting && bottomFittingData) {
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    
    // Показываем размерную линию только если нужен переход/заглушка (есть вылет)
    if (capReq.needsCap) {
      const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
      const isFlange = bottomCapFitting.startsWith('Flanec_');
      const defaultNakGaikaShaiba = shape === 'square' ? 58 : 40;
      const defaultLength = (isNakGaika || isShaibaNarRezba) ? defaultNakGaikaShaiba : (isFlange ? 100 : getFittingLength(bottomCapFitting, null));
      const fittingLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, defaultLength);
      const bottomEdgeY = mainPipeTopY + actualMainPipeHeight;
      const capBottomY = bottomEdgeY;
      
      const dimX = startX + mainPipeSize / 2 + 30;
      
      // Для ниппелей/муфт: размерная линия показывает только длину фитинга (от начала до конца фитинга)
      const isNippel = bottomCapFitting.startsWith('Nippel_');
      const isMufta = bottomCapFitting.startsWith('Mufta_');
      
      let elements;
      if (isNippel || isMufta) {
        // Размерная линия от начала фитинга до конца фитинга
        let connectionStartY = bottomEdgeY;
        if (params.hasBottomCap) {
          if (bottomCapType === 'elliptical') {
            const ellipticalCapKey = getCapKey('elliptical', mainPipeDiameter, shape === 'square', mainPipeProfile);
            const ellipticalCapData = fittingsData[ellipticalCapKey];
            const ellipticalCapLength = ellipticalCapData?.length || 0;
            connectionStartY = bottomEdgeY + ellipticalCapLength;
          } else if (bottomCapType === 'straight') {
            const straightCapKey = getCapKey('straight', mainPipeDiameter, shape === 'square', mainPipeProfile);
            const straightCapData = fittingsData[straightCapKey];
            const straightCapLength = straightCapData?.length || 0;
            connectionStartY = bottomEdgeY + straightCapLength;
          }
        }
        
        const fittingActualLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, bottomFittingData.length);
        
        elements = [
          // Верхняя точка - к началу фитинга (не к трубе!)
          { objectId: bottomFittingId, objectType: 'fitting', x: dimX, y: connectionStartY, type: 'bottom' },
          // Нижняя точка - к концу фитинга
          { objectId: bottomFittingId, objectType: 'fitting', x: dimX, y: connectionStartY + fittingActualLength, type: 'top' }
        ];
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimX,
          y1: connectionStartY,
          x2: dimX,
          y2: connectionStartY + fittingActualLength,
          position: { x: dimX, y: (connectionStartY + connectionStartY + fittingActualLength) / 2 },
          value: String(Math.round(fittingActualLength)),
          rawValue: fittingActualLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        });
      } else {
        // Для других фитингов (фланцев/гаек) - размерная линия от заглушки до конца подключения
        const isNakGaika = bottomCapFitting.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = bottomCapFitting.startsWith('Shaib_Nar_Rezba_');
        const isFlange = bottomCapFitting.startsWith('Flanec_');
        
        const hasStraightCap = params.hasBottomCap && bottomCapType === 'straight';
        
        // Для фланцев с прямой заглушкой: размерная линия от заглушки
        // Для накидных гаек и шайб с резьбой: размерная линия от заглушки (прямой или эллиптической)
        const useCapAsReference = (isFlange && hasStraightCap && bottomStraightCapIdForDimLine) ||
                                   ((isNakGaika || isShaibaNarRezba) && (bottomStraightCapIdForDimLine || bottomEllipticalCapIdForDimLine));
        const capId = bottomStraightCapIdForDimLine || bottomEllipticalCapIdForDimLine;
        
        // Для прямой заглушки: начальная точка - от края заглушки
        let referenceY = capBottomY;
        if (hasStraightCap) {
          referenceY = capBottomY + bottomStraightCapLength;
        }
        
        elements = [
          // Верхняя точка - к заглушке (для фланцев/гаек/шайб с прямой заглушкой) или к основной трубе
          useCapAsReference
            ? { objectId: capId, objectType: 'fitting', x: dimX, y: referenceY, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capBottomY, type: 'bottom' },
          // Нижняя точка - к конечному объекту (бортшайба или промежуточная труба)
          // Для rotation: 180 (идет вниз) привязываемся к 'top' стороне объекта
          bottomFittingEndObjectId 
            ? { objectId: bottomFittingEndObjectId, objectType: bottomFittingEndObjectType, x: dimX, y: referenceY + fittingLength, type: 'top' }
            : { objectId: mainPipeId, objectType: 'cylinder', x: dimX, y: capBottomY + fittingLength, type: 'bottom' }
        ];
        
        // Добавляем overrideText для накидных гаек и шайб с резьбой
        const dimLineData = {
          isHorizontal: false,
          lockOrientation: true,
          angle: 0, // Вертикальная линия
          x1: dimX,
          y1: referenceY,
          x2: dimX,
          y2: referenceY + fittingLength,
          position: { x: dimX, y: (referenceY + referenceY + fittingLength) / 2 },
          value: String(Math.round(fittingLength)),
          rawValue: fittingLength,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: elements,
        };
        
        // Применяем overrideText если это накидная гайка или шайба с резьбой
        if ((isNakGaika || isShaibaNarRezba) && (bottomCapFittingLengthNum === null || bottomCapFittingLengthNum === undefined || bottomCapFittingLengthNum === (shape === 'square' ? 58 : 40))) {
          dimLineData.overrideEnabled = true;
          dimLineData.overrideText = shape === 'square' ? '54' : '32';
        }
        
        dimensionLines.push(dimLineData);
      }
    }
    // Для фланцев/гаек/шайб впритык - не показываем размерную линию
  }

  // 9. Генерация позиций (balloons) для фитингов
  const balloons = [];

  // Функция для получения текста позиции по ключу фитинга
  const getFittingLabel = (fittingKey) => {
    if (!fittingKey) return '';

    const sizeMap = {
      '1.4': '1/4"',
      '3.8': '3/8"',
      '1.2': '1/2"',
      '3.4': '3/4"',
      '1': '1"',
      '1_1.4': '1.1/4"',
      '1_1.2': '1.1/2"',
      '2': '2"',
      '2_1/2': '2.1/2"',
      '3': '3"',
      '4': '4"',
    };

    if (fittingKey.startsWith('Nippel_')) {
      const size = fittingKey.replace('Nippel_', '');
      return (sizeMap[size] || size) + 'НР';
    }
    if (fittingKey.startsWith('Mufta_')) {
      const size = fittingKey.replace('Mufta_', '');
      return (sizeMap[size] || size) + 'ВР';
    }
    if (fittingKey.startsWith('Perehod_Konc_')) {
      const dims = fittingKey.replace('Perehod_Konc_', '');
      return 'Переход\n' + dims;
    }
    if (fittingKey.startsWith('Flanec_')) {
      const fittingData = fittingsData[fittingKey];
      if (fittingData) {
        const displayName = fittingData.displayName;
        const match = displayName.match(/Фланец (Ду\d+)\s*\((.+)\)/);
        if (match) return 'Фланец ' + match[1] + '\n(' + match[2] + ')';
        return displayName;
      }
    }
    if (fittingKey.startsWith('Nak_Gaika_')) {
      const size = fittingKey.replace('Nak_Gaika_', '');
      return (sizeMap[size] || size) + ' Н.Г';
    }
    if (fittingKey.startsWith('Shaib_Nar_Rezba_')) {
      const size = fittingKey.replace('Shaib_Nar_Rezba_', '');
      return (sizeMap[size] || size) + ' Ш.Н.Р';
    }
    return fittingKey;
  };

  // Позиции для левых подключений (размещаем СЛЕВА от фитингов)
  leftConnections.forEach((conn, index) => {
    const fittingData = fittingsData[conn.fittingId];
    if (!fittingData) return;

    // Вычисляем реальную позицию с учетом режима
    let actualOffset;
    if (params.connectionsPlacementMode === 'spacing') {
      // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
      actualOffset = params.leftConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов: используем offsetFromTop и offsetDirection
      actualOffset = getConnectionPosition(
        conn.offsetFromTop, 
        conn.offsetDirection, 
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }

    const label = getFittingLabel(conn.fittingId);
    const fittingLength = getFittingLength(conn.fittingId, conn.fittingLength);
    const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
    const connectionX = startX - mainPipeSize / 2;
    
    // Внешний край фитинга (левый конец)
    const fittingEndX = connectionX - fittingLength;
    
    // Смещаем Y от середины разделителя
    const balloonY = connectionY < midSeparatorY 
      ? connectionY - 60  // Если выше середины - сдвигаем вверх
      : connectionY + 60; // Если ниже середины - сдвигаем вниз
    
    balloons.push({
      id: `balloon-left-${index}-${Date.now()}`,
      x: fittingEndX - 60,
      y: balloonY,
      text: label,
      targets: [{ type: 'fitting', id: leftFittingIds[index]?.id }],
    });
  });

  // Позиции для правых подключений (размещаем СПРАВА от фитингов)
  rightConnections.forEach((conn, index) => {
    const fittingData = fittingsData[conn.fittingId];
    if (!fittingData) return;

    // Вычисляем реальную позицию с учетом режима
    let actualOffset;
    if (params.connectionsPlacementMode === 'spacing') {
      // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
      actualOffset = params.rightConnectionsFirstOffset || 50;
      for (let i = 0; i < index; i++) {
        const spacing = Number(rightConnections[i].spacingToNext) || params.rightConnectionsSpacing || 100;
        actualOffset += spacing;
      }
    } else {
      // Режим отступов: используем offsetFromTop и offsetDirection
      actualOffset = getConnectionPosition(
        conn.offsetFromTop, 
        conn.offsetDirection, 
        actualMainPipeHeight,
        topBortshaibaLengthForHeight,
        topStraightCapLength,
        bottomBortshaibaLengthForHeight,
        bottomStraightCapLength
      );
    }

    const label = getFittingLabel(conn.fittingId);
    const fittingLength = getFittingLength(conn.fittingId, conn.fittingLength);
    const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
    const connectionX = startX + mainPipeSize / 2;
    
    // Внешний край фитинга (правый конец)
    const fittingEndX = connectionX + fittingLength;
    
    // Смещаем Y от середины разделителя
    const balloonY = connectionY < midSeparatorY 
      ? connectionY - 60  // Если выше середины - сдвигаем вверх
      : connectionY + 60; // Если ниже середины - сдвигаем вниз
    
    balloons.push({
      id: `balloon-right-${index}-${Date.now()}`,
      x: fittingEndX + 60,
      y: balloonY,
      text: label,
      targets: [{ type: 'fitting', id: rightFittingIds[index]?.id }],
    });
  });

  // Позиция для верхнего фитинга (размещаем СВЕРХУ)
  if (params.hasTopConnection && topCapFitting && topFittingData && topFittingId) {
    const label = getFittingLabel(topCapFitting);
    const capReq = getCapRequirementForFitting(topCapFitting, mainPipeDiameter);
    
    let fittingTopY;
    if (capReq.fitsFlush) {
      // Впритык - фитинг от верха гребенки
      if (topCapFitting.startsWith('Nippel_') || topCapFitting.startsWith('Mufta_')) {
        const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, topFittingData.length);
        fittingTopY = mainPipeTopY - fLength;
      } else {
        // Фланец/гайка/шайба впритык - от верха гребенки
        fittingTopY = mainPipeTopY - topFittingData.length;
      }
    } else if (capReq.needsCap) {
      // С переходом/заглушкой - вылет от заглушки
      const fittingLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, getFittingLength(topCapFitting, null));
      const capTopY = topCapType === 'straight' 
        ? mainPipeTopY
        : mainPipeTopY - topEllipticalCapLength;
      fittingTopY = capTopY - fittingLength;
    } else {
      // Обычный фитинг с заглушкой
      const fLength = endFittingAutoLength(topCapFitting, topCapFittingLengthNum, topFittingData.length);
      const capTopY = topCapType === 'straight' 
        ? mainPipeTopY
        : mainPipeTopY - topEllipticalCapLength;
      fittingTopY = capTopY - fLength;
    }
    
    balloons.push({
      id: `balloon-top-${Date.now()}`,
      x: startX,
      y: fittingTopY - 30,
      text: label,
      targets: [{ type: 'fitting', id: topFittingId }],
    });
  }

  // Позиция для нижнего фитинга (размещаем СНИЗУ)
  if (params.hasBottomConnection && bottomCapFitting && bottomFittingData && bottomFittingId) {
    const label = getFittingLabel(bottomCapFitting);
    const capReq = getCapRequirementForFitting(bottomCapFitting, mainPipeDiameter);
    const bottomMainPipeEdge = mainPipeTopY + actualMainPipeHeight;
    
    let fittingBottomY;
    if (capReq.fitsFlush) {
      // Впритык - фитинг от низа гребенки
      if (bottomCapFitting.startsWith('Nippel_') || bottomCapFitting.startsWith('Mufta_')) {
        const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, bottomFittingData.length);
        fittingBottomY = bottomMainPipeEdge + fLength;
      } else {
        // Фланец/гайка/шайба впритык - от низа гребенки
        fittingBottomY = bottomMainPipeEdge + bottomFittingData.length;
      }
    } else if (capReq.needsCap) {
      // С переходом/заглушкой - вылет от заглушки
      const fittingLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, getFittingLength(bottomCapFitting, null));
      const capBottomY = bottomCapType === 'straight'
        ? bottomMainPipeEdge
        : bottomMainPipeEdge + bottomEllipticalCapLength;
      fittingBottomY = capBottomY + fittingLength;
    } else {
      // Обычный фитинг с заглушкой
      const fLength = endFittingAutoLength(bottomCapFitting, bottomCapFittingLengthNum, bottomFittingData.length);
      const capBottomY = bottomCapType === 'straight'
        ? bottomMainPipeEdge
        : bottomMainPipeEdge + bottomEllipticalCapLength;
      fittingBottomY = capBottomY + fLength;
    }
    
    balloons.push({
      id: `balloon-bottom-${Date.now()}`,
      x: startX,
      y: fittingBottomY + 30,
      text: label,
      targets: [{ type: 'fitting', id: bottomFittingId }],
    });
  }

  // Позиция для верхнего перехода (размещаем СВЕРХУ, выше фитинга если есть)
  if (topCapReducerId && params.topReducer) {
    const label = getFittingLabel(params.topReducer);
    const reducerData = fittingsData[params.topReducer];
    if (reducerData) {
      const reducerTopY = mainPipeTopY - reducerData.length;
      // Если есть фитинг сверху - переход чуть выше него
      const balloonY = topCapFitting && topFittingData ? reducerTopY - 120 : reducerTopY - 60;
      balloons.push({
        id: `balloon-top-reducer-${Date.now()}`,
        x: startX - 80, // Смещаем влево, чтобы не пересекаться с фитингом
        y: balloonY,
        text: label,
        targets: [{ type: 'fitting', id: topCapReducerId }],
      });
    }
  }

  // Позиция для нижнего перехода (размещаем СНИЗУ, ниже фитинга если есть)
  if (bottomCapReducerId && params.bottomReducer) {
    const label = getFittingLabel(params.bottomReducer);
    const reducerData = fittingsData[params.bottomReducer];
    if (reducerData) {
      const bottomMainPipeEdge = mainPipeTopY + actualMainPipeHeight;
      const reducerBottomY = bottomMainPipeEdge + reducerData.length;
      // Если есть фитинг снизу - переход чуть ниже него
      const balloonY = bottomCapFitting && bottomFittingData ? reducerBottomY + 120 : reducerBottomY + 60;
      balloons.push({
        id: `balloon-bottom-reducer-${Date.now()}`,
        x: startX - 80, // Смещаем влево, чтобы не пересекаться с фитингом
        y: balloonY,
        text: label,
        targets: [{ type: 'fitting', id: bottomCapReducerId }],
      });
    }
  }

  // 8. Дополнительные выходы (центральные фитинги на гидроразделителе)
  additionalOutlets.forEach((outlet, index) => {
    const fittingData = fittingsData[outlet.fittingId];
    if (!fittingData) return;

    const side = outlet.side || 'both';
    
    // Для "left" и "right" используем горизонтальную ориентацию (как торцевые подключения)
    if (side === 'left' || side === 'right') {
      // === ГОРИЗОНТАЛЬНЫЕ ВЫХОДЫ (СЛЕВА/СПРАВА) ===
      // Логика аналогична левым/правым подключениям
      
      // Вычисляем позицию Y (на расстоянии от внешнего края с учетом заглушек)
      let outletY;
      if (outlet.positionType === 'center') {
        // По середине гидроразделителя (по высоте)
        outletY = mainPipeTopY + actualMainPipeHeight / 2;
      } else {
        // На расстоянии от внешнего края (с учетом заглушек и бортшайб)
        const isFromTop = outlet.distanceDirection === 'left';
        const distance = outlet.distanceFromEdge || 50;
        
        // Внешние края (с учетом заглушек и бортшайб)
        // Для расстояния всегда используем actualMainPipeHeight (высота гидроразделителя)
        const topEdge = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomEdge = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        
        if (isFromTop) {
          // Отсчет от верхнего внешнего края
          outletY = topEdge + distance;
        } else {
          // Отсчет от нижнего внешнего края
          outletY = bottomEdge - distance;
        }
      }
      
      // Позиция X в зависимости от стороны
      const connectionX = side === 'left' 
        ? startX - mainPipeSize / 2  // Левый край основной трубы
        : startX + mainPipeSize / 2; // Правый край основной трубы
      
      // Обычный фитинг (ниппель, муфта без промежуточной трубы)
      // Используем getFittingLength для правильного применения дефолтных значений
      let fittingLength = getFittingLength(outlet.fittingId, outlet.fittingLength);
      const pipeDiameter = fittingData.diameter;
      const connectionDiameter = getFittingConnectionDiameter(outlet.fittingId) || pipeDiameter;

      let dimensionOverride = {};
      const nippelMuftaResolved = resolveNippelMuftaFitting({
        fittingId: outlet.fittingId,
        fittingLength: outlet.fittingLength ?? null,
        pipeDiameter: mainPipeDiameter,
        catalogLength: fittingData.length,
        shape,
      });
      if (nippelMuftaResolved) {
        fittingLength = nippelMuftaResolved.length;
        dimensionOverride = nippelMuftaResolved.dimensionOverride;
      }
      
      const fittingId = `fitting-outlet-${side}-${index}-${Date.now()}`;
      
      if (side === 'left') {
        // Левый выход - фитинг направлен влево (rotation = -90)
        fittings.push({
          id: fittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: connectionX,
          groupY: outletY,
          rotation: -90,
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (уменьшен для сварки, особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'left', // IGES: левый дополнительный выход
          ...dimensionOverride,
        });
      } else {
        // Правый выход - фитинг направлен вправо (rotation = 90)
        fittings.push({
          id: fittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: connectionX,
          groupY: outletY,
          rotation: 90,
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength,
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (уменьшен для сварки, особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'right', // IGES: правый дополнительный выход
          ...dimensionOverride,
        });
      }
      
      // Balloon - без подписи "(слева)"/"(справа)" - это и так понятно
      const label = getFittingLabel(outlet.fittingId);
      
      // Balloon слева/справа от фитинга
      const balloonX = side === 'left' 
        ? connectionX - fittingLength - 40 
        : connectionX + fittingLength + 40;
      
      // Смещаем Y от середины разделителя
      const balloonY = outletY < midSeparatorY 
        ? outletY - 60  // Если выше середины - сдвигаем вверх
        : outletY + 60; // Если ниже середины - сдвигаем вниз
      
      balloons.push({
        id: `balloon-outlet-${index}-${Date.now()}`,
        x: balloonX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [{ type: 'fitting', id: fittingId }],
      });
      
      // Размерная линия для длины фитинга (вылета)
      const dimLineY = outletY;
      const dimLineStartX = connectionX;
      const dimLineEndX = side === 'left' 
        ? connectionX - fittingLength 
        : connectionX + fittingLength;
      
      const isNakGaika = outlet.fittingId.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = outlet.fittingId.startsWith('Shaib_Nar_Rezba_');
      
      if (!nippelMuftaResolved) {
        // Для накидных гаек и шайб проверяем также дефолтное значение
        const defaultOutletLength = shape === 'square' ? 58 : 40;
        const shouldApplyOverride = (isNakGaika || isShaibaNarRezba)
          ? (outlet.fittingLength === null || outlet.fittingLength === undefined || outlet.fittingLength === defaultOutletLength)
          : (outlet.fittingLength === null || outlet.fittingLength === undefined);
        
        if (shouldApplyOverride) {
          if (shape === 'square') {
            if (isNakGaika || isShaibaNarRezba) {
              dimensionOverride = {
                overrideEnabled: true,
                overrideText: '54',
              };
            }
          } else if (isNakGaika || isShaibaNarRezba) {
            dimensionOverride = {
              overrideEnabled: true,
              overrideText: '32',
            };
          }
        }
      }
      
      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: dimLineStartX,
        y1: dimLineY,
        x2: dimLineEndX,
        y2: dimLineY,
        position: { 
          x: (dimLineStartX + dimLineEndX) / 2, 
          y: dimLineY - 30 
        },
        value: String(Math.round(fittingLength)),
        rawValue: fittingLength,
        extensionLines: [],
        precision: 0,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: mainPipeId, objectType: 'cylinder', x: dimLineStartX, y: dimLineY, type: side === 'left' ? 'left' : 'right' },
          { objectId: fittingId, objectType: 'fitting', x: dimLineEndX, y: dimLineY, type: 'top' },
        ],
        ...dimensionOverride,
      });
      
      // Размерная линия для positionType='distance'
      if (outlet.positionType === 'distance') {
        const topDimY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomDimY = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        const isFromTop = outlet.distanceDirection === 'left';
        
        // Размерная линия по центру гидроразделителя (как у вертикальных выходов)
        const dimLineX = startX;
        
        // Используем те же элементы привязки, что и для общей высоты (topDimElement/bottomDimElement)
        // но с координатой x = dimLineX
        const topElement = topStraightCapIdForDimLine
          ? { objectId: topStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : topBortshaibaIdForDimLine
          ? { objectId: topBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY, type: 'top' };
        
        // Для нижних заглушек/бортшайб используем type: 'top', так как они генерируются вниз от трубы
        const bottomElement = bottomStraightCapIdForDimLine
          ? { objectId: bottomStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : bottomBortshaibaIdForDimLine
          ? { objectId: bottomBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY + actualMainPipeHeight, type: 'bottom' };
        
        // Привязка к верхнему центру фитинга (type: 'top')
        // При rotation = ±90 верхний центр в локальных координатах становится центром по Y в мировых
        const fittingElement = { 
          objectId: fittingId,
          objectType: 'fitting', 
          x: dimLineX, 
          y: outletY, 
          type: 'top'
        };
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimLineX,
          y1: isFromTop ? topDimY : outletY,
          x2: dimLineX,
          y2: isFromTop ? outletY : bottomDimY,
          position: { 
            x: dimLineX + 15, 
            y: isFromTop ? (topDimY + outletY) / 2 : (outletY + bottomDimY) / 2 
          },
          value: String(Math.round(outlet.distanceFromEdge || 50)),
          rawValue: outlet.distanceFromEdge || 50,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: isFromTop 
            ? [topElement, fittingElement]
            : [fittingElement, bottomElement],
        });
      }
      
    } else {
      // === ВЕРТИКАЛЬНЫЕ ВЫХОДЫ (СПЕРЕДИ/СЗАДИ/С 2-Х СТОРОН) - "НА СЕБЯ" ===
      
      // Вычисляем позицию Y
      // Вычисляем позицию Y (на расстоянии от внешнего края с учетом заглушек)
      let outletY;
      if (outlet.positionType === 'center') {
        // По середине гидроразделителя (по высоте)
        outletY = mainPipeTopY + actualMainPipeHeight / 2;
      } else {
        // На расстоянии от внешнего края (с учетом заглушек и бортшайб)
        const isFromLeft = outlet.distanceDirection === 'left';
        const distance = outlet.distanceFromEdge || 50;
        
        // Внешние края (с учетом заглушек и бортшайб)
        // Для расстояния всегда используем actualMainPipeHeight (высота гидроразделителя)
        const topEdge = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomEdge = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        
        if (isFromLeft) {
          // Отсчет от верхнего внешнего края
          outletY = topEdge + distance;
        } else {
          // Отсчет от нижнего внешнего края
          outletY = bottomEdge - distance;
        }
      }

      const outletX = startX; // По центру трубы
      
      // Используем selfFacing конфигурацию для отображения "на себя"
      const useSelfFacing = fittingData.selfFacing && fittingData.selfFacing.diameter;
      const fittingLength = useSelfFacing ? fittingData.selfFacing.length : fittingData.length;
      const pipeDiameter = useSelfFacing ? fittingData.selfFacing.diameter : fittingData.diameter;
      const connectionDiameter = getFittingConnectionDiameter(outlet.fittingId) || fittingData.diameter;
      
      // Генерируем фитинги в зависимости от стороны
      const fittingIds = [];
      
      if (side === 'both' || side === 'front') {
        const frontFittingId = `fitting-outlet-front-${index}-${Date.now()}`;
        fittingIds.push(frontFittingId);
        
        fittings.push({
          id: frontFittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: outletX,
          groupY: outletY,
          rotation: 0,
          isSelfFacing: true,
          isCenterFitting: true, // Маркер для исключения сварок между фитингами "с 2-х сторон"
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength / 2,
          side: 'front', // IGES: спереди (отверстие вдоль +Z)
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'front', // IGES: направление отверстия
          ...facingStickFields(outlet),
        });
      }
      
      if (side === 'both' || side === 'back') {
        const backFittingId = `fitting-outlet-back-${index}-${Date.now()}`;
        fittingIds.push(backFittingId);
        
        fittings.push({
          id: backFittingId,
          key: outlet.fittingId,
          itemKey: outlet.fittingId,
          x: 0,
          y: 0,
          width: pipeDiameter,
          height: fittingLength,
          groupX: outletX,
          groupY: outletY,
          rotation: 0,
          isSelfFacing: true,
          isCenterFitting: true, // Маркер для исключения сварок между фитингами "с 2-х сторон"
          offsetX: pipeDiameter / 2,
          offsetY: fittingLength / 2,
          side: 'back', // IGES: сзади (отверстие вдоль -Z)
          igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
          igesCreateHole: true, // IGES: создаёт отверстие
          holeDiameter: getHoleDiameterForPipe(connectionDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (особая логика для муфты 1/2" на квадратных)
          igesHoleDirection: 'back', // IGES: направление отверстия
          ...facingStickFields(outlet),
        });
      }
      
      // Добавляем balloon с учетом стороны
      let label = getFittingLabel(outlet.fittingId);
      
      if (side === 'both') {
        label = label + '\n(c 2-х ст.)';
      } else if (side === 'back') {
        label = label + '\n(сзади)';
      }
      
      const targets = fittingIds.map(id => ({ type: 'fitting', id: id }));
      
      // Размещаем balloon по центру гидроразделителя (по X)
      // По Y: если отсчёт снизу (right) - над фитингом, если сверху (left) - под фитингом
      const isFromBottom = outlet.distanceDirection === 'right';
      const balloonY = (outlet.positionType === 'center' || isFromBottom) 
        ? outletY - 40  // Над фитингом (для центра и отсчёта снизу)
        : outletY + 40; // Под фитингом (для отсчёта сверху)
      
      balloons.push({
        id: `balloon-outlet-${index}-${Date.now()}`,
        x: startX, // По центру гидроразделителя
        y: balloonY,
        text: label,
        textAlign: 'center', // Центрирование текста для многострочных подписей
        targets: targets,
      });
      
      // Добавляем размерную линию только для positionType='distance'
      if (outlet.positionType === 'distance') {
        const dimLineX = startX;
        const topDimY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength;
        const bottomDimY = mainPipeTopY + actualMainPipeHeight + bottomBortshaibaLengthForHeight + bottomStraightCapLength;
        const isFromLeft = outlet.distanceDirection === 'left';
        
        // Используем те же элементы привязки, что и для общей высоты (topDimElement/bottomDimElement)
        // но с координатой x = dimLineX
        const topElement = topStraightCapIdForDimLine
          ? { objectId: topStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : topBortshaibaIdForDimLine
          ? { objectId: topBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: topDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY, type: 'top' };
        
        // Для нижних заглушек/бортшайб используем type: 'top', так как они генерируются вниз от трубы
        const bottomElement = bottomStraightCapIdForDimLine
          ? { objectId: bottomStraightCapIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : bottomBortshaibaIdForDimLine
          ? { objectId: bottomBortshaibaIdForDimLine, objectType: 'fitting', x: dimLineX, y: bottomDimY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: dimLineX, y: mainPipeTopY + actualMainPipeHeight, type: 'bottom' };
        
        // Привязка к левому/правому центру фитинга (type: 'left')
        // При rotation = 0 левый/правый центр дает центр по Y (как нужно для вертикальной размерной линии)
        const fittingElement = { 
          objectId: fittingIds[0], // Используем ID первого фитинга
          objectType: 'fitting', 
          x: dimLineX, 
          y: outletY, 
          type: 'left' // Или 'right' - для rotation=0 оба дают центр по Y
        };
        
        dimensionLines.push({
          isHorizontal: false,
          lockOrientation: true,
          angle: 0,
          x1: dimLineX,
          y1: isFromLeft ? topDimY : outletY,
          x2: dimLineX,
          y2: isFromLeft ? outletY : bottomDimY,
          position: { 
            x: dimLineX + 30, 
            y: isFromLeft ? (topDimY + outletY) / 2 : (outletY + bottomDimY) / 2 
          },
          value: String(Math.round(outlet.distanceFromEdge || 50)),
          rawValue: outlet.distanceFromEdge || 50,
          extensionLines: [],
          precision: 0,
          textPosition: { x: 0, y: 0 },
          textFontFamily: 'Arial',
          textFontStyle: 'italic',
          textColor: '#ff0000',
          elements: isFromLeft 
            ? [topElement, fittingElement]
            : [fittingElement, bottomElement],
        });
      }
    }
  });

  // Применяем измененные значения к размерным линиям
  const fittingsWithOverride = fittings.filter(f => f.overrideEnabled && f.overrideText);
  
  fittingsWithOverride.forEach((fitting) => {
    dimensionLines.forEach((dimLine) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;
      
      // Проверяем что линия относится к этому фитингу
      const hasFitting = dimLine.elements.some(el => el.objectId === fitting.id);
      const hasBortshaiba = dimLine.elements.some(el => el.objectId === fitting.bortshaibaId);
      
      if (!hasFitting && !hasBortshaiba) return;
      
      // Определяем тип фитинга по rotation:
      // rotation = 0 -> торцевое подключение сверху (вертикальный)
      // rotation = 180 -> торцевое подключение снизу (вертикальный)
      // rotation = -90 или 90 -> левое/правое подключение (горизонтальный)
      const isTopBottomConnection = fitting.rotation === 0 || fitting.rotation === 180;
      const isLeftRightConnection = fitting.rotation === -90 || fitting.rotation === 90;
      
      // Для вертикальных линий (торцевые подключения сверху/снизу)
      if (!dimLine.isHorizontal && isTopBottomConnection) {
        // Для накидных гаек и шайб с резьбой проверяем значение 40 (круглые) или 58 (квадратные)
        const isNakGaika = fitting.key?.startsWith('Nak_Gaika_');
        const isShaiba = fitting.key?.startsWith('Shaib_Nar_Rezba_');
        const isMufta = fitting.key?.startsWith('Mufta_');
        
        let shouldApplyOverride = false;
        const defaultNakGaikaLength = shape === 'square' ? 58 : 40;
        
        if (isNakGaika || isShaiba) {
          // Для накидных гаек и шайб с резьбой проверяем значение дефолтного вылета
          if (dimLine.rawValue === defaultNakGaikaLength || dimLine.value === String(defaultNakGaikaLength)) {
            shouldApplyOverride = true;
          }
        } else if (isMufta) {
          // Для муфт проверяем соответствие высоте фитинга или диапазону 15-30
          if (dimLine.rawValue === fitting.height || 
              dimLine.value === String(fitting.height) ||
              (dimLine.rawValue >= 15 && dimLine.rawValue <= 30)) {
            shouldApplyOverride = true;
          }
        }
        
        if (shouldApplyOverride) {
          // Не перезаписываем, если overrideText уже установлен в размерной линии
          if (!dimLine.overrideEnabled || !dimLine.overrideText) {
            dimLine.overrideEnabled = true;
            dimLine.overrideText = fitting.overrideText;
          }
        }
      }
      // Для горизонтальных линий (левые/правые подключения)
      else if (dimLine.isHorizontal && isLeftRightConnection) {
        const isNakGaika = fitting.key?.startsWith('Nak_Gaika_');
        const isShaiba = fitting.key?.startsWith('Shaib_Nar_Rezba_');
        const isMufta = fitting.key?.startsWith('Mufta_');
        const isNippel = fitting.key?.startsWith('Nippel_');
        
        let shouldApplyOverride = false;
        if (isNakGaika && hasBortshaiba) {
          shouldApplyOverride = true;
        } else if (isShaiba && hasFitting) {
          shouldApplyOverride = true;
        } else if ((isMufta || isNippel) && hasFitting) {
          shouldApplyOverride = fitting.overrideEnabled && fitting.overrideText != null;
        }
        
        if (shouldApplyOverride) {
          // Не перезаписываем, если overrideText уже установлен в размерной линии
          if (!dimLine.overrideEnabled || !dimLine.overrideText) {
            dimLine.overrideEnabled = true;
            dimLine.overrideText = fitting.overrideText;
          }
        }
      }
    });
  });

  // Расчет общей длины гидроразделителя (высота) для metadata
  // Высота = основная высота + верхние элементы + нижние элементы
  // Если есть подключение с фитингом, то заглушка НЕ добавляется (она внутри вылета фитинга)
  // Если подключение выключено, то добавляется только заглушка (если есть)
  const topExtra = (params.hasTopConnection && topFittingLength > 0) ? topFittingLength : topEllipticalCapLength;
  const bottomExtra = (params.hasBottomConnection && bottomFittingLength > 0) ? bottomFittingLength : bottomEllipticalCapLength;
  const totalHeight = separatorHeight + topExtra + bottomExtra;

  const result = {
    cylinders,
    fittings,
    welds: [],
    annotations: [],
    dimensionLines,
    balloons,
    lines: [],
    objectConstraints: [],
    settings: {
      zoom: 1,
      textZoom: 1,
      positionTextZoom: 1,
      isLandscape: false, // Вертикальная (портретная) ориентация для гидравлического разделителя
    },
    metadata: {
      totalLength: Math.round(totalHeight),
      separatorTotalHeight: Math.round(totalHeight),
      coating: coating, // Добавляем покрытие для аннотации
      // Параметры подключений для IGES экспорта
      circuitParams: {
        pipeId: mainPipeId,
        // Для гидроразделителя нет парной трубы (все подключения на одной трубе)
        pairedCylinderId: null,
        // Позиции подключений (левые и правые)
        leftConnectionPositions: leftConnections.map((conn, index) => {
          const actualOffset = getConnectionPosition(
            conn.offsetFromTop,
            conn.offsetDirection,
            actualMainPipeHeight,
            topBortshaibaLengthForHeight,
            topStraightCapLength,
            bottomBortshaibaLengthForHeight,
            bottomStraightCapLength
          );
          const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
          return { y: connectionY, direction: 'left' };
        }),
        rightConnectionPositions: rightConnections.map((conn, index) => {
          const actualOffset = getConnectionPosition(
            conn.offsetFromTop,
            conn.offsetDirection,
            actualMainPipeHeight,
            topBortshaibaLengthForHeight,
            topStraightCapLength,
            bottomBortshaibaLengthForHeight,
            bottomStraightCapLength
          );
          const connectionY = mainPipeTopY - topBortshaibaLengthForHeight - topStraightCapLength + actualOffset;
          return { y: connectionY, direction: 'right' };
        }),
      },
    },
  };
  
  // IGES: Создаём "снимок" координат сразу после генерации
  // Этот снимок будет использоваться для экспорта вне зависимости от перемещений на чертеже
  result.metadata.igesSnapshot = {
    cylinders: cylinders.map(cyl => ({
      id: cyl.id,
      x: cyl.x,
      y: cyl.y,
      width: cyl.width,
      height: cyl.height,
      rotation: cyl.rotation,
    })),
    fittings: fittings.map(fit => ({
      id: fit.id,
      groupX: fit.groupX,
      groupY: fit.groupY,
      diameter: fit.diameter,
      igesParentCylinderId: fit.igesParentCylinderId,
      igesCreateHole: fit.igesCreateHole,
      holeDiameter: fit.holeDiameter,
      igesHoleDirection: fit.igesHoleDirection,
      isLongCircuit: fit.isLongCircuit,
      pairedCylinderId: fit.pairedCylinderId,
    })),
  };
  
  return result;
}

export default HydraulicSeparatorEditor;
